import {
  tenantDirectoryEntrySchema,
  organizationDirectoryEntrySchema,
  organizationUnitSchema,
  userDirectoryPageSchema,
  userProfileSummarySchema,
  accessPolicySummarySchema,
  mapSettingsSchema,
  videoSettingsV1Schema,
  telemetrySchema,
  telemetryBatchSchema,
  conversationThreadSchema,
  chatResponseSchema,
  aiSettingsSchema,
  aiProviderCatalogSchema,
  ticketSchema,
  listTicketsQuerySchema,
  createTicketSchema,
  updateTicketSchema,
  addTicketEventSchema,
  ticketEventSchema,
  ticketNotificationSchema,
  insuranceClaimsPageSchema,
  insuranceClaimDetailSchema,
  agricultureOpsBootstrapSchema,
  insuranceFraudPageSchema,
  insuranceFraudDetailSchema,
  insuranceCompliancePageSchema,
  insuranceComplianceDetailSchema,
  insuranceUnderwritingPageSchema,
  insuranceUnderwritingDetailSchema,
  insuranceRiskPortfolioPageSchema,
  insuranceRiskPortfolioDetailSchema,
  insuranceRecordCommentInputSchema,
  insuranceRecordActionInputSchema,
  insuranceMutationAckSchema,
  insurerPartnerSchema,
  createUbiDriverCommentSchema,
  executeUbiDriverActionSchema,
  ubiRiskScoreSchema,
  ubiLeaderboardEntrySchema,
  ubiLeaderboardPageSchema,
  ubiDriverRecordSchema,
  ubiDriverFeedbackItemSchema,
  ubiDriverActionResultSchema,
  ubiDriverRecordFeedSchema,
  ubiPolicySchema,
  walletAccountSchema,
  walletLedgerEntrySchema,
  driverExpenseSchema,
  driverEarningSchema,
  routingSettingsSchema,
  cmsSettingsSchema,
  queryEventsSchema,
  eventSchema,
  deviceRawLogSchema,
  billingAccountSchema,
  billingPlanSchema,
  subscriptionSchema,
  billingSubscriptionSeatSchema,
  invoiceSchema,
  paymentSchema,
  purchaseOrderSchema,
  expenseSchema,
  revenueShareRuleSchema,
  simUsageRecordSchema,
  billingDashboardSummarySchema,
  cameraEventSchema,
  triggerCatalogEntrySchema,
  notificationListResponseSchema,
  notificationPreferencesResponseSchema,
  articleBaseSchema,
  articleListItemSchema,
  createArticleRequestSchema,
  updateArticleRequestSchema,
  publishArticleRequestSchema,
  aiGenerateArticleRequestSchema,
  aiRefreshArticleRequestSchema,
  vehicleAssetTypeSchema,
  rasterMarkerTypeSchema,
  type RegisterDevice,
  type Vehicle,
  type Driver,
  type DeviceRawLog,
  type DeviceOnboarding,
  type DeviceRecommendation,
  type Telemetry,
  type ConversationThread,
  type ChatResponse,
  type AiSettings,
  type AiProviderCatalog,
  type CmsSettings,
  type BillingPlan,
  type BillingSubscriptionSeat,
  type Ticket,
  type TicketEvent,
  type TicketNotification,
  type InsuranceClaimsPage,
  type InsuranceClaimDetail,
  type AgricultureOpsBootstrap,
  type InsuranceFraudPage,
  type InsuranceFraudDetail,
  type InsuranceCompliancePage,
  type InsuranceComplianceDetail,
  type InsuranceUnderwritingPage,
  type InsuranceUnderwritingDetail,
  type InsuranceRiskPortfolioPage,
  type InsuranceRiskPortfolioDetail,
  type InsuranceRecordCommentInput,
  type InsuranceRecordActionInput,
  type InsuranceMutationAck,
  type CreateTicketInput,
  type UpdateTicketInput,
  type AddTicketEventInput,
  type ListTicketsQuery,
  type BillingAccount,
  type Subscription,
  type Invoice,
  type Payment,
  type PurchaseOrder,
  type Expense,
  type RevenueShareRule,
  type SimUsageRecord,
  type BillingDashboardSummary,
  type UbiRiskScore,
  type UbiPolicy,
  type InsurerPartner,
  type UbiLeaderboardEntry,
  type UbiLeaderboardPage,
  type UbiDriverRecord,
  type UbiDriverFeedbackItem,
  type UbiDriverActionResult,
  type UbiDriverRecordFeed,
  type CreateUbiDriverCommentInput,
  type ExecuteUbiDriverActionInput,
  type UbiSettings,
  type WalletAccount,
  type WalletLedgerEntry,
  type DriverExpense,
  type DriverEarning,
  type MapSettings,
  type VideoSettingsV1,
  type RoutingSettings,
  type WalletSettings,
  type CameraEvent,
  type NotificationListResponse,
  type NotificationPreferencesResponse,
  type ArticleBase,
  type ArticleListItem,
  type CreateArticleRequest,
  type PublishArticleRequest,
  type AiGenerateArticleRequest,
  type AiRefreshArticleRequest,
  billingInsightSchema,
  type BillingInsight,
  commandEnvelopeSchema,
  type CommandEnvelope,
} from "@glonextrack/contracts";
import { z } from "zod";
import { mergeMapSettings } from "./map-defaults";
import { resolveOrganizationId as resolveScopedOrganizationId } from "./organization-scope";
import { getApiBase } from "./api-base";
import { log } from "./debug-log";

export type {
  BillingAccount,
  BillingPlan,
  Subscription,
  Invoice,
  Payment,
  PurchaseOrder,
  Expense,
  RevenueShareRule,
  SimUsageRecord,
  BillingDashboardSummary,
  ArticleBase,
  ArticleListItem,
};

const API_BASE = getApiBase();

let refreshSessionInFlight: Promise<boolean> | null = null;
let auth401ConsecutiveFailures = 0;
let auth401CooldownUntil = 0;
let lastSessionRefreshAttemptAt = 0;

const AUTH_401_MAX_CONSECUTIVE_FAILURES = 3;
const AUTH_401_COOLDOWN_MS = 30_000;
const MIN_REFRESH_ATTEMPT_INTERVAL_MS = 1_500;
const ACCESS_TOKEN_STORAGE_KEY = "auth-token";

const clearBrowserAccessToken = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  } catch {
    // ignore localStorage failures
  }
};

const inAuth401Cooldown = () => Date.now() < auth401CooldownUntil;

const markAuth401Recovered = () => {
  auth401ConsecutiveFailures = 0;
  auth401CooldownUntil = 0;
};

const registerAuth401Failure = (source: string) => {
  auth401ConsecutiveFailures += 1;
  const now = Date.now();
  if (
    auth401ConsecutiveFailures >= AUTH_401_MAX_CONSECUTIVE_FAILURES &&
    now >= auth401CooldownUntil
  ) {
    auth401CooldownUntil = now + AUTH_401_COOLDOWN_MS;
    log.api.warn(
      `401 retry circuit opened (${source}) for ${Math.round(AUTH_401_COOLDOWN_MS / 1000)}s`,
    );
  }
};

const refreshBrowserSession = async (): Promise<boolean> => {
  // Only meaningful in the browser where /api/auth/me can use httpOnly cookies
  // to refresh a split or expired session without trusting a stale local token.
  if (typeof window === "undefined") return false;

  if (inAuth401Cooldown()) {
    return false;
  }

  if (refreshSessionInFlight) {
    return refreshSessionInFlight;
  }

  const now = Date.now();
  if (now - lastSessionRefreshAttemptAt < MIN_REFRESH_ATTEMPT_INTERVAL_MS) {
    return false;
  }
  lastSessionRefreshAttemptAt = now;

  refreshSessionInFlight = (async () => {
    try {
      const response = await fetch("/api/auth/me", {
        credentials: "include",
        cache: "no-store",
      });
      if (response.ok) {
        markAuth401Recovered();
        return true;
      }
      if (response.status === 401) {
        clearBrowserAccessToken();
        registerAuth401Failure("refresh-session");
      }
      return false;
    } catch {
      return false;
    } finally {
      refreshSessionInFlight = null;
    }
  })();

  return refreshSessionInFlight;
};

const vehicleMapStatusSchema = z.enum(["online", "stationary", "stopped"]);
const vehicleMapFlagsSchema = z.object({
  gps: z.boolean(),
  driverAssigned: z.boolean(),
  fuelLow: z.boolean(),
  mechFault: z.boolean(),
  immobilized: z.boolean(),
});
const vehicleMapLocationSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  heading: z.number().optional(),
  altitude: z.number().optional(),
});
const vehicleMapMotionSchema = z
  .object({
    source: z.enum(["vehicle", "gps-session", "simulation"]),
    position: vehicleMapLocationSchema,
    speedKph: z.number(),
    sampleTimestamp: z.string(),
    feedTimestamp: z.string(),
    stale: z.boolean(),
    ageMs: z.number().optional(),
  })
  .optional();
const vehicleMapDriverSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    phone: z.string().optional(),
  })
  .optional();
const vehicleMapDeviceSchema = z
  .object({
    imei: z.string().optional(),
    protocol: z.string().optional(),
    lastSeen: z.string().optional(),
  })
  .optional();
const vehicleMapSensorsSchema = z
  .object({
    speedKph: z.number().optional(),
    rpm: z.number().optional(),
    signalStrength: z.number().optional(),
    batteryLevel: z.number().optional(),
    batteryVoltage: z.number().optional(),
    fuelLevelPct: z.number().optional(),
    odometerKm: z.number().optional(),
    engineHours: z.number().optional(),
    altitudeMeters: z.number().optional(),
    lastPing: z.string().optional(),
  })
  .optional();
const vehicleMapSchema = z.object({
  id: z.string(),
  plate: z.string().optional(),
  regNo: z.string().optional(),
  number: z.string().optional(),
  vin: z.string().optional(),
  name: z.string(),
  assetType: vehicleAssetTypeSchema.optional(),
  color: z.string().optional(),
  rasterMarkerType: rasterMarkerTypeSchema.optional(),
  status: vehicleMapStatusSchema,
  rawStatus: z.string().optional(),
  speedKph: z.number(),
  rpm: z.number().optional(),
  signalStrength: z.number().optional(),
  batteryLevel: z.number().optional(),
  batteryVoltage: z.number().optional(),
  fuelLevelPct: z.number().optional(),
  odometerKm: z.number().optional(),
  engineHours: z.number().optional(),
  tripCount: z.number().optional(),
  sensors: vehicleMapSensorsSchema,
  lastPing: z.string().optional(),
  location: vehicleMapLocationSchema.optional(),
  flags: vehicleMapFlagsSchema,
  driver: vehicleMapDriverSchema,
  device: vehicleMapDeviceSchema,
  ageMs: z.number().optional(),
  ageMinutes: z.number().optional(),
  stale: z.boolean().optional(),
  hasValidCoords: z.boolean().optional(),
  feedTimestamp: z.string().optional(),
  gpsFixTs: z.string().optional(),
  ingestTs: z.string().optional(),
  motion: vehicleMapMotionSchema,
  geofenceStatus: z.string().optional(),
  geofenceId: z.string().optional(),
  liveSession: z
    .object({
      id: z.string(),
      type: z.enum(["gps", "simulation"]),
      status: z.string(),
      startedAt: z.string(),
      progressPct: z.number().optional(),
    })
    .optional(),
});

export type VehicleMapFeedItem = z.infer<typeof vehicleMapSchema>;
const vehicleMapFeedDeltaSchema = z.object({
  mode: z.literal("delta"),
  generatedAt: z.string(),
  since: z.string().optional(),
  vehicles: vehicleMapSchema.array(),
});
const vehicleMapFeedFullSchema = z.object({
  mode: z.literal("full"),
  generatedAt: z.string(),
  vehicles: vehicleMapSchema.array(),
});
const vehicleMapFeedResponseSchema = z.union([
  vehicleMapFeedDeltaSchema,
  vehicleMapFeedFullSchema,
]);
export type VehicleMapFeedResponse = z.infer<
  typeof vehicleMapFeedResponseSchema
>;

const geoJsonObjectSchema = z
  .object({
    type: z.string(),
    coordinates: z.unknown().optional(),
  })
  .passthrough();

const geofenceSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  type: z.string(),
  geometry: geoJsonObjectSchema,
  active: z.boolean().optional(),
});

export type Geofence = z.infer<typeof geofenceSchema>;
export type CreateGeofenceType = "radius" | "polygon" | "corridor";
export interface CreateGeofencePayload {
  tenantId: string;
  name: string;
  type: CreateGeofenceType;
  radius?: {
    center: { lat: number; lng: number };
    radiusMeters: number;
  };
  polygon?: {
    coordinates: Array<{ lat: number; lng: number }>;
  };
  active?: boolean;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;

const mapApiWarnedKeys = new Set<string>();
const warnMapApiOnce = (key: string, message: string, detail?: unknown) => {
  if (mapApiWarnedKeys.has(key)) return;
  mapApiWarnedKeys.add(key);
  if (detail !== undefined) {
    log.map.warn(message, detail);
    return;
  }
  log.map.warn(message);
};

export interface UserSearchResult {
  id: string;
  name: string;
  email: string;
  avatar?: string | null;
  status?: string | null;
  persona?: string | null;
  tenantId?: string | null;
  organizationId?: string | null;
}

const userSearchResponseSchema = z.object({
  data: z.array(
    z
      .object({
        id: z.string(),
        email: z.string().nullable().optional(),
        firstName: z.string().nullable().optional(),
        lastName: z.string().nullable().optional(),
        status: z.string().nullable().optional(),
        persona: z.string().nullable().optional(),
        tenantId: z.string().nullable().optional(),
        organizationId: z.string().nullable().optional(),
        avatarUrl: z.string().nullable().optional(),
      })
      .passthrough(),
  ),
});

const recentApiErrors = new Map<string, number>();
const API_ERROR_DEDUP_MS = 60_000;
const TICKETS_TRANSIENT_BACKOFF_MIN_MS = 5_000;
const TICKETS_TRANSIENT_BACKOFF_MAX_MS = 60_000;
let ticketsTransientBackoffMs = 0;
let ticketsTransientCooldownUntil = 0;

const isTransientApiStatus = (status: number) =>
  status === 429 || status === 502 || status === 503 || status === 504;

const parseRetryAfterMs = (response: Response): number => {
  const header = response.headers.get("Retry-After");
  if (!header) return 0;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;
  const date = Date.parse(header);
  if (Number.isFinite(date)) return Math.max(0, date - Date.now());
  return 0;
};

const consumeTicketsTransientBackoff = () => {
  ticketsTransientBackoffMs =
    ticketsTransientBackoffMs > 0
      ? Math.min(
        ticketsTransientBackoffMs * 2,
        TICKETS_TRANSIENT_BACKOFF_MAX_MS,
      )
      : TICKETS_TRANSIENT_BACKOFF_MIN_MS;
  ticketsTransientCooldownUntil = Date.now() + ticketsTransientBackoffMs;
  return ticketsTransientBackoffMs;
};

const resetTicketsTransientBackoff = () => {
  ticketsTransientBackoffMs = 0;
  ticketsTransientCooldownUntil = 0;
};

const isTicketsTransientCooldownActive = () =>
  Date.now() < ticketsTransientCooldownUntil;

const shouldLogApiError = (key: string): boolean => {
  const now = Date.now();
  const last = recentApiErrors.get(key);
  if (last && now - last < API_ERROR_DEDUP_MS) return false;
  recentApiErrors.set(key, now);
  if (recentApiErrors.size > 200) {
    const cutoff = now - API_ERROR_DEDUP_MS;
    for (const [k, v] of recentApiErrors) {
      if (v < cutoff) recentApiErrors.delete(k);
    }
  }
  return true;
};

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const tenantScopedHeaders = (
  tenantId?: string | null,
): Record<string, string> | undefined => {
  const normalized = typeof tenantId === "string" ? tenantId.trim() : "";
  return normalized ? { "x-organization-id": normalized } : undefined;
};

type OrganizationRequestOptions = {
  organizationId?: string | null;
  tenantId?: string | null;
};

const resolveRequestOrganizationId = (
  options?: OrganizationRequestOptions,
): string | undefined =>
  resolveScopedOrganizationId({
    organizationId: options?.organizationId,
    tenantId: options?.tenantId,
  });

const applyOrganizationScope = (
  searchParams: URLSearchParams,
  options?: OrganizationRequestOptions,
): string | undefined => {
  const organizationId = resolveRequestOrganizationId(options);
  if (organizationId) {
    searchParams.set("organizationId", organizationId);
  }
  return organizationId;
};

const buildOrganizationScopeQuery = (
  options?: OrganizationRequestOptions,
): string => {
  const organizationId = resolveRequestOrganizationId(options);
  return organizationId
    ? `?organizationId=${encodeURIComponent(organizationId)}`
    : "";
};

const getErrorStatus = (error: unknown): number | null => {
  if (!(error instanceof Error)) return null;
  const match = /Request failed \((\d{3})\)/.exec(error.message);
  if (!match) return null;
  const parsed = Number.parseInt(match[1] ?? "", 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const isSoftMissingFeatureError = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 401 || status === 403 || status === 404;
};

const OPTIONAL_SILENT_404_PATHS = new Set<string>([
  "/settings/ai",
  "/ai/catalog/providers",
  // Public marketing-page enhancements: these render optional blog content and
  // degrade to an empty state, so a 404 (e.g. no blog org configured) is expected
  // and should not surface as a console error.
  "/public/blog/recent",
  "/public/blog/articles",
  "/public/blog/categories",
]);

export async function request<T>(path: string, init?: RequestInit) {
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };

  const doFetch = () =>
    fetch(url, {
      ...init,
      headers,
      credentials: "include",
    });

  const method = (init?.method ?? "GET").toUpperCase();
  const isIdempotent = method === "GET" || method === "HEAD";

  let response = await doFetch();

  if (response.status === 401) {
    const refreshed = await refreshBrowserSession();
    if (refreshed) {
      response = await doFetch();
      if (response.status === 401) {
        registerAuth401Failure(
          isIdempotent ? "post-refresh-retry" : "non-idempotent-post-refresh-retry",
        );
      } else if (response.ok) {
        markAuth401Recovered();
      }
    } else {
      registerAuth401Failure(
        isIdempotent ? "refresh-blocked-or-failed" : "non-idempotent-refresh-blocked-or-failed",
      );
    }
  } else if (response.ok) {
    markAuth401Recovered();
  }

  if (isIdempotent && isTransientApiStatus(response.status)) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const retryMs =
        response.status === 429
          ? Math.max(parseRetryAfterMs(response), 1500 * (attempt + 1))
          : 1000 * (attempt + 1);
      await sleep(Math.min(retryMs, 10_000));
      response = await doFetch();
      if (response.ok) break;
      if (!isTransientApiStatus(response.status)) break;
    }
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    const normalizedPath = path.split("?")[0];
    const isMapApiCall =
      normalizedPath.startsWith("/maps/") ||
      normalizedPath === "/settings/maps";
    const isOptionalSilent404 =
      response.status === 404 && OPTIONAL_SILENT_404_PATHS.has(normalizedPath);
    const isTransientTicketsRead =
      normalizedPath === "/tickets" && isTransientApiStatus(response.status);
    const dedupeKey = `${response.status}:${normalizedPath}`;
    if (isMapApiCall) {
      warnMapApiOnce(
        dedupeKey,
        `Map API request failed (${response.status}) on ${normalizedPath}`,
        errorText,
      );
    } else if (isOptionalSilent404) {
      log.api.warn(
        `${response.status} ${normalizedPath} (optional endpoint unavailable)`,
      );
    } else if (isTransientTicketsRead) {
      if (shouldLogApiError(dedupeKey)) {
        log.api.warn(`${response.status} ${normalizedPath} (transient)`);
      }
    } else if (shouldLogApiError(dedupeKey)) {
      if (response.status === 401) {
        log.api.warn(`${response.status} ${normalizedPath}`);
      } else {
        log.api.error(`${response.status} ${normalizedPath}`);
      }
    }
    throw new Error(`Request failed (${response.status}): ${errorText}`);
  }

  return response.json() as Promise<T>;
}

async function requestFormData<T>(
  path: string,
  body: FormData,
  options?: {
    method?: "POST" | "PATCH";
    includeAuthorization?: boolean;
    headers?: Record<string, string>;
  },
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    ...(options?.headers ?? {}),
  };
  const method = options?.method ?? "POST";
  const doFetch = () =>
    fetch(url, {
      method,
      body,
      headers,
      credentials: "include",
    });

  let response = await doFetch();
  if (response.status === 401) {
    const refreshed = await refreshBrowserSession();
    if (refreshed) {
      response = await doFetch();
      if (response.status === 401) {
        registerAuth401Failure("formdata-post-refresh-retry");
      } else if (response.ok) {
        markAuth401Recovered();
      }
    } else {
      registerAuth401Failure("formdata-refresh-blocked-or-failed");
    }
  } else if (response.ok) {
    markAuth401Recovered();
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(`Request failed (${response.status}): ${errorText}`);
  }

  return response.json() as Promise<T>;
}

async function requestBinary(path: string, init?: RequestInit) {
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    ...((init?.headers as Record<string, string>) ?? {}),
  };

  const doFetch = () =>
    fetch(url, {
      ...init,
      headers,
      credentials: "include",
    });

  let response = await doFetch();
  const method = (init?.method ?? "GET").toUpperCase();
  if (response.status === 401) {
    const refreshed = await refreshBrowserSession();
    if (refreshed) {
      response = await doFetch();
      if (response.status === 401) {
        registerAuth401Failure(
          method === "GET" || method === "HEAD"
            ? "binary-post-refresh-retry"
            : "binary-non-idempotent-post-refresh-retry",
        );
      } else if (response.ok) {
        markAuth401Recovered();
      }
    } else {
      registerAuth401Failure(
        method === "GET" || method === "HEAD"
          ? "binary-refresh-blocked-or-failed"
          : "binary-non-idempotent-refresh-blocked-or-failed",
      );
    }
  } else if (response.ok) {
    markAuth401Recovered();
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    log.api.error(`Binary ${response.status} ${path.split("?")[0]}`);
    throw new Error(`Binary request failed: ${response.status}`);
  }

  return response;
}

const generateId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `veh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const coerceNumber = (input: unknown, fallback = 0): number => {
  if (typeof input === "number" && Number.isFinite(input)) return input;
  if (typeof input === "string") {
    const parsed = Number.parseFloat(input);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const buildLocation = (
  raw: Record<string, unknown>,
): Vehicle["location"] | undefined => {
  const existing = raw.location as Vehicle["location"] | undefined;
  if (
    existing &&
    Number.isFinite(existing.lat) &&
    Number.isFinite(existing.lng)
  ) {
    return existing as Vehicle["location"];
  }

  const geo = raw.geoCoordinates as
    | { lat?: unknown; lng?: unknown }
    | undefined;
  if (
    geo &&
    Number.isFinite(geo.lat as number) &&
    Number.isFinite(geo.lng as number)
  ) {
    return { lat: geo.lat as number, lng: geo.lng as number };
  }

  const lat = coerceNumber(
    (raw as { latitude?: unknown }).latitude ??
    (raw as { lat?: unknown }).lat ??
    NaN,
    NaN,
  );
  const lng = coerceNumber(
    (raw as { longitude?: unknown }).longitude ??
    (raw as { lng?: unknown }).lng ??
    NaN,
    NaN,
  );
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng };
  }
  return undefined;
};

const extractLastPing = (raw: Record<string, unknown>): string | undefined => {
  const candidates = [raw.lastPing, raw.updatedAt, raw.createdAt];
  for (const value of candidates) {
    if (typeof value === "string" && !Number.isNaN(Date.parse(value))) {
      return value;
    }
  }
  return undefined;
};

const normalizeVehicle = (
  raw: Record<string, unknown>,
): Vehicle & { driverId?: string } => {
  const id = (raw.id as string) || (raw.deviceId as string) || generateId();
  const location = buildLocation(raw);
  const lastPing = extractLastPing(raw) ?? new Date().toISOString();

  const rawRecord = asRecord(raw) ?? {};
  const sensorsRecord = asRecord(rawRecord["sensors"]) ?? {};

  const batteryRaw = coerceNumber(
    sensorsRecord.batteryVoltage ??
    sensorsRecord.batteryLevel ??
    rawRecord.batteryVoltage ??
    rawRecord.batteryLevel ??
    rawRecord.battery ??
    0,
  );

  const sensors: Vehicle["sensors"] = {
    speedKph: coerceNumber(
      sensorsRecord.speedKph ?? rawRecord.speedKph ?? rawRecord.speed ?? 0,
    ),
    rpm: coerceNumber(sensorsRecord.rpm ?? rawRecord.rpm ?? 0),
    batteryVoltage: batteryRaw,
    fuelLevelPct: coerceNumber(
      sensorsRecord.fuelLevelPct ??
      sensorsRecord.fuelLevel ??
      rawRecord.fuelLevel ??
      rawRecord.fuel ??
      0,
    ),
    odometerKm: coerceNumber(
      sensorsRecord.odometerKm ??
      rawRecord.odometerKm ??
      rawRecord.odometer ??
      0,
    ),
    engineTempC: coerceNumber(
      sensorsRecord.engineTempC ?? rawRecord.engineTemp ?? 0,
    ),
    signalStrength: coerceNumber(
      sensorsRecord.signalStrength ?? rawRecord.signalStrength ?? 0,
    ),
    batteryLevel: batteryRaw,
  } as Vehicle["sensors"];

  // Preserve driverId from backend response (may be direct field or nested in driver object)
  const driverId =
    (raw.driverId as string | undefined) ??
    (raw.driver as { id?: string } | undefined)?.id ??
    undefined;

  return {
    ...(raw as Vehicle),
    id,
    location,
    sensors: { ...((raw as Vehicle).sensors ?? {}), ...sensors },
    lastPing,
    ...(driverId ? { driverId } : {}),
  } as Vehicle & { driverId?: string };
};

const telemetryDigestCache: Record<"vehicles" | "map-feed", string | null> = {
  vehicles: null,
  "map-feed": null,
};

const summariseTelemetryCoverage = (
  label: "vehicles" | "map-feed",
  vehicles: Vehicle[],
  raw?: Array<Record<string, unknown>>,
) => {
  if (typeof window === "undefined" || vehicles.length === 0) {
    return;
  }

  const summary = vehicles.reduce(
    (acc, vehicle, index) => {
      const name =
        vehicle.name ??
        (vehicle as { registrationNumber?: string }).registrationNumber ??
        vehicle.id;
      const location = vehicle.location;
      const hasLocation = Boolean(
        location &&
        Number.isFinite(location.lat) &&
        Number.isFinite(location.lng),
      );
      if (hasLocation) {
        acc.withLocation += 1;
      } else {
        acc.missingLocation.push(name);
      }

      const rawRecord = raw?.[index] as
        | (Record<string, unknown> & {
          sensors?: { odometerKm?: unknown };
          odometerKm?: unknown;
          odometer?: unknown;
          lastPing?: unknown;
          updatedAt?: unknown;
          createdAt?: unknown;
        })
        | undefined;

      const lastPingCandidates = [
        vehicle.lastPing,
        rawRecord?.lastPing,
        rawRecord?.updatedAt,
        rawRecord?.createdAt,
      ].filter((value): value is string => typeof value === "string");
      const hasLastPing = lastPingCandidates.some(
        (value) => !Number.isNaN(Date.parse(value)),
      );
      if (hasLastPing) {
        acc.withLastPing += 1;
      } else {
        acc.missingLastPing.push(name);
      }

      const rawOdometer =
        rawRecord?.sensors?.odometerKm ??
        rawRecord?.odometerKm ??
        rawRecord?.odometer;
      const normalizedOdo = vehicle.sensors?.odometerKm;
      const hasOdometer =
        (typeof rawOdometer === "number" && Number.isFinite(rawOdometer)) ||
        (typeof normalizedOdo === "number" &&
          Number.isFinite(normalizedOdo) &&
          normalizedOdo > 0);
      if (hasOdometer) {
        acc.withOdometer += 1;
      } else {
        acc.missingOdometer.push(name);
      }

      return acc;
    },
    {
      total: vehicles.length,
      withLocation: 0,
      withLastPing: 0,
      withOdometer: 0,
      missingLocation: [] as string[],
      missingLastPing: [] as string[],
      missingOdometer: [] as string[],
    },
  );

  const digest = JSON.stringify({
    total: summary.total,
    withLocation: summary.withLocation,
    withLastPing: summary.withLastPing,
    withOdometer: summary.withOdometer,
  });

  if (telemetryDigestCache[label] === digest) {
    return;
  }
  telemetryDigestCache[label] = digest;

  const formatSample = (items: string[]) => {
    const slice = items.slice(0, 3);
    const suffix =
      items.length > slice.length
        ? ` +${items.length - slice.length} more`
        : "";
    return slice.join(", ") + suffix;
  };

  log.telemetry.debug(
    `${label} coverage — total: ${summary.total}, gps: ${summary.withLocation}, ping: ${summary.withLastPing}, odo: ${summary.withOdometer}`,
  );

  if (summary.missingLocation.length) {
    log.telemetry.debug(
      `${label}: vehicles missing location → ${formatSample(summary.missingLocation)}`,
    );
  }
  if (summary.missingLastPing.length) {
    log.telemetry.debug(
      `${label}: vehicles missing lastPing → ${formatSample(summary.missingLastPing)}`,
    );
  }
  if (summary.missingOdometer.length) {
    log.telemetry.debug(
      `${label}: vehicles missing odometer → ${formatSample(summary.missingOdometer)}`,
    );
  }
};

export async function fetchVehicles(options?: {
  parentUserId?: string;
  tenantId?: string;
  organizationId?: string;
}): Promise<Vehicle[]> {
  const params = new URLSearchParams();
  if (options?.parentUserId) {
    params.set("parentUserId", options.parentUserId);
  }
  const orgId = applyOrganizationScope(params, options);
  const query = params.toString();
  const data = await request<Array<Record<string, unknown>>>(
    `/vehicles${query ? `?${query}` : ""}`,
    { headers: tenantScopedHeaders(orgId) } as RequestInit,
  );
  const normalized = data.map(normalizeVehicle);
  summariseTelemetryCoverage("vehicles", normalized, data);
  return normalized;
}

export async function fetchChildUsers(parentUserId: string) {
  const data = await request<unknown[]>(
    `/users/parent/${parentUserId}/children`,
  );
  return data;
}

export async function fetchParentUser(userId: string) {
  const data = await request<unknown>(`/users/${userId}/parent`);
  return data;
}

// Session Instance Management APIs
export async function createSessionInstance(data: {
  name: string;
  type: string;
  description?: string;
  managerId?: string;
  enabledServices?: string[];
  permissions?: string[];
  vehicleIds?: string[];
  deviceIds?: string[];
}) {
  return request<unknown>("/sessions", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function fetchSessions() {
  return request<unknown>("/sessions");
}

export async function fetchSessionInstance(sessionId: string) {
  return request<unknown>(`/sessions/${sessionId}`);
}

export async function updateSessionInstance(
  sessionId: string,
  data: {
    name?: string;
    description?: string;
    enabledServices?: string[];
    permissions?: string[];
  },
) {
  return request<unknown>(`/sessions/${sessionId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteSessionInstance(sessionId: string) {
  return request<unknown>(`/sessions/${sessionId}`, {
    method: "DELETE",
  });
}

export async function addMemberToSession(
  sessionId: string,
  userId: string,
  role: string,
  permissions?: string[],
) {
  return request<unknown>(`/sessions/${sessionId}/members`, {
    method: "POST",
    body: JSON.stringify({ userId, role, permissions }),
  });
}

export async function removeMemberFromSession(
  sessionId: string,
  userId: string,
) {
  return request<unknown>(`/sessions/${sessionId}/members/${userId}`, {
    method: "DELETE",
  });
}

export async function fetchSessionVehicles(sessionId: string) {
  return request<unknown[]>(`/sessions/${sessionId}/vehicles`);
}

export async function assignVehiclesToSession(
  sessionId: string,
  vehicleIds: string[],
) {
  return request<unknown>(`/sessions/${sessionId}/vehicles`, {
    method: "PUT",
    body: JSON.stringify({ vehicleIds }),
  });
}

export async function fetchSessionDevices(sessionId: string) {
  return request<unknown[]>(`/sessions/${sessionId}/devices`);
}

export async function assignDevicesToSession(
  sessionId: string,
  deviceIds: string[],
) {
  return request<unknown>(`/sessions/${sessionId}/devices`, {
    method: "PUT",
    body: JSON.stringify({ deviceIds }),
  });
}

export async function fetchVehicleMapFeedWithMeta(params?: {
  tenantId?: string;
  organizationId?: string;
  fleetId?: string;
  bbox?: string;
  since?: string;
  zoom?: number;
  limit?: number;
  mode?: "full" | "delta";
}): Promise<VehicleMapFeedResponse> {
  const searchParams = new URLSearchParams();
  const orgId = applyOrganizationScope(searchParams, params);
  if (params?.fleetId) searchParams.set("fleetId", params.fleetId);
  if (params?.bbox) searchParams.set("bbox", params.bbox);
  if (params?.since) searchParams.set("since", params.since);
  if (Number.isFinite(params?.zoom ?? Number.NaN)) {
    searchParams.set("zoom", String(params?.zoom));
  }
  if (Number.isFinite(params?.limit ?? Number.NaN)) {
    searchParams.set("limit", String(params?.limit));
  }
  if (params?.mode) searchParams.set("mode", params.mode);

  const query = searchParams.toString();
  const endpoint = `/vehicles/feed${query ? `?${query}` : ""}`;
  let data: unknown;
  try {
    data = await request<unknown>(endpoint, {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit);
  } catch (error) {
    if (params?.mode === "delta" && params?.since) {
      log.api.warn("Delta vehicle feed failed, retrying with full feed.", {
        endpoint,
        error: error instanceof Error ? error.message : String(error),
      });
      const fallbackSearchParams = new URLSearchParams();
      applyOrganizationScope(fallbackSearchParams, params);
      if (params?.fleetId) fallbackSearchParams.set("fleetId", params.fleetId);
      if (params?.bbox) fallbackSearchParams.set("bbox", params.bbox);
      if (Number.isFinite(params?.zoom ?? Number.NaN)) {
        fallbackSearchParams.set("zoom", String(params?.zoom));
      }
      if (Number.isFinite(params?.limit ?? Number.NaN)) {
        fallbackSearchParams.set("limit", String(params?.limit));
      }
      const fallbackQuery = fallbackSearchParams.toString();
      const fallbackEndpoint = `/vehicles/feed${fallbackQuery ? `?${fallbackQuery}` : ""}`;
      data = await request<unknown>(fallbackEndpoint, {
        headers: tenantScopedHeaders(orgId),
      } as RequestInit);
    } else {
      throw error;
    }
  }
  const parsedResponse = Array.isArray(data)
    ? vehicleMapFeedFullSchema.parse({
      mode: "full",
      generatedAt: new Date().toISOString(),
      vehicles: data,
    })
    : vehicleMapFeedResponseSchema.parse(data);
  const parsed = vehicleMapSchema.array().parse(parsedResponse.vehicles);
  const normalizedVehicles: Vehicle[] = [];
  const rawRecords: Array<Record<string, unknown>> = [];

  const mapped = parsed.map((item) => {
    const rawRecord = item as unknown as Record<string, unknown>;
    rawRecords.push(rawRecord);
    const normalized = normalizeVehicle(rawRecord);
    const normalizedRegistration =
      item.regNo ??
      item.plate ??
      normalized.registration?.regNo ??
      (normalized as unknown as { number?: string }).number;
    const normalizedVin =
      item.vin ??
      normalized.registration?.vin ??
      (normalized as unknown as { vin?: string }).vin;
    const mergedSensors = {
      ...normalized.sensors,
      ...(item.sensors ?? {}),
      speedKph:
        item.sensors?.speedKph ?? normalized.sensors?.speedKph ?? item.speedKph,
      rpm: item.sensors?.rpm ?? normalized.sensors?.rpm ?? item.rpm,
      signalStrength:
        item.sensors?.signalStrength ??
        (normalized.sensors as { signalStrength?: number } | undefined)
          ?.signalStrength ??
        item.signalStrength,
      batteryLevel:
        item.sensors?.batteryLevel ??
        (normalized.sensors as { batteryLevel?: number } | undefined)
          ?.batteryLevel ??
        item.batteryLevel,
      batteryVoltage:
        item.sensors?.batteryVoltage ??
        normalized.sensors?.batteryVoltage ??
        item.batteryVoltage,
      fuelLevelPct:
        item.sensors?.fuelLevelPct ??
        normalized.sensors?.fuelLevelPct ??
        item.fuelLevelPct,
      odometerKm:
        item.sensors?.odometerKm ??
        normalized.sensors?.odometerKm ??
        item.odometerKm,
      engineHours:
        item.sensors?.engineHours ??
        (normalized.sensors as { engineHours?: number } | undefined)
          ?.engineHours ??
        item.engineHours,
      altitudeMeters:
        item.sensors?.altitudeMeters ??
        (normalized.sensors as { altitudeMeters?: number } | undefined)
          ?.altitudeMeters,
      lastPing: item.sensors?.lastPing ?? normalized.lastPing ?? item.lastPing,
    };
    const normalizedVehicle = {
      ...normalized,
      regNo: normalizedRegistration,
      number:
        item.number ??
        (normalized as unknown as { number?: string }).number ??
        undefined,
      vin: normalizedVin,
      speedKph: mergedSensors.speedKph ?? item.speedKph,
      rawStatus:
        item.rawStatus ??
        String(
          (normalized as unknown as { status?: string | undefined }).status ??
          item.status ??
          "unknown",
        ),
      tripCount:
        item.tripCount ??
        (normalized as unknown as { tripCount?: number | undefined }).tripCount,
      sensors: mergedSensors,
      registration: {
        ...(normalized.registration ?? {}),
        regNo: normalizedRegistration ?? undefined,
        vin: normalizedVin ?? undefined,
      },
      driver:
        item.driver ?? (normalized as unknown as { driver?: unknown }).driver,
    } as Vehicle;
    normalizedVehicles.push(normalizedVehicle);

    return {
      id: normalizedVehicle.id,
      plate: item.plate ?? normalizedRegistration ?? undefined,
      regNo: normalizedRegistration ?? undefined,
      number:
        item.number ??
        (normalized as unknown as { number?: string | undefined }).number ??
        undefined,
      vin: normalizedVin ?? undefined,
      name: normalizedVehicle.name ?? item.name,
      assetType: item.assetType,
      color: item.color,
      rasterMarkerType: item.rasterMarkerType,
      status: item.status,
      rawStatus:
        item.rawStatus ??
        (normalizedVehicle as unknown as { rawStatus?: string | undefined })
          .rawStatus ??
        undefined,
      speedKph: mergedSensors.speedKph ?? item.speedKph,
      rpm: mergedSensors.rpm ?? item.rpm ?? undefined,
      signalStrength:
        mergedSensors.signalStrength ?? item.signalStrength ?? undefined,
      batteryLevel:
        mergedSensors.batteryLevel ?? item.batteryLevel ?? undefined,
      batteryVoltage:
        mergedSensors.batteryVoltage ?? item.batteryVoltage ?? undefined,
      fuelLevelPct:
        mergedSensors.fuelLevelPct ?? item.fuelLevelPct ?? undefined,
      odometerKm: mergedSensors.odometerKm ?? item.odometerKm ?? undefined,
      engineHours: mergedSensors.engineHours ?? item.engineHours ?? undefined,
      tripCount:
        item.tripCount ??
        (normalized as unknown as { tripCount?: number | undefined }).tripCount,
      sensors: mergedSensors,
      lastPing: normalizedVehicle.lastPing ?? item.lastPing,
      location: normalizedVehicle.location ?? item.location,
      flags: item.flags,
      driver: item.driver,
      device: item.device,
      stale: item.stale,
      hasValidCoords: item.hasValidCoords,
      liveSession: item.liveSession,
      feedTimestamp: item.feedTimestamp,
      gpsFixTs: item.gpsFixTs,
      ingestTs: item.ingestTs,
      motion: item.motion,
      geofenceStatus: item.geofenceStatus,
      geofenceId: item.geofenceId,
    } satisfies VehicleMapFeedItem;
  });

  summariseTelemetryCoverage("map-feed", normalizedVehicles, rawRecords);
  if (parsedResponse.mode === "delta") {
    return {
      mode: "delta",
      generatedAt: parsedResponse.generatedAt,
      since: parsedResponse.since,
      vehicles: mapped,
    };
  }
  return {
    mode: "full",
    generatedAt: parsedResponse.generatedAt,
    vehicles: mapped,
  };
}

export async function fetchVehicleMapFeed(params?: {
  tenantId?: string;
  organizationId?: string;
  fleetId?: string;
  bbox?: string;
  since?: string;
  zoom?: number;
  limit?: number;
  mode?: "full" | "delta";
}): Promise<VehicleMapFeedItem[]> {
  const response = await fetchVehicleMapFeedWithMeta(params);
  return response.vehicles;
}

export async function fetchGeofences(params?: {
  tenantId?: string;
  active?: boolean;
}): Promise<Geofence[]> {
  const searchParams = new URLSearchParams();
  if (params?.tenantId) searchParams.set("tenantId", params.tenantId);
  if (params?.active !== undefined) {
    searchParams.set("active", String(params.active));
  }
  const query = searchParams.toString();
  const endpoint = `/geofences${query ? `?${query}` : ""}`;
  const data = await request<unknown[]>(endpoint);
  return data.map((item) => geofenceSchema.parse(item));
}

export async function createGeofence(
  payload: CreateGeofencePayload,
): Promise<Geofence> {
  const normalizedType = String(
    payload.type ?? "",
  ).toLowerCase() as CreateGeofenceType;
  if (!payload.tenantId || !payload.name?.trim()) {
    throw new Error("Geofence tenantId and name are required.");
  }
  if (!["radius", "polygon", "corridor"].includes(normalizedType)) {
    throw new Error(`Unsupported geofence type: ${String(payload.type)}`);
  }

  const sanitized: CreateGeofencePayload = {
    tenantId: payload.tenantId,
    name: payload.name.trim(),
    type: normalizedType,
    ...(typeof payload.active === "boolean" ? { active: payload.active } : {}),
  };

  if (normalizedType === "radius") {
    const center = payload.radius?.center;
    const radiusMeters = payload.radius?.radiusMeters;
    const validCenter =
      typeof center?.lat === "number" &&
      Number.isFinite(center.lat) &&
      typeof center?.lng === "number" &&
      Number.isFinite(center.lng);
    if (
      !validCenter ||
      typeof radiusMeters !== "number" ||
      !Number.isFinite(radiusMeters) ||
      radiusMeters <= 0
    ) {
      throw new Error(
        "Radius geofence requires center(lat,lng) and a positive radiusMeters.",
      );
    }
    sanitized.radius = {
      center: { lat: center.lat, lng: center.lng },
      radiusMeters,
    };
  } else {
    const points = (payload.polygon?.coordinates ?? [])
      .map((point) => ({
        lat: Number(point?.lat),
        lng: Number(point?.lng),
      }))
      .filter(
        (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
      );
    if (points.length < 4) {
      throw new Error(
        "Polygon/corridor geofence requires at least 4 coordinates.",
      );
    }
    sanitized.polygon = {
      coordinates: points,
    };
  }

  const data = await request<unknown>(`/geofences`, {
    method: "POST",
    body: JSON.stringify(sanitized),
  });

  return geofenceSchema.parse(data);
}

export async function deleteGeofence(geofenceId: string): Promise<void> {
  await request(`/geofences/${geofenceId}`, {
    method: "DELETE",
  });
}

export async function replayTelemetryDlq(
  count?: number,
): Promise<{ replayed: number }> {
  const payload = count ? { count } : undefined;
  const data = await request<{ status: string; replayed: number }>(
    "/dlq/telemetry/replay",
    {
      method: "POST",
      body: payload ? JSON.stringify(payload) : undefined,
    },
  );
  return { replayed: data.replayed };
}

export async function replayWebhookDlq(
  count?: number,
): Promise<{ replayed: number }> {
  const payload = count ? { count } : undefined;
  const data = await request<{ status: string; replayed: number }>(
    "/dlq/webhooks/replay",
    {
      method: "POST",
      body: payload ? JSON.stringify(payload) : undefined,
    },
  );
  return { replayed: data.replayed };
}

export async function fetchDrivers(options?: {
  tenantId?: string;
  organizationId?: string;
}): Promise<Driver[]> {
  const orgId = resolveRequestOrganizationId(options);
  const query = buildOrganizationScopeQuery(options);
  const data = await request<unknown[]>(`/vehicles/drivers${query}`, {
    headers: tenantScopedHeaders(orgId),
  } as RequestInit);
  return data as Driver[];
}

export async function fetchPendingOnboarding(
  limit?: number,
): Promise<DeviceOnboarding[]> {
  const query = limit ? `?limit=${limit}` : "";
  try {
    const data = await request<unknown[]>(
      `/vehicles/onboarding/pending${query}`,
    );
    return data as DeviceOnboarding[];
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Device onboarding endpoint unavailable; returning empty list.",
      );
      return [];
    }
    throw error;
  }
}

export async function approveOnboarding(id: string) {
  return request(`/vehicles/onboarding/${id}/approve`, { method: "POST" });
}

export async function rejectOnboarding(id: string) {
  return request(`/vehicles/onboarding/${id}/reject`, { method: "POST" });
}

export async function fetchDeviceRecommendation(
  deviceId: string,
): Promise<DeviceRecommendation | null> {
  const data = await request<{ recommendation: DeviceRecommendation | null }>(
    `/vehicles/${deviceId}/recommendation`,
  );
  return data.recommendation ?? null;
}

export async function registerDevice(payload: RegisterDevice) {
  return request("/vehicles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

const toIsoTimestamp = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const normalized = value < 1_000_000_000_000 ? value * 1000 : value;
    return new Date(normalized).toISOString();
  }
  return undefined;
};

const resolveTelemetryTimestamp = (
  payload: Record<string, unknown>,
  fallbackIso: string,
): string => {
  const metadata =
    payload.meta &&
      typeof payload.meta === "object" &&
      !Array.isArray(payload.meta)
      ? (payload.meta as Record<string, unknown>)
      : undefined;
  const candidate =
    toIsoTimestamp(payload.ts) ??
    toIsoTimestamp(payload.timestamp) ??
    toIsoTimestamp(payload.recordedAt) ??
    toIsoTimestamp(payload.createdAt) ??
    toIsoTimestamp(payload.receivedAt) ??
    toIsoTimestamp(payload.ingestedAt) ??
    toIsoTimestamp(metadata?.ts) ??
    toIsoTimestamp(metadata?.timestamp);
  return candidate ?? fallbackIso;
};

export async function fetchTelemetrySnapshot(deviceId: string) {
  const data = await request<Record<string, unknown>>(
    `/ingest/latest/${deviceId}`,
  );
  const fallbackIso = new Date().toISOString();
  const normalized = {
    ...data,
    ts: resolveTelemetryTimestamp(data, fallbackIso),
  };
  return telemetrySchema.parse(normalized);
}

export async function fetchTelemetryHistory(
  deviceId: string,
  limit = 120,
): Promise<Telemetry[]> {
  const data = await request<unknown[]>(
    `/ingest/history/${deviceId}?limit=${limit}`,
  );
  return telemetryBatchSchema.parse(data);
}

export async function listConversations(
  tenantId: string,
  scope?: "admin" | "user",
  deviceId?: string,
): Promise<ConversationThread[]> {
  const params = new URLSearchParams({ tenantId });
  if (scope) params.set("scope", scope);
  if (deviceId) params.set("deviceId", deviceId);
  const data = await request<unknown[]>(
    `/conversations/threads?${params.toString()}`,
  );
  return data.map((thread) => conversationThreadSchema.parse(thread));
}

export async function createConversationThread(payload: {
  tenantId: string;
  prompt: string;
  scope: "admin" | "user";
  title?: string;
  deviceId?: string;
  metadata?: Record<string, unknown>;
}): Promise<ConversationThread> {
  const data = await request<unknown>("/conversations/threads", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return conversationThreadSchema.parse(data);
}

export async function sendConversationMessage(payload: {
  tenantId: string;
  threadId: string;
  scope: "admin" | "user";
  body: string;
  metadata?: Record<string, unknown>;
}): Promise<ConversationThread> {
  const { threadId, ...rest } = payload;
  const data = await request<unknown>(
    `/conversations/threads/${threadId}/messages`,
    {
      method: "POST",
      body: JSON.stringify(rest),
    },
  );
  return conversationThreadSchema.parse(data);
}

export async function summarizeConversation(
  threadId: string,
  tenantId: string,
): Promise<ChatResponse | null> {
  const data = await request<unknown>(
    `/conversations/threads/${threadId}/summary`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    },
  );
  const parsed = chatResponseSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

export async function fetchAiSettings(
  options?: OrganizationRequestOptions,
): Promise<AiSettings> {
  try {
    const data = await request<unknown>(
      `/settings/ai${buildOrganizationScopeQuery(options)}`,
    );
    return aiSettingsSchema.parse((data as { data: unknown }).data ?? data);
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "AI settings endpoint unavailable; using safe empty defaults.",
      );
      return aiSettingsSchema.parse({
        defaultProviderId: "",
        providers: [],
        updatedAt: new Date().toISOString(),
        updatedBy: "system",
      });
    }
    throw error;
  }
}

export async function updateAiSettings(
  payload: AiSettings,
  options?: OrganizationRequestOptions,
): Promise<AiSettings> {
  const data = await request<unknown>(
    `/settings/ai${buildOrganizationScopeQuery(options)}`,
    {
      method: "PATCH",
      body: JSON.stringify({
        defaultProviderId: payload.defaultProviderId,
        providers: payload.providers,
        updatedBy: payload.updatedBy,
      }),
    },
  );
  return aiSettingsSchema.parse((data as { data: unknown }).data ?? data);
}

export async function fetchAiProviderCatalog(): Promise<AiProviderCatalog> {
  try {
    const data = await request<unknown>("/ai/catalog/providers");
    return aiProviderCatalogSchema.parse(data);
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "AI provider catalog endpoint unavailable; using empty provider catalog.",
      );
      return aiProviderCatalogSchema.parse({
        providers: [],
        generatedAt: new Date().toISOString(),
      });
    }
    throw error;
  }
}

const adminArticlesResponseSchema = z
  .object({
    data: z.array(articleListItemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
    analyticsSnippet: z
      .object({
        gaMeasurementId: z.string().nullable().optional(),
        trackArticleViews: z.boolean(),
        topViewed: z.array(
          z.object({
            articleId: z.string(),
            slug: z.string(),
            title: z.string(),
            viewCount: z.number().int().nonnegative(),
            recentViews: z.number().int().nonnegative(),
          }),
        ),
      })
      .optional(),
  })
  .strict();

const publicArticlesResponseSchema = z
  .object({
    organizationId: z.string().nullable(),
    data: z.array(articleListItemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
  })
  .strict();

const publicBlogCategorySchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    color: z.string().nullable(),
    articleCount: z.number().int().nonnegative(),
  })
  .strict();

const publicRecentArticleSchema = articleListItemSchema
  .extend({
    preview: z.string(),
    readingMinutes: z.number().int().nonnegative(),
  })
  .strict();

const trackArticleViewResponseSchema = z
  .object({
    tracked: z.boolean(),
    articleId: z.string().optional(),
  })
  .strict();

const submitPublicContactResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    routedTo: z.string(),
  })
  .strict();

export type AdminArticlesResponse = z.infer<typeof adminArticlesResponseSchema>;
export type PublicArticlesResponse = z.infer<
  typeof publicArticlesResponseSchema
>;
export type PublicBlogCategory = z.infer<typeof publicBlogCategorySchema>;
export type PublicRecentArticle = z.infer<typeof publicRecentArticleSchema>;
export type TrackPublicArticleViewResponse = z.infer<
  typeof trackArticleViewResponseSchema
>;
export type SubmitPublicContactResponse = z.infer<
  typeof submitPublicContactResponseSchema
>;

const PUBLIC_BLOG_CACHE_TTL_MS = 60_000;
const publicBlogCache = new Map<
  string,
  { expiresAt: number; value: unknown }
>();

const getPublicBlogCache = <T>(key: string): T | null => {
  if (typeof window === "undefined") return null;
  const hit = publicBlogCache.get(key);
  if (!hit) return null;
  if (Date.now() > hit.expiresAt) {
    publicBlogCache.delete(key);
    return null;
  }
  return hit.value as T;
};

const setPublicBlogCache = <T>(key: string, value: T) => {
  if (typeof window === "undefined") return;
  publicBlogCache.set(key, {
    expiresAt: Date.now() + PUBLIC_BLOG_CACHE_TTL_MS,
    value,
  });
};

const invalidatePublicBlogCache = () => {
  publicBlogCache.clear();
};

export async function fetchAdminArticles(query?: {
  organizationId?: string;
  status?: string;
  categoryId?: string;
  tagId?: string;
  q?: string;
  page?: number;
  limit?: number;
  sortBy?: "createdAt" | "updatedAt" | "publishedAt" | "viewCount";
  order?: "asc" | "desc";
  includeUnpublished?: boolean;
}): Promise<AdminArticlesResponse> {
  const params = new URLSearchParams();
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
  }
  const suffix = params.toString();
  const data = await request<unknown>(
    `/blog/articles${suffix ? `?${suffix}` : ""}`,
  );
  return adminArticlesResponseSchema.parse(data);
}

export async function fetchAdminArticle(
  articleId: string,
  organizationId?: string,
): Promise<ArticleBase> {
  const scopedPath = organizationId
    ? `/blog/articles/${encodeURIComponent(articleId)}?organizationId=${encodeURIComponent(organizationId)}`
    : `/blog/articles/${encodeURIComponent(articleId)}`;
  const data = await request<unknown>(scopedPath);
  return articleBaseSchema.parse(data);
}

const stripBlogScopeFields = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  const {
    organizationId: _organizationId,
    providerConfig: _providerConfig,
    ...rest
  } = payload;
  return rest;
};

export async function createAdminArticle(
  payload: CreateArticleRequest,
  organizationId?: string,
): Promise<ArticleBase> {
  const body = createArticleRequestSchema.parse(
    stripBlogScopeFields(payload as Record<string, unknown>),
  );
  const scopedPath = organizationId
    ? `/blog/articles?organizationId=${encodeURIComponent(organizationId)}`
    : "/blog/articles";
  const data = await request<unknown>(scopedPath, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const parsed = articleBaseSchema.parse(data);
  invalidatePublicBlogCache();
  return parsed;
}

export async function updateAdminArticle(
  articleId: string,
  payload: Partial<CreateArticleRequest>,
  organizationId?: string,
): Promise<ArticleBase> {
  const parsedPayload = updateArticleRequestSchema.parse({
    id: articleId,
    ...stripBlogScopeFields(payload as Record<string, unknown>),
  });
  const { id: _id, ...body } = parsedPayload;
  const scopedPath = organizationId
    ? `/blog/articles/${encodeURIComponent(articleId)}?organizationId=${encodeURIComponent(organizationId)}`
    : `/blog/articles/${encodeURIComponent(articleId)}`;
  const data = await request<unknown>(scopedPath, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  const parsed = articleBaseSchema.parse(data);
  invalidatePublicBlogCache();
  return parsed;
}

export async function deleteAdminArticle(
  articleId: string,
  organizationId?: string,
): Promise<{ success: boolean }> {
  const scopedPath = organizationId
    ? `/blog/articles/${encodeURIComponent(articleId)}?organizationId=${encodeURIComponent(organizationId)}`
    : `/blog/articles/${encodeURIComponent(articleId)}`;
  const data = await request<unknown>(scopedPath, {
    method: "DELETE",
  });
  const parsed = z.object({ success: z.boolean() }).parse(data);
  invalidatePublicBlogCache();
  return parsed;
}

export async function publishAdminArticle(
  articleId: string,
  payload: PublishArticleRequest,
  organizationId?: string,
): Promise<ArticleBase> {
  const body = publishArticleRequestSchema.parse(payload);
  const scopedPath = organizationId
    ? `/blog/articles/${encodeURIComponent(articleId)}/publish?organizationId=${encodeURIComponent(organizationId)}`
    : `/blog/articles/${encodeURIComponent(articleId)}/publish`;
  const data = await request<unknown>(scopedPath, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const parsed = articleBaseSchema.parse(data);
  invalidatePublicBlogCache();
  return parsed;
}

export async function aiGenerateAdminArticle(
  payload: AiGenerateArticleRequest,
  organizationId?: string,
): Promise<ArticleBase> {
  const body = aiGenerateArticleRequestSchema.parse(
    stripBlogScopeFields(payload as Record<string, unknown>),
  );
  const scopedPath = organizationId
    ? `/blog/ai-generate?organizationId=${encodeURIComponent(organizationId)}`
    : "/blog/ai-generate";
  const data = await request<unknown>(scopedPath, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const parsed = articleBaseSchema.parse(data);
  invalidatePublicBlogCache();
  return parsed;
}

export async function aiRefreshAdminArticle(
  articleId: string,
  payload: Omit<AiRefreshArticleRequest, "articleId">,
  organizationId?: string,
): Promise<ArticleBase> {
  const body = aiRefreshArticleRequestSchema.parse({
    articleId,
    ...stripBlogScopeFields(payload as Record<string, unknown>),
  });
  const { articleId: _articleId, ...sendBody } = body;
  const scopedPath = organizationId
    ? `/blog/articles/${encodeURIComponent(articleId)}/ai-refresh?organizationId=${encodeURIComponent(organizationId)}`
    : `/blog/articles/${encodeURIComponent(articleId)}/ai-refresh`;
  const data = await request<unknown>(scopedPath, {
    method: "POST",
    body: JSON.stringify(sendBody),
  });
  const parsed = articleBaseSchema.parse(data);
  invalidatePublicBlogCache();
  return parsed;
}

export async function fetchPublicBlogArticles(query?: {
  organizationId?: string;
  organizationSlug?: string;
  category?: string;
  tag?: string;
  q?: string;
  page?: number;
  limit?: number;
}): Promise<PublicArticlesResponse> {
  const params = new URLSearchParams();
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
  }
  const suffix = params.toString();
  const cacheKey = `public-blog:articles:${suffix}`;
  const cached = getPublicBlogCache<PublicArticlesResponse>(cacheKey);
  if (cached) return cached;
  const data = await request<unknown>(
    `/public/blog/articles${suffix ? `?${suffix}` : ""}`,
  );
  const parsed = publicArticlesResponseSchema.parse(data);
  setPublicBlogCache(cacheKey, parsed);
  return parsed;
}

export async function fetchPublicBlogArticle(
  slug: string,
  query?: {
    organizationId?: string;
    organizationSlug?: string;
  },
): Promise<ArticleBase> {
  const params = new URLSearchParams();
  if (query?.organizationId) {
    params.set("organizationId", query.organizationId);
  }
  if (query?.organizationSlug) {
    params.set("organizationSlug", query.organizationSlug);
  }
  const suffix = params.toString();
  const cacheKey = `public-blog:article:${slug}:${suffix}`;
  const cached = getPublicBlogCache<ArticleBase>(cacheKey);
  if (cached) return cached;
  const data = await request<unknown>(
    `/public/blog/articles/${encodeURIComponent(slug)}${suffix ? `?${suffix}` : ""}`,
  );
  const parsed = articleBaseSchema.parse(data);
  setPublicBlogCache(cacheKey, parsed);
  return parsed;
}

export async function trackPublicBlogArticleView(
  slug: string,
  payload?: {
    organizationId?: string;
    organizationSlug?: string;
    source?: string;
    referrer?: string;
    userAgent?: string;
  },
): Promise<TrackPublicArticleViewResponse> {
  const params = new URLSearchParams();
  if (payload?.organizationId) {
    params.set("organizationId", payload.organizationId);
  }
  if (payload?.organizationSlug) {
    params.set("organizationSlug", payload.organizationSlug);
  }
  const suffix = params.toString();
  const data = await request<unknown>(
    `/public/blog/articles/${encodeURIComponent(slug)}/view${suffix ? `?${suffix}` : ""}`,
    {
      method: "POST",
      body: JSON.stringify({
        source: payload?.source,
        referrer: payload?.referrer,
        userAgent: payload?.userAgent,
      }),
    },
  );
  return trackArticleViewResponseSchema.parse(data);
}

export async function fetchPublicBlogCategories(query?: {
  organizationId?: string;
  organizationSlug?: string;
}): Promise<PublicBlogCategory[]> {
  const params = new URLSearchParams();
  if (query?.organizationId) {
    params.set("organizationId", query.organizationId);
  }
  if (query?.organizationSlug) {
    params.set("organizationSlug", query.organizationSlug);
  }
  const suffix = params.toString();
  const cacheKey = `public-blog:categories:${suffix}`;
  const cached = getPublicBlogCache<PublicBlogCategory[]>(cacheKey);
  if (cached) return cached;
  const data = await request<unknown[]>(
    `/public/blog/categories${suffix ? `?${suffix}` : ""}`,
  );
  const parsed = data.map((item) => publicBlogCategorySchema.parse(item));
  setPublicBlogCache(cacheKey, parsed);
  return parsed;
}

export async function fetchRecentPublicBlogArticles(query?: {
  organizationId?: string;
  organizationSlug?: string;
  limit?: number;
}): Promise<PublicRecentArticle[]> {
  const params = new URLSearchParams();
  if (query?.organizationId) {
    params.set("organizationId", query.organizationId);
  }
  if (query?.organizationSlug) {
    params.set("organizationSlug", query.organizationSlug);
  }
  if (typeof query?.limit === "number" && query.limit > 0) {
    params.set("limit", String(query.limit));
  }
  const suffix = params.toString();
  const cacheKey = `public-blog:recent:${suffix}`;
  const cached = getPublicBlogCache<PublicRecentArticle[]>(cacheKey);
  if (cached) return cached;
  const data = await request<unknown[]>(
    `/public/blog/recent${suffix ? `?${suffix}` : ""}`,
  );
  const parsed = data.map((item) => publicRecentArticleSchema.parse(item));
  setPublicBlogCache(cacheKey, parsed);
  return parsed;
}

export async function submitPublicContactForm(payload: {
  organizationId?: string;
  organizationSlug?: string;
  name: string;
  email: string;
  subject?: string;
  phone?: string;
  company?: string;
  industry?: string;
  fleetSize?: string;
  message: string;
}): Promise<SubmitPublicContactResponse> {
  const data = await request<unknown>("/public/contact", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return submitPublicContactResponseSchema.parse(data);
}

export async function fetchTickets(query: ListTicketsQuery): Promise<{
  data: Ticket[];
  nextCursor?: string;
  degraded?: boolean;
  retryAfterMs?: number;
}> {
  const parsed = listTicketsQuerySchema.parse(query);
  if (isTicketsTransientCooldownActive()) {
    return {
      data: [],
      degraded: true,
      retryAfterMs: ticketsTransientBackoffMs,
    };
  }
  const params = new URLSearchParams();
  Object.entries(parsed).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });
  try {
    const data = await request<{ data: unknown[]; nextCursor?: string }>(
      `/tickets?${params.toString()}`,
    );
    resetTicketsTransientBackoff();
    return {
      data: (data.data ?? []).map((item) => ticketSchema.parse(item)),
      nextCursor: data.nextCursor,
    };
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Tickets endpoint unavailable; returning empty ticket list.",
      );
      return { data: [] };
    }
    const status = getErrorStatus(error);
    if (status !== null && isTransientApiStatus(status)) {
      const retryAfterMs = consumeTicketsTransientBackoff();
      log.api.warn(
        `Tickets endpoint transient (${status}); backing off for ${Math.round(
          retryAfterMs / 1000,
        )}s.`,
      );
      return { data: [], degraded: true, retryAfterMs };
    }
    throw error;
  }
}

export async function fetchTicket(ticketId: string): Promise<Ticket> {
  const data = await request<unknown>(`/tickets/${ticketId}`);
  return ticketSchema.parse(data);
}

export async function createTicket(
  payload: CreateTicketInput,
): Promise<Ticket> {
  const body = createTicketSchema.parse(payload);
  const data = await request<unknown>(`/tickets`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return ticketSchema.parse(data);
}

export async function updateTicket(
  ticketId: string,
  payload: UpdateTicketInput,
): Promise<Ticket> {
  const body = updateTicketSchema.parse(payload);
  const data = await request<unknown>(`/tickets/${ticketId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return ticketSchema.parse(data);
}

export async function addTicketEvent(
  ticketId: string,
  payload: AddTicketEventInput,
): Promise<TicketEvent> {
  const body = addTicketEventSchema.parse(payload);
  const data = await request<unknown>(`/tickets/${ticketId}/events`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return ticketEventSchema.parse(data);
}

export async function linkTicketPlaybackClip(
  ticketId: string,
  payload: { eventId: string; comment?: string },
): Promise<TicketEvent> {
  const data = await request<unknown>(
    `/tickets/${ticketId}/attachments/link-playback`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return ticketEventSchema.parse(data);
}

export async function fetchTicketEvents(
  ticketId: string,
): Promise<TicketEvent[]> {
  const data = await request<unknown[]>(`/tickets/${ticketId}/events`);
  return data.map((item) => ticketEventSchema.parse(item));
}

export async function fetchTicketNotifications(
  ticketId: string,
): Promise<TicketNotification[]> {
  const data = await request<unknown[]>(`/tickets/${ticketId}/notifications`);
  return data.map((item) => ticketNotificationSchema.parse(item));
}

export async function fetchUbiSettings(
  options?: OrganizationRequestOptions,
): Promise<{ data: unknown }> {
  return request(`/settings/ubi${buildOrganizationScopeQuery(options)}`);
}

export async function fetchWalletSettings(
  options?: OrganizationRequestOptions,
): Promise<{ data: unknown }> {
  return request(`/settings/wallet${buildOrganizationScopeQuery(options)}`);
}

export async function fetchUbiRiskScores(
  driverId: string,
  tenantId?: string,
): Promise<UbiRiskScore[]> {
  const params = new URLSearchParams({ driverId, window: "28d" });
  if (tenantId) {
    params.set("tenantId", tenantId);
  }
  const data = await request<unknown[]>(`/ubi/scores?${params.toString()}`);
  return data.map((entry) => ubiRiskScoreSchema.parse(entry));
}

export async function fetchUbiLeaderboard(options?: OrganizationRequestOptions & {
  limit?: number;
}): Promise<UbiLeaderboardEntry[]> {
  const search = new URLSearchParams();
  const orgId = applyOrganizationScope(search, options);
  if (typeof options?.limit === "number" && Number.isFinite(options.limit)) {
    search.set("limit", String(options.limit));
  }
  const query = search.toString();
  const data = await request<unknown[]>(
    `/ubi/leaderboard${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return data.map((entry) => ubiLeaderboardEntrySchema.parse(entry));
}

export async function fetchUbiLeaderboardPage(
  options?: OrganizationRequestOptions & {
    search?: string;
    riskBand?: string;
    minScore?: number;
    maxScore?: number;
    sortBy?: "score" | "name" | "trend";
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  },
): Promise<UbiLeaderboardPage> {
  const search = new URLSearchParams();
  const orgId = applyOrganizationScope(search, options);
  if (options?.search) search.set("search", options.search);
  if (options?.riskBand) search.set("riskBand", options.riskBand);
  if (typeof options?.minScore === "number")
    search.set("minScore", String(options.minScore));
  if (typeof options?.maxScore === "number")
    search.set("maxScore", String(options.maxScore));
  if (options?.sortBy) search.set("sortBy", options.sortBy);
  if (options?.sortOrder) search.set("sortOrder", options.sortOrder);
  if (typeof options?.page === "number") search.set("page", String(options.page));
  if (typeof options?.pageSize === "number")
    search.set("pageSize", String(options.pageSize));
  const query = search.toString();
  const data = await request<unknown>(
    `/ubi/leaderboard/page${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return ubiLeaderboardPageSchema.parse(data);
}

export async function fetchUbiDriverRecord(
  driverId: string,
  options?: OrganizationRequestOptions,
): Promise<UbiDriverRecord> {
  const search = new URLSearchParams();
  const orgId = applyOrganizationScope(search, options);
  const query = search.toString();
  const data = await request<unknown>(
    `/ubi/drivers/${encodeURIComponent(driverId)}/record${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return ubiDriverRecordSchema.parse(data);
}

export async function postUbiDriverComment(
  driverId: string,
  payload: CreateUbiDriverCommentInput,
  options?: OrganizationRequestOptions,
): Promise<UbiDriverFeedbackItem> {
  const body = createUbiDriverCommentSchema.parse(payload);
  const search = new URLSearchParams();
  const orgId = applyOrganizationScope(search, options);
  const query = search.toString();
  const data = await request<unknown>(
    `/ubi/drivers/${encodeURIComponent(driverId)}/comments${query ? `?${query}` : ""}`,
    {
      method: "POST",
      headers: tenantScopedHeaders(orgId),
      body: JSON.stringify(body),
    } as RequestInit,
  );
  return ubiDriverFeedbackItemSchema.parse(data);
}

export async function postUbiDriverAction(
  driverId: string,
  payload: ExecuteUbiDriverActionInput,
  options?: OrganizationRequestOptions,
): Promise<UbiDriverActionResult> {
  const body = executeUbiDriverActionSchema.parse(payload);
  const search = new URLSearchParams();
  const orgId = applyOrganizationScope(search, options);
  const query = search.toString();
  const data = await request<unknown>(
    `/ubi/drivers/${encodeURIComponent(driverId)}/actions${query ? `?${query}` : ""}`,
    {
      method: "POST",
      headers: tenantScopedHeaders(orgId),
      body: JSON.stringify(body),
    } as RequestInit,
  );
  return ubiDriverActionResultSchema.parse(data);
}

export async function fetchUbiExportFeed(
  options?: OrganizationRequestOptions & {
    cursor?: string;
    limit?: number;
  },
): Promise<UbiDriverRecordFeed> {
  const search = new URLSearchParams();
  const orgId = applyOrganizationScope(search, options);
  if (options?.cursor) {
    search.set("cursor", options.cursor);
  }
  if (typeof options?.limit === "number" && Number.isFinite(options.limit)) {
    search.set("limit", String(options.limit));
  }
  const query = search.toString();
  const data = await request<unknown>(
    `/ubi/export/records${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return ubiDriverRecordFeedSchema.parse(data);
}

function buildInsuranceOpsScopedParams(
  options?: OrganizationRequestOptions,
): { search: URLSearchParams; orgId?: string } {
  const search = new URLSearchParams();
  const orgId = applyOrganizationScope(search, options);
  return { search, orgId };
}

export async function fetchAgricultureOpsBootstrap(
  options?: OrganizationRequestOptions,
): Promise<AgricultureOpsBootstrap> {
  const query = buildOrganizationScopeQuery(options);
  const orgId = resolveRequestOrganizationId(options);
  const data = await request<unknown>(
    `/agriculture-ops/bootstrap${query}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return agricultureOpsBootstrapSchema.parse(data);
}

export type AgricultureLivePosition = {
  entity: string;
  recordId: string;
  label: string;
  trackerId: string;
  lat: number;
  lng: number;
  heading: number | null;
  speedKph: number | null;
  status: string | null;
  lastPing: string | null;
};

export async function fetchAgricultureLivePositions(
  options?: OrganizationRequestOptions,
): Promise<AgricultureLivePosition[]> {
  const query = buildOrganizationScopeQuery(options);
  const orgId = resolveRequestOrganizationId(options);
  return request<AgricultureLivePosition[]>(
    `/agriculture-ops/live-positions${query}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
}

export async function createAgricultureEntity(
  entity: string,
  payload: Record<string, unknown>,
  options?: OrganizationRequestOptions,
): Promise<unknown> {
  const query = buildOrganizationScopeQuery(options);
  const orgId = resolveRequestOrganizationId(options);
  return request<unknown>(
    `/agriculture-ops/collections/${encodeURIComponent(entity)}${query}`,
    {
      method: "POST",
      headers: tenantScopedHeaders(orgId),
      body: JSON.stringify(payload),
    } as RequestInit,
  );
}

export async function updateAgricultureEntity(
  entity: string,
  recordId: string,
  payload: Record<string, unknown>,
  options?: OrganizationRequestOptions,
): Promise<unknown> {
  const query = buildOrganizationScopeQuery(options);
  const orgId = resolveRequestOrganizationId(options);
  return request<unknown>(
    `/agriculture-ops/collections/${encodeURIComponent(entity)}/${encodeURIComponent(recordId)}${query}`,
    {
      method: "PATCH",
      headers: tenantScopedHeaders(orgId),
      body: JSON.stringify(payload),
    } as RequestInit,
  );
}

export async function deleteAgricultureEntity(
  entity: string,
  recordId: string,
  options?: OrganizationRequestOptions,
): Promise<void> {
  const query = buildOrganizationScopeQuery(options);
  const orgId = resolveRequestOrganizationId(options);
  await request<unknown>(
    `/agriculture-ops/collections/${encodeURIComponent(entity)}/${encodeURIComponent(recordId)}${query}`,
    {
      method: "DELETE",
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
}

export async function addAgricultureComment(
  entity: string,
  recordId: string,
  payload: { author?: string; message: string },
  options?: OrganizationRequestOptions,
): Promise<unknown> {
  const query = buildOrganizationScopeQuery(options);
  const orgId = resolveRequestOrganizationId(options);
  return request<unknown>(
    `/agriculture-ops/collections/${encodeURIComponent(entity)}/${encodeURIComponent(recordId)}/comments${query}`,
    {
      method: "POST",
      headers: tenantScopedHeaders(orgId),
      body: JSON.stringify(payload),
    } as RequestInit,
  );
}

async function postInsuranceOpsComment(
  path: string,
  payload: InsuranceRecordCommentInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  const body = insuranceRecordCommentInputSchema.parse(payload);
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  const query = search.toString();
  const data = await request<unknown>(
    `${path}${query ? `?${query}` : ""}`,
    {
      method: "POST",
      headers: tenantScopedHeaders(orgId),
      body: JSON.stringify(body),
    } as RequestInit,
  );
  return insuranceMutationAckSchema.parse(data);
}

async function postInsuranceOpsAction(
  path: string,
  payload: InsuranceRecordActionInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  const body = insuranceRecordActionInputSchema.parse(payload);
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  const query = search.toString();
  const data = await request<unknown>(
    `${path}${query ? `?${query}` : ""}`,
    {
      method: "POST",
      headers: tenantScopedHeaders(orgId),
      body: JSON.stringify(body),
    } as RequestInit,
  );
  return insuranceMutationAckSchema.parse(data);
}

export async function fetchInsuranceClaims(
  options?: OrganizationRequestOptions & {
    search?: string;
    severity?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  },
): Promise<InsuranceClaimsPage> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  if (options?.search) search.set("search", options.search);
  if (options?.severity) search.set("severity", options.severity);
  if (options?.status) search.set("status", options.status);
  if (options?.sortBy) search.set("sortBy", options.sortBy);
  if (options?.sortOrder) search.set("sortOrder", options.sortOrder);
  if (typeof options?.page === "number") search.set("page", String(options.page));
  if (typeof options?.pageSize === "number")
    search.set("pageSize", String(options.pageSize));
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/claims${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceClaimsPageSchema.parse(data);
}

export async function fetchInsuranceClaimDetail(
  claimId: string,
  options?: OrganizationRequestOptions,
): Promise<InsuranceClaimDetail> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/claims/${encodeURIComponent(claimId)}${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceClaimDetailSchema.parse(data);
}

export async function postInsuranceClaimComment(
  claimId: string,
  payload: InsuranceRecordCommentInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsComment(
    `/insurance-ops/claims/${encodeURIComponent(claimId)}/comments`,
    payload,
    options,
  );
}

export async function postInsuranceClaimAction(
  claimId: string,
  payload: InsuranceRecordActionInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsAction(
    `/insurance-ops/claims/${encodeURIComponent(claimId)}/actions`,
    payload,
    options,
  );
}

export async function fetchInsuranceFraudCases(
  options?: OrganizationRequestOptions & {
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  },
): Promise<InsuranceFraudPage> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  if (options?.search) search.set("search", options.search);
  if (options?.status) search.set("status", options.status);
  if (options?.sortBy) search.set("sortBy", options.sortBy);
  if (options?.sortOrder) search.set("sortOrder", options.sortOrder);
  if (typeof options?.page === "number") search.set("page", String(options.page));
  if (typeof options?.pageSize === "number")
    search.set("pageSize", String(options.pageSize));
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/fraud${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceFraudPageSchema.parse(data);
}

export async function fetchInsuranceFraudDetail(
  fraudId: string,
  options?: OrganizationRequestOptions,
): Promise<InsuranceFraudDetail> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/fraud/${encodeURIComponent(fraudId)}${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceFraudDetailSchema.parse(data);
}

export async function postInsuranceFraudComment(
  fraudId: string,
  payload: InsuranceRecordCommentInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsComment(
    `/insurance-ops/fraud/${encodeURIComponent(fraudId)}/comments`,
    payload,
    options,
  );
}

export async function postInsuranceFraudAction(
  fraudId: string,
  payload: InsuranceRecordActionInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsAction(
    `/insurance-ops/fraud/${encodeURIComponent(fraudId)}/actions`,
    payload,
    options,
  );
}

export async function fetchInsuranceComplianceRecords(
  options?: OrganizationRequestOptions & {
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  },
): Promise<InsuranceCompliancePage> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  if (options?.search) search.set("search", options.search);
  if (options?.status) search.set("status", options.status);
  if (options?.sortBy) search.set("sortBy", options.sortBy);
  if (options?.sortOrder) search.set("sortOrder", options.sortOrder);
  if (typeof options?.page === "number") search.set("page", String(options.page));
  if (typeof options?.pageSize === "number")
    search.set("pageSize", String(options.pageSize));
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/compliance${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceCompliancePageSchema.parse(data);
}

export async function fetchInsuranceComplianceDetail(
  recordId: string,
  options?: OrganizationRequestOptions,
): Promise<InsuranceComplianceDetail> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/compliance/${encodeURIComponent(recordId)}${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceComplianceDetailSchema.parse(data);
}

export async function postInsuranceComplianceComment(
  recordId: string,
  payload: InsuranceRecordCommentInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsComment(
    `/insurance-ops/compliance/${encodeURIComponent(recordId)}/comments`,
    payload,
    options,
  );
}

export async function postInsuranceComplianceAction(
  recordId: string,
  payload: InsuranceRecordActionInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsAction(
    `/insurance-ops/compliance/${encodeURIComponent(recordId)}/actions`,
    payload,
    options,
  );
}

export async function fetchInsuranceUnderwritingApplications(
  options?: OrganizationRequestOptions & {
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  },
): Promise<InsuranceUnderwritingPage> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  if (options?.search) search.set("search", options.search);
  if (options?.status) search.set("status", options.status);
  if (options?.sortBy) search.set("sortBy", options.sortBy);
  if (options?.sortOrder) search.set("sortOrder", options.sortOrder);
  if (typeof options?.page === "number") search.set("page", String(options.page));
  if (typeof options?.pageSize === "number")
    search.set("pageSize", String(options.pageSize));
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/underwriting${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceUnderwritingPageSchema.parse(data);
}

export async function fetchInsuranceUnderwritingDetail(
  applicationId: string,
  options?: OrganizationRequestOptions,
): Promise<InsuranceUnderwritingDetail> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/underwriting/${encodeURIComponent(applicationId)}${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceUnderwritingDetailSchema.parse(data);
}

export async function postInsuranceUnderwritingComment(
  applicationId: string,
  payload: InsuranceRecordCommentInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsComment(
    `/insurance-ops/underwriting/${encodeURIComponent(applicationId)}/comments`,
    payload,
    options,
  );
}

export async function postInsuranceUnderwritingAction(
  applicationId: string,
  payload: InsuranceRecordActionInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsAction(
    `/insurance-ops/underwriting/${encodeURIComponent(applicationId)}/actions`,
    payload,
    options,
  );
}

export async function fetchInsuranceRiskPortfolio(
  options?: OrganizationRequestOptions & {
    search?: string;
    band?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  },
): Promise<InsuranceRiskPortfolioPage> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  if (options?.search) search.set("search", options.search);
  if (options?.band) search.set("band", options.band);
  if (options?.sortBy) search.set("sortBy", options.sortBy);
  if (options?.sortOrder) search.set("sortOrder", options.sortOrder);
  if (typeof options?.page === "number") search.set("page", String(options.page));
  if (typeof options?.pageSize === "number")
    search.set("pageSize", String(options.pageSize));
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/risk-portfolio${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceRiskPortfolioPageSchema.parse(data);
}

export async function fetchInsuranceRiskPortfolioDetail(
  regionId: string,
  options?: OrganizationRequestOptions & {
    searchName?: string;
    vehicleQuery?: string;
    minScore?: number;
    maxScore?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  },
): Promise<InsuranceRiskPortfolioDetail> {
  const { search, orgId } = buildInsuranceOpsScopedParams(options);
  if (options?.searchName) search.set("searchName", options.searchName);
  if (options?.vehicleQuery) search.set("vehicleQuery", options.vehicleQuery);
  if (typeof options?.minScore === "number")
    search.set("minScore", String(options.minScore));
  if (typeof options?.maxScore === "number")
    search.set("maxScore", String(options.maxScore));
  if (options?.sortBy) search.set("sortBy", options.sortBy);
  if (options?.sortOrder) search.set("sortOrder", options.sortOrder);
  if (typeof options?.page === "number") search.set("page", String(options.page));
  if (typeof options?.pageSize === "number")
    search.set("pageSize", String(options.pageSize));
  const query = search.toString();
  const data = await request<unknown>(
    `/insurance-ops/risk-portfolio/${encodeURIComponent(regionId)}${query ? `?${query}` : ""}`,
    {
      headers: tenantScopedHeaders(orgId),
    } as RequestInit,
  );
  return insuranceRiskPortfolioDetailSchema.parse(data);
}

export async function postInsuranceRiskPortfolioComment(
  regionId: string,
  payload: InsuranceRecordCommentInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsComment(
    `/insurance-ops/risk-portfolio/${encodeURIComponent(regionId)}/comments`,
    payload,
    options,
  );
}

export async function postInsuranceRiskPortfolioAction(
  regionId: string,
  payload: InsuranceRecordActionInput,
  options?: OrganizationRequestOptions,
): Promise<InsuranceMutationAck> {
  return postInsuranceOpsAction(
    `/insurance-ops/risk-portfolio/${encodeURIComponent(regionId)}/actions`,
    payload,
    options,
  );
}

type UbiPoliciesQuery = {
  driverId?: string;
  vehicleId?: string;
  tenantId?: string;
};

export async function fetchUbiPolicies(
  filters: UbiPoliciesQuery = {},
): Promise<UbiPolicy[]> {
  const params = new URLSearchParams();
  if (filters.driverId) params.set("driverId", filters.driverId);
  if (filters.vehicleId) params.set("vehicleId", filters.vehicleId);
  if (filters.tenantId) {
    params.set("tenantId", filters.tenantId);
  }
  const queryString = params.toString();
  try {
    const data = await request<unknown[]>(
      `/ubi/policies${queryString ? `?${queryString}` : ""}`,
    );
    return data.map((entry) => ubiPolicySchema.parse(entry));
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "UBI policies endpoint unavailable; returning empty policy list.",
      );
      return [];
    }
    throw error;
  }
}

export async function fetchUbiPartners(
  tenantId?: string,
): Promise<InsurerPartner[]> {
  const search = new URLSearchParams();
  if (tenantId) {
    search.set("tenantId", tenantId);
  }
  const query = search.toString();
  try {
    const data = await request<unknown[]>(
      `/ubi/partners${query ? `?${query}` : ""}`,
    );
    return data.map((entry) => insurerPartnerSchema.parse(entry));
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "UBI partners endpoint unavailable; returning empty partner list.",
      );
      return [];
    }
    throw error;
  }
}

export async function createUbiPolicy(payload: UbiPolicy): Promise<UbiPolicy> {
  const data = await request<unknown>("/ubi/policies", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return ubiPolicySchema.parse(data);
}

export async function fetchWalletAccounts(
  tenantId?: string,
): Promise<WalletAccount[]> {
  const search = new URLSearchParams();
  if (tenantId) {
    search.set("tenantId", tenantId);
  }
  const query = search.toString();
  try {
    const data = await request<unknown[]>(
      `/wallet/accounts${query ? `?${query}` : ""}`,
    );
    return data.map((entry) => walletAccountSchema.parse(entry));
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Wallet accounts endpoint unavailable; returning empty accounts list.",
      );
      return [];
    }
    throw error;
  }
}

export async function fetchWalletLedger(
  accountId: string,
): Promise<WalletLedgerEntry[]> {
  const data = await request<unknown[]>(`/wallet/accounts/${accountId}/ledger`);
  return data.map((entry) => walletLedgerEntrySchema.parse(entry));
}

const walletVehicleExpenseSchema = z.array(driverExpenseSchema);
const walletVehicleEarningSchema = z.array(driverEarningSchema);

const walletGatewayResultSchema = z
  .object({
    success: z.boolean(),
    transactionId: z.string().optional(),
    transactionRef: z.string().optional(),
    reference: z.string().optional(),
    redirectUrl: z.string().optional(),
    formFields: z.record(z.string(), z.string()).optional(),
    status: z.string().optional(),
    error: z.string().optional(),
  })
  .passthrough();

export async function topUpOrganizationWallet(params: {
  gateway: string;
  amount: number;
  currency?: string;
  returnUrl?: string;
  cancelUrl?: string;
}): Promise<{
  success: boolean;
  transactionRef?: string;
  redirectUrl?: string;
  formFields?: Record<string, string>;
  status?: string;
  error?: string;
}> {
  const data = await request<unknown>("/wallet/topup", {
    method: "POST",
    body: JSON.stringify(params),
  });
  const parsed = walletGatewayResultSchema.parse(data);
  return {
    success: parsed.success,
    transactionRef:
      parsed.transactionRef ?? parsed.transactionId ?? parsed.reference,
    redirectUrl: parsed.redirectUrl,
    formFields: parsed.formFields,
    status: parsed.status,
    error: parsed.error,
  };
}

export async function payInvoiceWithWallet(params: {
  invoiceId: string;
  amount?: number;
}): Promise<{
  success: boolean;
  invoiceId: string;
  status?: string;
  amount?: number;
  currency?: string;
  transactionRef?: string;
  walletId?: string;
  newBalance?: number;
  message?: string;
}> {
  const data = await request<unknown>("/wallet/pay-invoice", {
    method: "POST",
    body: JSON.stringify(params),
  });
  return data as any;
}

export async function updateMapSettings(
  payload: MapSettings & { updatedBy: string },
  options?: OrganizationRequestOptions,
) {
  return request(`/settings/maps${buildOrganizationScopeQuery(options)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchMapSettings(options?: {
  organizationId?: string;
  tenantId?: string;
}): Promise<MapSettings> {
  const orgId = options?.organizationId ?? options?.tenantId;
  const query = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : "";
  const data = await request<unknown>(`/settings/maps${query}`);
  const payload = (data as { data?: unknown }).data ?? data;
  const sanitizedPayload =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (() => {
        const next = { ...(payload as Partial<MapSettings>) };
        if (
          typeof next.apiKey === "string" &&
          next.apiKey.trim().length === 0
        ) {
          delete next.apiKey;
        }
        if (
          typeof next.mapId === "string" &&
          next.mapId.trim().length === 0
        ) {
          delete next.mapId;
        }
        return next;
      })()
      : payload;

  log.map.debug("Map Settings API Response:", sanitizedPayload);

  const result = mapSettingsSchema.safeParse(sanitizedPayload ?? {});

  if (!result.success) {
    log.map.warn("Map settings validation failed:", result.error);
    // Forward-compatible fallback:
    // If backend introduces additional keys before this client updates its
    // schema bundle, keep known values instead of resetting everything.
    if (
      sanitizedPayload &&
      typeof sanitizedPayload === "object" &&
      !Array.isArray(sanitizedPayload)
    ) {
      return mergeMapSettings(sanitizedPayload as Partial<MapSettings>);
    }
    return mergeMapSettings({});
  }

  return mergeMapSettings(result.data);
}

export async function updateVideoSettings(
  payload: VideoSettingsV1 & { updatedBy: string },
  options?: OrganizationRequestOptions,
) {
  return request(`/settings/video${buildOrganizationScopeQuery(options)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchVideoSettings(
  options?: OrganizationRequestOptions,
): Promise<VideoSettingsV1> {
  const data = await request<unknown>(
    `/settings/video${buildOrganizationScopeQuery(options)}`,
  );
  const payload = (data as { data?: unknown }).data ?? data;
  const result = videoSettingsV1Schema.safeParse(payload);
  if (!result.success) {
    throw new Error(`Invalid video settings payload: ${result.error.message}`);
  }
  return result.data;
}

export async function updateRoutingSettings(
  payload: RoutingSettings & { updatedBy: string },
  options?: OrganizationRequestOptions,
) {
  return request(`/settings/routing${buildOrganizationScopeQuery(options)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchRoutingSettings(options?: {
  organizationId?: string;
  tenantId?: string;
}): Promise<RoutingSettings> {
  const orgId = options?.organizationId ?? options?.tenantId;
  const query = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : "";
  const data = await request<unknown>(`/settings/routing${query}`);
  const payload = (data as { data?: unknown }).data ?? data;

  // Use safeParse instead of parse to handle validation errors gracefully
  const result = routingSettingsSchema.safeParse(payload);

  if (!result.success) {
    log.api.warn("Routing settings validation failed:", result.error);
    // Return empty routing settings if validation fails
    return {
      trafficModel: "best_guess",
      defaultDepartureOffsetMinutes: 0,
      avoidTolls: false,
      avoidHighways: false,
      avoidFerries: false,
      useRoutesApi: false,
      routingPreference: "TRAFFIC_AWARE_OPTIMAL",
      assignBy: "tenant",
      assignedVehicles: [],
      assignedUsers: [],
      mapMatchingEnabled: true,
      interpolation: true,
      favouritePois: [],
    };
  }

  return result.data;
}

export async function fetchMapDirections(params: {
  origin: string;
  destination: string;
  mode?: "driving" | "walking" | "bicycling" | "transit";
  departureTime?: string;
  arrivalTime?: string;
  avoid?: string;
  trafficModel?: "best_guess" | "pessimistic" | "optimistic";
}): Promise<{
  routes: Array<{
    summary?: string;
    legs?: Array<{
      distance?: { text?: string; value?: number };
      duration?: { text?: string; value?: number };
      duration_in_traffic?: { text?: string; value?: number };
      start_address?: string;
      end_address?: string;
      start_location?: { lat: number; lng: number };
      end_location?: { lat: number; lng: number };
    }>;
    overview_polyline?: { points?: string };
    warnings?: string[];
    waypoint_order?: number[];
  }>;
}> {
  const search = new URLSearchParams({
    origin: params.origin,
    destination: params.destination,
  });
  if (params.mode) search.set("mode", params.mode);
  if (params.departureTime) search.set("departure_time", params.departureTime);
  if (params.arrivalTime) search.set("arrival_time", params.arrivalTime);
  if (params.avoid) search.set("avoid", params.avoid);
  if (params.trafficModel) search.set("traffic_model", params.trafficModel);
  if (directionsApiDisabled) {
    throw new Error(
      "Directions API disabled due unauthorized key restrictions.",
    );
  }
  try {
    return await request(`/maps/directions?${search.toString()}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes("Request failed (401)") ||
      message.includes("Request failed (403)") ||
      message.includes("referer")
    ) {
      directionsApiDisabled = true;
      warnMapApiOnce(
        "directions:unauthorized",
        "[Directions] Disabled: missing/unauthorized Maps API key or referrer restriction.",
      );
    }
    warnMapApiOnce(
      `directions:${message}`,
      "[Directions] Unable to fetch road route.",
      message,
    );
    throw error;
  }
}

const getOsrmBase = () => {
  const configured = process.env.NEXT_PUBLIC_OSRM_BASE_URL?.trim();
  if (configured) {
    return configured.replace(/\/+$/, "");
  }
  return "https://router.project-osrm.org";
};

export async function fetchOsrmRoute(params: {
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
}): Promise<Array<{ lat: number; lng: number }>> {
  const base = getOsrmBase();
  const url = new URL(
    `/route/v1/driving/${params.origin.lng},${params.origin.lat};${params.destination.lng},${params.destination.lat}`,
    base,
  );
  url.searchParams.set("overview", "full");
  url.searchParams.set("geometries", "geojson");
  url.searchParams.set("alternatives", "false");
  url.searchParams.set("steps", "true");

  const response = await fetch(url.toString(), {
    method: "GET",
    cache: "no-store",
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "Unknown error");
    throw new Error(
      `OSRM route request failed (${response.status}): ${detail}`,
    );
  }

  const payload = (await response.json()) as {
    code?: string;
    routes?: Array<{
      geometry?: {
        coordinates?: Array<[number, number]>;
      };
    }>;
  };

  const coordinates = payload.routes?.[0]?.geometry?.coordinates ?? [];
  return coordinates
    .filter(
      (coord): coord is [number, number] =>
        Array.isArray(coord) &&
        coord.length >= 2 &&
        Number.isFinite(coord[0]) &&
        Number.isFinite(coord[1]),
    )
    .map(([lng, lat]) => ({ lat, lng }));
}

export async function fetchDistanceMatrix(params: {
  origins: string[];
  destinations: string[];
  mode?: "driving" | "walking" | "bicycling" | "transit";
  departureTime?: string;
  trafficModel?: "best_guess" | "pessimistic" | "optimistic";
}): Promise<{
  rows: Array<{
    elements: Array<{
      status?: string;
      distance?: { text?: string; value?: number };
      duration?: { text?: string; value?: number };
      duration_in_traffic?: { text?: string; value?: number };
    }>;
  }>;
}> {
  const search = new URLSearchParams({
    origins: params.origins.join("|"),
    destinations: params.destinations.join("|"),
  });
  if (params.mode) search.set("mode", params.mode);
  if (params.departureTime) search.set("departure_time", params.departureTime);
  if (params.trafficModel) search.set("traffic_model", params.trafficModel);
  return request(`/maps/distance-matrix?${search.toString()}`);
}

export async function geocodeAddress(
  input: string,
): Promise<{
  results: Array<{
    formatted_address?: string;
    geometry?: { location?: { lat: number; lng: number } };
  }>;
}> {
  const search = new URLSearchParams({ address: input });
  return request(`/maps/geocode?${search.toString()}`);
}

export async function reverseGeocodeLocation(
  lat: number,
  lng: number,
): Promise<{
  results: Array<{ formatted_address?: string; place_id?: string }>;
}> {
  const search = new URLSearchParams({ latlng: `${lat},${lng}` });
  return request(`/maps/reverse-geocode?${search.toString()}`);
}

let roadsSnapDisabled = false;
let roadsSnapDisabledUntil = 0;
let directionsApiDisabled = false;
let routesApiDisabled = false;

export async function snapPointsToRoads(
  path: Array<{ lat: number; lng: number }>,
  interpolate = true,
): Promise<{
  snappedPoints: Array<{
    location: { latitude: number; longitude: number };
    originalIndex?: number;
  }>;
}> {
  if (!path.length) {
    return { snappedPoints: [] };
  }
  if (roadsSnapDisabled && Date.now() > roadsSnapDisabledUntil) {
    roadsSnapDisabled = false;
  }
  if (roadsSnapDisabled) {
    return { snappedPoints: [] };
  }
  const chunkSize = 100;
  const overlap = 1;

  const snapChunk = async (points: Array<{ lat: number; lng: number }>) => {
    const pathParam = points
      .map((point) => `${point.lat},${point.lng}`)
      .join("|");
    const search = new URLSearchParams({
      path: pathParam,
      interpolate: interpolate ? "true" : "false",
    });
    return request<{
      snappedPoints: Array<{
        location: { latitude: number; longitude: number };
        originalIndex?: number;
      }>;
    }>(`/maps/roads/snap?${search.toString()}`);
  };

  try {
    if (path.length <= chunkSize) {
      return await snapChunk(path);
    }

    const merged: Array<{
      location: { latitude: number; longitude: number };
      originalIndex?: number;
    }> = [];
    for (let i = 0; i < path.length; i += chunkSize - overlap) {
      const chunk = path.slice(i, i + chunkSize);
      if (!chunk.length) break;
      const { snappedPoints } = await snapChunk(chunk);
      if (i > 0 && snappedPoints.length) {
        merged.push(...snappedPoints.slice(1));
      } else {
        merged.push(...snappedPoints);
      }
    }
    return { snappedPoints: merged };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes("Request failed (401)") ||
      message.includes("Request failed (403)")
    ) {
      roadsSnapDisabled = true;
      roadsSnapDisabledUntil = Date.now() + 10 * 60 * 1000;
      warnMapApiOnce(
        "roads:unauthorized",
        "[RoadSnap] Disabled: missing/unauthorized Maps API key.",
      );
      return { snappedPoints: [] };
    }
    warnMapApiOnce(
      `roads:${message}`,
      "[RoadSnap] Failed to snap points to roads.",
      message,
    );
    throw error;
  }
}

export async function findNearestRoads(
  path: Array<{ lat: number; lng: number }>,
): Promise<{
  snappedPoints: Array<{
    location: { latitude: number; longitude: number };
    originalIndex?: number;
  }>;
}> {
  const capped = path.slice(0, 100);
  if (!capped.length) {
    return { snappedPoints: [] };
  }
  const pathParam = capped
    .map((point) => `${point.lat},${point.lng}`)
    .join("|");
  const search = new URLSearchParams({ path: pathParam });
  return request(`/maps/roads/nearest?${search.toString()}`);
}

export interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
}

export async function autocompletePlace(
  input: string,
  options?: {
    sessionToken?: string;
    types?: string;
    location?: string;
    radius?: string;
  },
): Promise<{ predictions: PlacePrediction[] }> {
  const search = new URLSearchParams({ input });
  if (options?.sessionToken) search.set("sessiontoken", options.sessionToken);
  if (options?.types) search.set("types", options.types);
  if (options?.location) search.set("location", options.location);
  if (options?.radius) search.set("radius", options.radius);
  return request(`/maps/places/autocomplete?${search.toString()}`);
}

export async function fetchPlacesNearby(params: {
  location: string;
  radius: string;
  keyword?: string;
  type?: string;
  sessionToken?: string;
}): Promise<{ results: unknown[] }> {
  const search = new URLSearchParams({
    location: params.location,
    radius: params.radius,
  });
  if (params.keyword) search.set("keyword", params.keyword);
  if (params.type) search.set("type", params.type);
  if (params.sessionToken) search.set("sessiontoken", params.sessionToken);
  return request(`/maps/places/nearby?${search.toString()}`);
}

export async function fetchPlacesTextSearch(params: {
  query: string;
  location?: string;
  radius?: string;
  type?: string;
  sessionToken?: string;
}): Promise<{ results: unknown[] }> {
  const search = new URLSearchParams({ query: params.query });
  if (params.location) search.set("location", params.location);
  if (params.radius) search.set("radius", params.radius);
  if (params.type) search.set("type", params.type);
  if (params.sessionToken) search.set("sessiontoken", params.sessionToken);
  return request(`/maps/places/search?${search.toString()}`);
}

export async function fetchPlaceDetails(
  placeId: string,
): Promise<{
  result?: {
    formatted_address?: string;
    geometry?: { location?: { lat: number; lng: number } };
  };
}> {
  const search = new URLSearchParams({ place_id: placeId });
  return request(`/maps/places/details?${search.toString()}`);
}

export async function updateWalletSettings(
  payload: Partial<WalletSettings> & { updatedBy: string },
  options?: OrganizationRequestOptions,
) {
  return request(`/settings/wallet${buildOrganizationScopeQuery(options)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function updateUbiSettings(
  payload: Partial<UbiSettings> & { updatedBy: string },
  options?: OrganizationRequestOptions,
) {
  return request(`/settings/ubi${buildOrganizationScopeQuery(options)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchTenants(params?: { search?: string }) {
  const query = params?.search
    ? `?search=${encodeURIComponent(params.search)}`
    : "";
  const data = await request<unknown[]>(`/user-directory/tenants${query}`);
  return data
    .map((entry) => {
      const result = tenantDirectoryEntrySchema.safeParse(entry);
      if (!result.success) {
        log.api.warn("Tenant entry validation failed:", result.error);
        return null;
      }
      return result.data;
    })
    .filter(Boolean);
}

export async function fetchOrganizations(params: {
  tenantId: string;
  search?: string;
}) {
  const searchParams = new URLSearchParams({ tenantId: params.tenantId });
  if (params.search) searchParams.set("search", params.search);
  const data = await request<unknown[]>(
    `/user-directory/organizations?${searchParams.toString()}`,
  );
  return data
    .map((entry) => {
      const result = organizationDirectoryEntrySchema.safeParse(entry);
      if (!result.success) {
        log.api.warn("Organization entry validation failed:", result.error);
        return null;
      }
      return result.data;
    })
    .filter(Boolean);
}

export async function fetchOrganizationUnits(params: {
  organizationId: string;
}) {
  const searchParams = new URLSearchParams({
    organizationId: params.organizationId,
  });
  const data = await request<unknown[]>(
    `/user-directory/organization-units?${searchParams.toString()}`,
  );
  return data
    .map((entry) => {
      const result = organizationUnitSchema.safeParse(entry);
      if (!result.success) {
        log.api.warn("Organization unit validation failed:", result.error);
        return null;
      }
      return result.data;
    })
    .filter(Boolean);
}

export async function fetchUserDirectory(params: {
  tenantId: string;
  scope?: "tenant" | "organization" | "unit";
  organizationId?: string;
  organizationUnitId?: string;
  search?: string;
  cursor?: string;
  statuses?: string[];
}) {
  const searchParams = new URLSearchParams({ tenantId: params.tenantId });
  if (params.scope) searchParams.set("scope", params.scope);
  if (params.organizationId)
    searchParams.set("organizationId", params.organizationId);
  if (params.organizationUnitId)
    searchParams.set("organizationUnitId", params.organizationUnitId);
  if (params.search) searchParams.set("search", params.search);
  if (params.cursor) searchParams.set("cursor", params.cursor);
  if (params.statuses?.length)
    searchParams.set("statuses", params.statuses.join(","));
  const data = await request<unknown>(
    `/user-directory/users?${searchParams.toString()}`,
  );
  return userDirectoryPageSchema.parse(data);
}

export async function fetchUserProfile(userId: string) {
  const data = await request<unknown>(`/user-directory/users/${userId}`);
  return userProfileSummarySchema.parse(data);
}

export async function fetchAccessPolicies(params: { tenantId: string }) {
  const searchParams = new URLSearchParams({ tenantId: params.tenantId });
  const data = await request<unknown[]>(
    `/user-directory/access-policies?${searchParams.toString()}`,
  );
  return data.map((entry) => accessPolicySummarySchema.parse(entry));
}

// User Management APIs
export async function fetchUsers(params?: {
  organizationId?: string;
  status?: string;
  search?: string;
  scopeMode?: "selected" | "all";
  includeAllMembershipStates?: boolean;
}) {
  const searchParams = new URLSearchParams();
  if (params?.organizationId)
    searchParams.set("organizationId", params.organizationId);
  if (params?.status) searchParams.set("status", params.status);
  if (params?.search) searchParams.set("search", params.search);
  if (params?.scopeMode) searchParams.set("scopeMode", params.scopeMode);
  if (params?.includeAllMembershipStates)
    searchParams.set("includeAll", "true");
  const query = searchParams.toString();
  try {
    const data = await request<unknown>(`/users${query ? `?${query}` : ""}`);
    return data;
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn("Users endpoint unavailable; returning empty user list.");
      return [];
    }
    throw error;
  }
}

export async function fetchUsersWithSearch(
  search?: string,
  tenantId?: string,
): Promise<UserSearchResult[]> {
  const searchParams = new URLSearchParams();
  if (search) searchParams.set("search", search);
  if (tenantId) searchParams.set("organizationId", tenantId);

  const query = searchParams.toString();
  const response = await request<unknown>(`/users${query ? `?${query}` : ""}`);
  const parsed = userSearchResponseSchema.safeParse(response);
  if (!parsed.success) {
    log.api.warn("Failed to parse user search response", parsed.error);
    return [];
  }

  return parsed.data.data.map((user) => {
    const name =
      user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : (user.email ?? user.id);

    return {
      id: user.id,
      name,
      email: user.email ?? "",
      avatar: user.avatarUrl ?? null,
      status: user.status ?? null,
      persona: user.persona ?? null,
      tenantId: user.tenantId ?? user.organizationId ?? null,
      organizationId: user.organizationId ?? null,
    } satisfies UserSearchResult;
  });
}

export async function fetchUser(userId: string) {
  const data = await request<unknown>(`/users/${userId}`);
  return data;
}

export async function createUser(payload: {
  email: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  persona?: string;
  organizationId?: string;
  roleId?: string;
  parentUserId?: string;
}) {
  const data = await request<unknown>("/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data;
}

export async function updateUser(
  userId: string,
  payload: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    avatarUrl?: string;
    status?: string;
    locale?: string;
    timeZone?: string;
  },
) {
  const data = await request<unknown>(`/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data;
}

export async function adminSetUserPassword(userId: string, password: string) {
  return request(`/users/${userId}/password`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function adminSendUserPasswordReset(userId: string) {
  return request(`/users/${userId}/password-reset`, {
    method: "POST",
  });
}

export async function deleteUser(userId: string) {
  await request<void>(`/users/${userId}`, {
    method: "DELETE",
  });
}

export async function fetchUserMemberships(userId: string) {
  const data = await request<unknown>(`/users/${userId}/memberships`);
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;

    if (Array.isArray(record.data)) {
      return record.data;
    }

    if (Array.isArray(record.organizationMemberships)) {
      return record.organizationMemberships.map((entry) => {
        const source = (
          entry && typeof entry === "object"
            ? (entry as Record<string, unknown>)
            : {}
        ) as Record<string, unknown>;
        const org =
          (source.organization as Record<string, unknown> | undefined) ??
          (source.Organization as Record<string, unknown> | undefined);
        const role =
          (source.role as Record<string, unknown> | undefined) ??
          (source.Role as Record<string, unknown> | undefined);

        return {
          ...source,
          organization: org
            ? {
              ...org,
              name:
                typeof org.name === "string" && org.name.trim().length > 0
                  ? org.name
                  : "Unknown organization",
            }
            : undefined,
          role: role
            ? {
              ...role,
              name:
                typeof role.name === "string" && role.name.trim().length > 0
                  ? role.name
                  : "Member",
            }
            : undefined,
        };
      });
    }
  }

  return [];
}

export async function addUserMembership(
  userId: string,
  payload: {
    organizationId: string;
    roleId?: string;
    title?: string;
  },
) {
  const data = await request<unknown>(`/users/${userId}/memberships`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data;
}

export async function removeUserMembership(
  userId: string,
  membershipId: string,
) {
  await request<void>(`/users/${userId}/memberships/${membershipId}`, {
    method: "DELETE",
  });
}

export async function fetchUserPermissions(userId: string) {
  const data = await request<unknown>(`/users/${userId}/permissions`);
  return data;
}

export async function updateUserRole(userId: string, roleId: string) {
  const data = await request<unknown>(`/users/${userId}/role`, {
    method: "PATCH",
    body: JSON.stringify({ roleId }),
  });
  return data;
}

// Permission Management APIs
export async function updateUserPermissions(
  userId: string,
  permissions: string[],
) {
  const data = await request<unknown>(`/users/${userId}/permissions`, {
    method: "PATCH",
    body: JSON.stringify({ permissions }),
  });
  return data;
}

export async function grantUserPermission(userId: string, permission: string) {
  const data = await request<unknown>(`/users/${userId}/permissions/grant`, {
    method: "POST",
    body: JSON.stringify({ permission }),
  });
  return data;
}

export async function revokeUserPermission(userId: string, permission: string) {
  const data = await request<unknown>(`/users/${userId}/permissions/revoke`, {
    method: "POST",
    body: JSON.stringify({ permission }),
  });
  return data;
}

export async function fetchRoles() {
  try {
    const data = await request<unknown[]>("/roles");
    return data;
  } catch (err) {
    log.api.debug("/roles not available; using default roles");
    return [
      { id: "SUPER_ADMIN", key: "SUPER_ADMIN", name: "Super Admin" },
      { id: "ORG_ADMIN", key: "ORG_ADMIN", name: "Org Admin" },
      { id: "FARM_OWNER", key: "FARM_OWNER", name: "Farm Owner" },
      { id: "FARM_MANAGER", key: "FARM_MANAGER", name: "Farm Manager" },
      { id: "FARM_WORKER", key: "FARM_WORKER", name: "Farm Worker" },
      { id: "FARM_VIEWER", key: "FARM_VIEWER", name: "Farm Viewer" },
      { id: "FLEET_OWNER", key: "FLEET_OWNER", name: "Fleet Owner" },
      { id: "FLEET_MANAGER", key: "FLEET_MANAGER", name: "Fleet Manager" },
      { id: "DISPATCHER", key: "DISPATCHER", name: "Dispatcher" },
      { id: "DRIVER", key: "DRIVER", name: "Driver" },
      { id: "ANALYST", key: "ANALYST", name: "Analyst" },
      { id: "END_USER", key: "END_USER", name: "End User" },
      { id: "SUPPORT", key: "SUPPORT", name: "Support" },
      {
        id: "SERVICE_PROVIDER",
        key: "SERVICE_PROVIDER",
        name: "Service Provider",
      },
    ];
  }
}

export async function fetchAllPermissions() {
  try {
    const data = await request<unknown[]>("/permissions");
    return data;
  } catch (err) {
    log.api.debug(
      "/permissions not available; returning empty permission list",
    );
    return [] as unknown[];
  }
}

export async function fetchUserBilling(userId: string) {
  const data = await request<unknown>(`/users/${userId}/billing`);
  return data;
}

export async function updateUserBilling(
  userId: string,
  payload: {
    billingAccountId?: string;
    paymentMethodId?: string;
    invoicingEmail?: string;
    limits?: Record<string, unknown>;
  },
) {
  const data = await request<unknown>(`/users/${userId}/billing`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data;
}

export async function fetchUserWallet(userId: string) {
  const data = await request<unknown>(`/users/${userId}/wallet`);
  return data;
}

export async function topUpUserWallet(userId: string, amount: number) {
  const data = await request<unknown>(`/users/${userId}/wallet/topup`, {
    method: "POST",
    body: JSON.stringify({ amount }),
  });
  return data;
}

// Two-Factor Authentication (MFA) APIs
export async function setupMfa() {
  const data = await request<{
    secret: string;
    qrCode: string;
    backupCodes: string[];
  }>("/auth/mfa/setup", {
    method: "POST",
  });
  return data;
}

export async function verifyMfaSetup(token: string) {
  const data = await request<{ success: boolean }>("/auth/mfa/verify-setup", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
  return data;
}

export async function getMfaStatus() {
  const data = await request<{ enabled: boolean }>("/auth/mfa/status");
  return data;
}

export async function disableMfa() {
  const data = await request<{ success: boolean }>("/auth/mfa/disable", {
    method: "POST",
  });
  return data;
}

export async function fetchUserVehicles(userId: string) {
  const data = await request<unknown>(`/linking/users/${userId}/vehicles`);
  return data;
}

export async function linkUserVehicle(
  userId: string,
  vehicleId: string,
  permissions?: string[],
) {
  const data = await request<unknown>(
    `/linking/vehicles/${vehicleId}/users/${userId}`,
    {
      method: "POST",
      body: JSON.stringify({ permissions }),
    },
  );
  return data;
}

export async function unlinkUserVehicle(userId: string, vehicleId: string) {
  await request<void>(`/linking/vehicles/${vehicleId}/users/${userId}`, {
    method: "DELETE",
  });
}

export async function fetchUserTickets(userId: string) {
  const data = await request<unknown>(`/users/${userId}/tickets`);
  return data;
}

export async function fetchUserSessions(userId: string) {
  const data = await request<unknown>(`/users/${userId}/sessions`);
  return data;
}

export async function revokeUserSession(userId: string, sessionId: string) {
  await request<void>(`/users/${userId}/sessions/${sessionId}`, {
    method: "DELETE",
  });
}

const provisioningLogSchema = z.object({
  key: z.string(),
  status: z.enum(["success", "failed", "info"]).optional(),
  message: z.string().optional(),
  timestamp: z.string(),
  meta: z.record(z.string(), z.unknown()).optional(),
});

const tenantProvisioningJobSchema = z.object({
  id: z.string(),
  status: z.string(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  steps: z.array(provisioningLogSchema).nullable().optional(),
  organizationId: z.string().optional(),
  error: z.unknown().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

const tenantDomainSchema = z.object({
  id: z.string(),
  hostname: z.string(),
  isPrimary: z.boolean(),
  status: z.string(),
  verification: z.unknown().nullable().optional(),
  verifiedAt: z.string().nullable().optional(),
  organizationId: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const tenantSettingsSchema = z
  .object({
    theme: z.record(z.string(), z.unknown()).nullable().optional(),
    map: z
      .object({
        provider: z.string().optional(),
        apiKey: z.string().nullable().optional(),
        mapId: z.string().nullable().optional(),
        defaultCenter: z
          .object({
            lat: z.number(),
            lng: z.number(),
          })
          .optional(),
        defaultZoom: z.number().optional(),
        features: z.record(z.string(), z.unknown()).optional(),
      })
      .nullable()
      .optional(),
    ai: z
      .object({
        provider: z.string().optional(),
        baseUrl: z.string().nullable().optional(),
        apiKey: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    alerts: z.record(z.string(), z.unknown()).nullable().optional(),
    devices: z.record(z.string(), z.unknown()).nullable().optional(),
    cameras: z.record(z.string(), z.unknown()).nullable().optional(),
    telemetry: z.record(z.string(), z.unknown()).nullable().optional(),
    retention: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .nullable();

const rawOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  legalName: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  industry: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  contactEmail: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  status: z.string(),
  plan: z.string().nullable().optional(),
  enabledServices: z.record(z.string(), z.boolean()).optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  billingAccountId: z.string().nullable().optional(),
  subscriptionId: z.string().optional(),
  invoiceId: z.string().optional(),
  BillingAccount: z
    .object({
      id: z.string(),
    })
    .nullable()
    .optional(),
  provisioningJobId: z.string().optional(),
  provisioningStatus: z.string().optional(),
  provisioningWarning: z.boolean().optional(),
  provisioningErrorDetails: z
    .array(
      z.object({
        key: z.string(),
        message: z.string(),
      }),
    )
    .optional(),
  commercialWarnings: z.array(z.string()).optional(),
  OrganizationMembership: z.array(z.unknown()).nullable().optional(),
  OrganizationSettings: z.unknown().nullable().optional(),
  TenantSettings: tenantSettingsSchema.nullable().optional(),
  TenantDomains: z.array(tenantDomainSchema).nullable().optional(),
  TenantProvisioningJobs: z
    .array(tenantProvisioningJobSchema)
    .nullable()
    .optional(),
  Fleet: z.array(z.unknown()).nullable().optional(),
  Vehicle: z.array(z.unknown()).nullable().optional(),
});

// Derive enabledServices from metadata if it's not at the top level
const organizationSchema = rawOrganizationSchema.transform((org) => {
  if (!org.enabledServices && org.metadata) {
    const meta = org.metadata as Record<string, unknown>;
    if (meta.enabledServices && typeof meta.enabledServices === "object") {
      return {
        ...org,
        enabledServices: meta.enabledServices as Record<string, boolean>,
      };
    }
  }
  return org;
});

const organizationsListSchema = z.union([
  z.object({
    items: z.array(rawOrganizationSchema),
    total: z.number().optional(),
    offset: z.number().optional(),
    limit: z.number().optional(),
  }),
  z.array(rawOrganizationSchema),
]);

const notificationCountSchema = z.object({ count: z.number() });

export type TenantProvisioningJob = z.infer<typeof tenantProvisioningJobSchema>;
export type TenantSettingsResponse = z.infer<typeof tenantSettingsSchema>;
export type OrganizationResponse = z.infer<typeof rawOrganizationSchema>;

export type TenantSettingsInput = {
  theme?: Record<string, unknown> | null;
  map?: {
    provider?: string;
    apiKey?: string | null;
    mapId?: string | null;
    defaultCenter?: { lat: number; lng: number };
    defaultZoom?: number;
    features?: Record<string, unknown>;
  } | null;
  ai?: {
    provider?: string;
    baseUrl?: string | null;
    apiKey?: string | null;
  } | null;
  alerts?: Record<string, unknown> | null;
  devices?: Record<string, unknown> | null;
  cameras?: Record<string, unknown> | null;
  telemetry?: Record<string, unknown> | null;
  retention?: Record<string, unknown> | null;
  cms?: Record<string, unknown> | null;
};

const tenantTriggerEntrySchema = triggerCatalogEntrySchema.extend({
  enabled: z.boolean().optional(),
});

export type TenantTriggerEntry = z.infer<typeof tenantTriggerEntrySchema>;

const normalizeOrganizationList = (
  value: z.infer<typeof organizationsListSchema>,
  params?: { limit?: number; offset?: number },
) => {
  if (Array.isArray(value)) {
    return {
      items: value,
      total: value.length,
      offset: params?.offset ?? 0,
      limit: params?.limit ?? value.length,
    };
  }

  return {
    items: value.items,
    total: value.total ?? value.items.length,
    offset: value.offset ?? params?.offset ?? 0,
    limit: value.limit ?? params?.limit ?? value.items.length,
  };
};

// Organization Management APIs
export async function fetchOrganizationsList(params?: {
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{
  items: OrganizationResponse[];
  total: number;
  offset: number;
  limit: number;
}> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set("search", params.search);
  if (params?.status) searchParams.set("status", params.status);
  if (params?.limit) searchParams.set("limit", params.limit.toString());
  if (params?.offset) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  let data: unknown;
  try {
    data = await request<unknown>(`/organizations${query ? `?${query}` : ""}`);
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Organizations endpoint unavailable; returning empty organization list.",
      );
      return normalizeOrganizationList([], params);
    }
    throw error;
  }
  const parsed = organizationsListSchema.safeParse(data);

  let normalized = parsed.success
    ? normalizeOrganizationList(parsed.data, params)
    : null;

  if (!normalized) {
    log.api.warn(
      "Failed to parse organizations list — attempting raw fallback",
      parsed.error,
    );
    // Best-effort fallback: extract items from the raw response
    const raw = data as Record<string, unknown> | unknown[];
    if (Array.isArray(raw)) {
      normalized = normalizeOrganizationList(
        { items: raw as any[], total: raw.length },
        params,
      );
    } else if (
      raw &&
      typeof raw === "object" &&
      "items" in raw &&
      Array.isArray((raw as any).items)
    ) {
      normalized = normalizeOrganizationList(raw as any, params);
    } else {
      normalized = normalizeOrganizationList([], params);
    }
  }

  // Ensure enabledServices is derived from metadata for all list items.
  const items = normalized.items.map((org) => {
    const transformed = organizationSchema.safeParse(org);
    return transformed.success ? transformed.data : org;
  });

  return { ...normalized, items };
}

export async function fetchOrganization(
  organizationId: string,
): Promise<OrganizationResponse | null> {
  let data: unknown;
  try {
    data = await request<unknown>(`/organizations/${organizationId}`);
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        `Organization ${organizationId} is unavailable; returning null.`,
      );
      return null;
    }
    throw error;
  }
  const parsed = organizationSchema.safeParse(data);

  if (!parsed.success) {
    log.api.warn(
      "Failed to parse organization — returning raw data",
      parsed.error,
    );
    const raw = data as Record<string, unknown>;
    if (raw && typeof raw.id === "string") {
      return raw as unknown as OrganizationResponse;
    }
    return null;
  }

  return parsed.data;
}

export async function createOrganization(payload: {
  name: string;
  slug?: string;
  legalName?: string;
  type?: string;
  industry?: string;
  tenantPackage?: "core" | "insurance" | "farming";
  countryCode?: string;
  region?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  status?: string;
  plan?: string;
  billingCycle?: "monthly" | "quarterly" | "biannual" | "annual";
  seatCount?: number;
  vehicleCount?: number;
  deviceCount?: number;
  logoUrl?: string;
  enabledServices?: Record<string, boolean>;
  metadata?: unknown;
  adminEmail?: string;
  adminPassword?: string;
  customDomain?: string;
}): Promise<OrganizationResponse | null> {
  const data = await request<unknown>("/organizations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const parsed = organizationSchema.safeParse(data);

  if (!parsed.success) {
    log.api.warn(
      "Failed to parse created organization — returning raw data",
      parsed.error,
    );
    // Return the raw response as a best-effort fallback so the UI can still use it
    const raw = data as Record<string, unknown>;
    if (raw && typeof raw.id === "string" && typeof raw.name === "string") {
      return raw as unknown as OrganizationResponse;
    }
    return null;
  }

  return parsed.data;
}

export async function fetchTriggerCatalog(
  tenantId: string,
): Promise<TenantTriggerEntry[]> {
  const params = new URLSearchParams({ tenantId });
  const data = await request<unknown>(`/triggers/catalog?${params.toString()}`);
  const parsed = z.array(tenantTriggerEntrySchema).safeParse(data);

  if (!parsed.success) {
    // Avoid leaking Zod issue payloads into the UI. Log full details only in dev.
    if (process.env.NODE_ENV === "development") {
      log.api.error("Failed to parse trigger catalog response", parsed.error);
    }
    throw new Error("Unable to load trigger catalog");
  }

  return parsed.data;
}

export async function updateTriggerStatus(
  triggerId: string,
  enabled: boolean,
): Promise<{ triggerId: string; enabled: boolean }> {
  const data = await request<unknown>(`/triggers/config/${triggerId}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
  return z.object({ triggerId: z.string(), enabled: z.boolean() }).parse(data);
}

/** A fired trigger event (persisted as a Ticket), with its origin vehicle. */
export interface TriggerEventRecord {
  id: string;
  organizationId?: string | null;
  vehicleId?: string | null;
  triggerId?: string | null;
  category?: string | null;
  priority?: string | null;
  status?: string | null;
  title?: string | null;
  description?: string | null;
  createdAt?: string | null;
  acknowledgedAt?: string | null;
  resolvedAt?: string | null;
  metadata?: {
    location?: { lat?: number; lng?: number } | null;
    occurredAt?: string | null;
    acknowledgedBy?: string | null;
    resolvedBy?: string | null;
    resolutionNotes?: string | null;
    [key: string]: unknown;
  } | null;
  Vehicle?: { id: string; name?: string | null; regNo?: string | null } | null;
}

export async function fetchTriggerEvents(options?: {
  limit?: number;
}): Promise<TriggerEventRecord[]> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  const query = params.toString();
  const data = await request<unknown>(
    `/triggers/events${query ? `?${query}` : ""}`,
  );
  return Array.isArray(data) ? (data as TriggerEventRecord[]) : [];
}

export async function acknowledgeTriggerEvent(
  eventId: string,
): Promise<TriggerEventRecord> {
  return request<TriggerEventRecord>(
    `/triggers/events/${eventId}/acknowledge`,
    { method: "POST" },
  );
}

export async function resolveTriggerEvent(
  eventId: string,
  notes: string,
): Promise<TriggerEventRecord> {
  return request<TriggerEventRecord>(`/triggers/events/${eventId}/resolve`, {
    method: "POST",
    body: JSON.stringify({ notes }),
  });
}

export async function fetchBillingAccounts(): Promise<BillingAccount[]>;
export async function fetchBillingAccounts(
  tenantId: string,
): Promise<BillingAccount[]>;
export async function fetchBillingAccounts(options: {
  tenantId?: string;
  parentAccountId?: string | null;
}): Promise<BillingAccount[]>;
export async function fetchBillingAccounts(
  tenantOrOptions?:
    | string
    | { tenantId?: string; parentAccountId?: string | null },
  maybeParentAccountId?: string | null,
): Promise<BillingAccount[]> {
  let tenantId: string | undefined;
  let parentAccountId: string | null | undefined;

  if (typeof tenantOrOptions === "string") {
    tenantId = tenantOrOptions;
    parentAccountId = maybeParentAccountId;
  } else if (tenantOrOptions) {
    tenantId = tenantOrOptions.tenantId;
    parentAccountId = tenantOrOptions.parentAccountId;
  }

  const params = new URLSearchParams();
  if (tenantId) {
    params.set("tenantId", tenantId);
  }
  if (parentAccountId !== undefined) {
    params.set(
      "parentAccountId",
      parentAccountId === null ? "null" : parentAccountId,
    );
  }

  const query = params.toString();
  const path = `/billing/accounts${query ? `?${query}` : ""}`;
  // When tenantId is provided (e.g. SUPER_ADMIN viewing another org), pass as header so backend RLS/context works
  const headers = tenantId
    ? { "x-organization-id": tenantId }
    : undefined;
  const response = await request<{ data?: unknown[] } | unknown[]>(path, {
    ...(headers && { headers }),
  });
  const dataArray = Array.isArray(response)
    ? response
    : Array.isArray(response.data)
      ? response.data
      : [];
  return dataArray.map((account) => billingAccountSchema.parse(account));
}

export async function fetchBillingPlans(): Promise<BillingPlan[]> {
  const data = await request<unknown[]>(`/billing/plans`, {
    cache: "no-store",
  });
  return data.map((item) => billingPlanSchema.parse(item));
}

export async function fetchAdminBillingPlans(filters?: {
  status?: string;
}): Promise<BillingPlan[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  const query = params.toString();
  const data = await request<unknown[]>(
    `/billing/admin/plans${query ? `?${query}` : ""}`,
    { cache: "no-store" },
  );
  return data.map((item) => billingPlanSchema.parse(item));
}

export async function upsertBillingPlan(payload: {
  id: string;
  name: string;
  description?: string | null;
  tierKey: BillingPlan["tierKey"];
  status?: BillingPlan["status"];
  currency?: string;
  setupFee?: number;
  monthlyFee?: number;
  perVehicleFee?: number;
  perDeviceFee?: number;
  maxSeats?: number;
  maxVehicles?: number;
  maxDevices?: number;
  availableBillingCycles?: BillingPlan["availableBillingCycles"];
  billingCycleAdjustments?: Record<string, unknown> | null;
  servicesEnabled?: Record<string, boolean> | null;
  features?: string[];
  metadata?: Record<string, unknown> | null;
}): Promise<BillingPlan> {
  const data = await request<unknown>(`/billing/admin/plans`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return billingPlanSchema.parse(data);
}

export async function updateBillingPlan(
  planId: string,
  patch: Partial<{
    name: string;
    description: string | null;
    tierKey: BillingPlan["tierKey"];
    status: BillingPlan["status"];
    currency: string;
    setupFee: number;
    monthlyFee: number;
    perVehicleFee: number;
    perDeviceFee: number;
    maxSeats: number;
    maxVehicles: number;
    maxDevices: number;
    availableBillingCycles: BillingPlan["availableBillingCycles"];
    billingCycleAdjustments: Record<string, unknown> | null;
    servicesEnabled: Record<string, boolean> | null;
    features: string[];
    metadata: Record<string, unknown> | null;
  }>,
): Promise<BillingPlan> {
  const data = await request<unknown>(`/billing/admin/plans/${planId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return billingPlanSchema.parse(data);
}

export async function archiveBillingPlan(planId: string): Promise<BillingPlan> {
  const data = await request<unknown>(
    `/billing/admin/plans/${planId}/archive`,
    {
      method: "POST",
    },
  );
  return billingPlanSchema.parse(data);
}

// ── Org-scoped billing plan CRUD (for ORG_ADMIN tenant-private plans) ──

export async function fetchOrgBillingPlans(): Promise<BillingPlan[]> {
  const data = await request<unknown[]>(`/billing/org-plans`, {
    cache: "no-store",
  });
  return data.map((item) => billingPlanSchema.parse(item));
}

export async function createOrgBillingPlan(payload: {
  id?: string;
  name: string;
  description?: string | null;
  tierKey: BillingPlan["tierKey"];
  status?: BillingPlan["status"];
  currency?: string;
  setupFee?: number;
  monthlyFee?: number;
  perVehicleFee?: number;
  perDeviceFee?: number;
  maxSeats?: number;
  maxVehicles?: number;
  maxDevices?: number;
  availableBillingCycles?: BillingPlan["availableBillingCycles"];
  billingCycleAdjustments?: Record<string, unknown> | null;
  servicesEnabled?: Record<string, boolean> | null;
  features?: string[];
  metadata?: Record<string, unknown> | null;
}): Promise<BillingPlan> {
  const data = await request<unknown>(`/billing/org-plans`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return billingPlanSchema.parse(data);
}

export async function updateOrgBillingPlan(
  planId: string,
  patch: Partial<{
    name: string;
    description: string | null;
    tierKey: BillingPlan["tierKey"];
    status: BillingPlan["status"];
    currency: string;
    setupFee: number;
    monthlyFee: number;
    perVehicleFee: number;
    perDeviceFee: number;
    maxSeats: number;
    maxVehicles: number;
    maxDevices: number;
    availableBillingCycles: BillingPlan["availableBillingCycles"];
    billingCycleAdjustments: Record<string, unknown> | null;
    servicesEnabled: Record<string, boolean> | null;
    features: string[];
    metadata: Record<string, unknown> | null;
  }>,
): Promise<BillingPlan> {
  const data = await request<unknown>(`/billing/org-plans/${planId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return billingPlanSchema.parse(data);
}

export async function archiveOrgBillingPlan(
  planId: string,
): Promise<BillingPlan> {
  const data = await request<unknown>(`/billing/org-plans/${planId}/archive`, {
    method: "POST",
  });
  return billingPlanSchema.parse(data);
}

export async function fetchPublicBillingPlans(): Promise<BillingPlan[]> {
  const data = await request<unknown[]>(`/public/plans`, { cache: "no-store" });
  return data.map((item) => billingPlanSchema.parse(item));
}

export async function createBillingAccount(payload: {
  displayName: string;
  type: BillingAccount["type"];
  status?: BillingAccount["status"];
  currency?: string;
  ownerId?: string;
  parentAccountId?: string | null;
  metadata?: Record<string, unknown> | null;
  tenantId?: string;
}): Promise<BillingAccount> {
  const data = await request<unknown>(`/billing/accounts`, {
    method: "POST",
    headers: tenantScopedHeaders(payload.tenantId),
    body: JSON.stringify(payload),
  });
  return billingAccountSchema.parse(data);
}

// ── Fleets (Sub-clients) ────────────────────────────────────────────

const fleetSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
    name: z.string(),
    code: z.string().nullable().optional(),
    status: z.string().optional(),
    description: z.string().nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

export type Fleet = z.infer<typeof fleetSchema>;

export async function fetchFleets(tenantId?: string): Promise<Fleet[]> {
  const params = new URLSearchParams();
  if (tenantId) {
    params.set("tenantId", tenantId);
  }
  const query = params.toString();
  const data = await request<unknown>(`/fleets${query ? `?${query}` : ""}`);

  const list = Array.isArray(data)
    ? data
    : typeof data === "object" &&
      data !== null &&
      Array.isArray((data as any).data)
      ? (data as any).data
      : [];

  return list.map((item: unknown) => fleetSchema.parse(item));
}

export async function createFleet(payload: {
  tenantId?: string;
  name: string;
  code?: string;
  description?: string;
  status?: string;
  metadata?: Record<string, unknown> | null;
  createBillingAccount?: boolean;
  currency?: string;
}): Promise<{ fleet: Fleet; billingAccount?: BillingAccount }> {
  const params = new URLSearchParams();
  if (payload.tenantId) {
    params.set("tenantId", payload.tenantId);
  }
  const query = params.toString();

  const data = await request<unknown>(`/fleets${query ? `?${query}` : ""}`, {
    method: "POST",
    body: JSON.stringify({
      name: payload.name,
      code: payload.code,
      description: payload.description,
      status: payload.status,
      metadata: payload.metadata ?? undefined,
      createBillingAccount: payload.createBillingAccount,
      currency: payload.currency,
    }),
  });

  // API returns { fleet, billingAccount? }.
  const parsed = z
    .object({
      fleet: fleetSchema,
      billingAccount: billingAccountSchema.optional(),
    })
    .passthrough()
    .parse(data);

  return parsed;
}

export async function ensureFleetBillingAccount(
  fleetId: string,
  tenantId?: string,
  currency?: string,
): Promise<BillingAccount> {
  const params = new URLSearchParams();
  if (tenantId) {
    params.set("tenantId", tenantId);
  }
  if (currency) {
    params.set("currency", currency);
  }
  const query = params.toString();
  const data = await request<unknown>(
    `/fleets/${encodeURIComponent(fleetId)}/billing-account/ensure${query ? `?${query}` : ""}`,
    {
      method: "POST",
    },
  );
  return billingAccountSchema.parse(data);
}

export async function fetchNotifications(params: {
  tenantId?: string;
  organizationId?: string;
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
}): Promise<NotificationListResponse> {
  const query = new URLSearchParams();
  if (params.unreadOnly) {
    query.set("unreadOnly", "true");
  }
  if (typeof params.limit === "number") {
    query.set("limit", String(params.limit));
  }
  if (typeof params.offset === "number") {
    query.set("offset", String(params.offset));
  }
  const scopedOrg = params.organizationId ?? params.tenantId;
  if (scopedOrg) {
    query.set("organizationId", scopedOrg);
  }

  const suffix = query.toString().length > 0 ? `?${query.toString()}` : "";
  try {
    const data = await request<unknown>(`/notifications${suffix}`);
    return notificationListResponseSchema.parse(data);
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Notifications endpoint unavailable; returning empty notifications payload.",
      );
      return {
        notifications: [],
        total: 0,
        unread: 0,
        hasMore: false,
        limit: params.limit,
        offset: params.offset,
      };
    }
    throw error;
  }
}

export async function markNotificationRead(notificationId: string) {
  await request(`/notifications/${notificationId}/read`, {
    method: "PATCH",
  });
}

export async function markAllNotificationsRead() {
  await request(`/notifications/read-all`, {
    method: "PATCH",
  });
}

export async function deleteNotification(notificationId: string) {
  await request(`/notifications/${notificationId}`, {
    method: "DELETE",
  });
}

export async function fetchNotificationPreferences(
  tenantId?: string,
  organizationId?: string,
): Promise<NotificationPreferencesResponse> {
  const query = new URLSearchParams();
  const scopedOrg = organizationId ?? tenantId;
  if (scopedOrg) {
    query.set("organizationId", scopedOrg);
  }
  const suffix = query.toString().length > 0 ? `?${query.toString()}` : "";
  try {
    const data = await request<unknown>(
      `/notifications/preferences/me${suffix}`,
    );
    return notificationPreferencesResponseSchema.parse(data);
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Notification preferences endpoint unavailable; returning empty preferences.",
      );
      return {};
    }
    throw error;
  }
}

export async function updateNotificationPreferences(
  channels: Record<string, Record<string, boolean>>,
  tenantId?: string,
  organizationId?: string,
): Promise<NotificationPreferencesResponse> {
  const query = new URLSearchParams();
  const scopedOrg = organizationId ?? tenantId;
  if (scopedOrg) {
    query.set("organizationId", scopedOrg);
  }
  const suffix = query.toString().length > 0 ? `?${query.toString()}` : "";
  const data = await request<unknown>(
    `/notifications/preferences/me${suffix}`,
    {
      method: "PATCH",
      body: JSON.stringify({ channels }),
    },
  );
  return notificationPreferencesResponseSchema.parse(data);
}

export async function fetchNotificationUnreadCount(
  tenantId?: string,
  organizationId?: string,
) {
  const query = new URLSearchParams();
  const scopedOrg = organizationId ?? tenantId;
  if (scopedOrg) {
    query.set("organizationId", scopedOrg);
  }
  const suffix = query.toString().length > 0 ? `?${query.toString()}` : "";
  try {
    const data = await request<unknown>(`/notifications/unread/count${suffix}`);
    return notificationCountSchema.parse(data);
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Notification unread count endpoint unavailable; defaulting to 0.",
      );
      return { count: 0 };
    }
    throw error;
  }
}

export async function updateOrganization(
  organizationId: string,
  payload: Partial<{
    name: string;
    legalName?: string;
    type?: string;
    industry?: string;
    countryCode?: string;
    region?: string;
    website?: string;
    contactEmail?: string;
    contactPhone?: string;
    status?: string;
    plan?: string;
    enabledServices?: Record<string, boolean>;
    metadata?: unknown;
  }>,
) {
  const data = await request<unknown>(`/organizations/${organizationId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data;
}

export async function deleteOrganization(organizationId: string) {
  await request<void>(`/organizations/${organizationId}`, {
    method: "DELETE",
  });
}

export async function fetchOrganizationUsers(organizationId: string) {
  const data = await request<unknown>(`/organizations/${organizationId}/users`);
  return data;
}

export async function fetchOrganizationVehicles(
  organizationId: string,
): Promise<Vehicle[]> {
  const data = await request<unknown>(
    `/organizations/${organizationId}/vehicles`,
  );
  return Array.isArray(data) ? (data as Vehicle[]) : [];
}

export async function fetchOrganizationDevices(
  organizationId: string,
): Promise<Vehicle[]> {
  const data = await request<unknown>(
    `/organizations/${organizationId}/devices`,
  );
  return Array.isArray(data) ? (data as Vehicle[]) : [];
}

export async function updateOrganizationVehicle(
  organizationId: string,
  vehicleId: string,
  payload: Partial<Vehicle>,
) {
  return request(`/organizations/${organizationId}/vehicles/${vehicleId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function updateOrganizationDeviceConfig(
  organizationId: string,
  deviceId: string,
  payload: Partial<{ mode: string; intervalSec: number; otaChannel: string }>,
) {
  return request(
    `/organizations/${organizationId}/devices/${deviceId}/config`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export async function stopOrganizationDevice(
  organizationId: string,
  deviceId: string,
) {
  return request(`/organizations/${organizationId}/devices/${deviceId}/stop`, {
    method: "POST",
  });
}

export async function suspendOrganizationDevice(
  organizationId: string,
  deviceId: string,
) {
  return request(
    `/organizations/${organizationId}/devices/${deviceId}/suspend`,
    {
      method: "POST",
    },
  );
}

export async function updateOrganizationServices(
  organizationId: string,
  enabledServices: Record<string, boolean>,
) {
  const data = await request<unknown>(
    `/organizations/${organizationId}/services`,
    {
      method: "PATCH",
      body: JSON.stringify(enabledServices),
    },
  );
  return data;
}

export interface OrganizationAccessControlMatrix {
  catalog: Array<{
    key: string;
    name: string;
    description?: string | null;
    category: string;
    route?: string | null;
    isInsurance?: boolean;
    sortOrder: number;
  }>;
  tenantModules: Record<string, boolean>;
  roleModules: Record<string, Record<string, boolean>>;
  userOverrides: Array<{
    userId: string;
    moduleKey: string;
    mode: "ALLOW" | "DENY";
    reason?: string | null;
    updatedAt?: string;
  }>;
  userOverridesByUser: Record<string, Record<string, "ALLOW" | "DENY">>;
  users: Array<{
    membershipId: string;
    userId: string;
    roleKey: string;
    roleName?: string | null;
    status?: string | null;
    user: {
      id: string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      emailHash?: string | null;
      persona?: string | null;
      status?: string | null;
    };
  }>;
  legacyEnabledServices: Record<string, boolean>;
}

export async function fetchOrganizationAccessControl(
  organizationId: string,
): Promise<OrganizationAccessControlMatrix> {
  const data = await request<unknown>(
    `/organizations/${organizationId}/access-control`,
  );
  return data as OrganizationAccessControlMatrix;
}

export async function updateOrganizationAccessControlModules(
  organizationId: string,
  modules: Record<string, boolean>,
) {
  const data = await request<unknown>(
    `/organizations/${organizationId}/access-control/modules`,
    {
      method: "PATCH",
      body: JSON.stringify({ modules }),
    },
  );
  return data as {
    tenantModules: Record<string, boolean>;
    legacyEnabledServices: Record<string, boolean>;
  };
}

export async function updateOrganizationAccessControlRole(
  organizationId: string,
  roleKey: string,
  modules: Record<string, boolean>,
) {
  const data = await request<unknown>(
    `/organizations/${organizationId}/access-control/roles/${roleKey}`,
    {
      method: "PATCH",
      body: JSON.stringify({ modules }),
    },
  );
  return data as { roleKey: string; modules: Record<string, boolean> };
}

export async function updateOrganizationAccessControlUserOverrides(
  organizationId: string,
  userId: string,
  overrides: Record<string, "ALLOW" | "DENY" | "INHERIT">,
  reason?: string,
) {
  const data = await request<unknown>(
    `/organizations/${organizationId}/access-control/users/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ overrides, reason }),
    },
  );
  return data as {
    userId: string;
    overrides: Record<string, "ALLOW" | "DENY">;
  };
}

export interface OrganizationBillingAccessState {
  organizationId: string;
  organizationName?: string | null;
  status: "active" | "grace" | "warning" | "restricted" | "suspended";
  source: "healthy" | "delinquency" | "manual_override";
  reason: string | null;
  customerMessage: string;
  graceUntil: string | null;
  disabledModules: string[];
  billingOnly: boolean;
  overdueInvoiceCount: number;
  outstandingAmount: number;
  currency: string | null;
  highestDaysPastDue: number;
  highestStage: string | null;
  actionable: boolean;
  openInvoiceCount?: number;
  evaluatedAt?: string;
  manualPolicy: {
    manualMode: "inherit" | "warning" | "restrict_modules" | "billing_only";
    disabledModules: string[];
    graceUntil: string | null;
    reason: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
  };
}

export interface OrganizationBillingActivityItem {
  id: string;
  action: string;
  summary: string | null;
  targetType: string;
  targetId: string | null;
  actorUserId: string | null;
  actorLabel: string | null;
  createdAt: string;
  context: Record<string, unknown> | null;
}

export async function fetchOrganizationBillingAccess(
  organizationId: string,
): Promise<OrganizationBillingAccessState> {
  const data = await request<unknown>(
    `/organizations/${organizationId}/billing-access`,
  );
  return data as OrganizationBillingAccessState;
}

export async function fetchOrganizationBillingActivity(
  organizationId: string,
  limit = 20,
): Promise<OrganizationBillingActivityItem[]> {
  const data = await request<unknown>(
    `/organizations/${organizationId}/billing-activity?limit=${encodeURIComponent(
      String(limit),
    )}`,
  );
  return Array.isArray(data) ? (data as OrganizationBillingActivityItem[]) : [];
}

export async function updateOrganizationBillingAccess(
  organizationId: string,
  payload: {
    manualMode?: "inherit" | "warning" | "restrict_modules" | "billing_only";
    disabledModules?: string[];
    graceUntil?: string | null;
    reason?: string | null;
  },
): Promise<OrganizationBillingAccessState> {
  const data = await request<unknown>(
    `/organizations/${organizationId}/billing-access`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
  return data as OrganizationBillingAccessState;
}

export async function updateTenantSettings(
  organizationId: string,
  payload: TenantSettingsInput,
): Promise<TenantSettingsResponse | null> {
  const data = await request<unknown>(
    `/organizations/${organizationId}/tenant-settings`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );

  const parsed = tenantSettingsSchema.safeParse(data);
  if (!parsed.success) {
    log.api.warn("Failed to parse tenant settings response", parsed.error);
    return null;
  }

  return parsed.data;
}

export async function fetchCmsSettings(): Promise<CmsSettings> {
  const data = await request<unknown>("/settings/cms");
  const payload = (data as { data?: unknown }).data ?? data;
  return cmsSettingsSchema.parse(payload);
}

export async function updateCmsSettings(
  payload: Partial<CmsSettings>,
): Promise<CmsSettings> {
  const data = await request<unknown>("/settings/cms", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  const payloadData = (data as { data?: unknown }).data ?? data;
  return cmsSettingsSchema.parse(payloadData);
}

export async function fetchEvents(params: Record<string, string | undefined>) {
  queryEventsSchema.parse(params);
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const query = search.toString();
  const data = await request<unknown[]>(`/events${query ? `?${query}` : ""}`);
  return data.map((event) => eventSchema.parse(event));
}

export async function fetchDeviceLogs(
  deviceId: string,
  limit?: number,
): Promise<DeviceRawLog[]> {
  const query = limit ? `?limit=${limit}` : "";
  const data = await request<unknown[]>(`/vehicles/${deviceId}/logs${query}`);
  return data.map((item) => deviceRawLogSchema.parse(item));
}

export async function fetchBillingSubscriptions(
  accountId: string,
  tenantId?: string,
): Promise<Subscription[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/subscriptions`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => subscriptionSchema.parse(item));
}

export async function fetchBillingSubscriptionSeats(
  subscriptionId: string,
  tenantId?: string,
): Promise<BillingSubscriptionSeat[]> {
  const data = await request<unknown[]>(
    `/billing/subscriptions/${subscriptionId}/seats`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => billingSubscriptionSeatSchema.parse(item));
}

export async function assignBillingSubscriptionSeats(
  subscriptionId: string,
  userIds: string[],
  tenantId?: string,
): Promise<BillingSubscriptionSeat[]> {
  const data = await request<unknown[]>(
    `/billing/subscriptions/${subscriptionId}/seats`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify({ userIds }),
    },
  );
  return data.map((item) => billingSubscriptionSeatSchema.parse(item));
}

export async function revokeBillingSubscriptionSeat(
  subscriptionId: string,
  userId: string,
  tenantId?: string,
): Promise<BillingSubscriptionSeat> {
  const data = await request<unknown>(
    `/billing/subscriptions/${subscriptionId}/seats/${userId}`,
    {
      method: "DELETE",
      headers: tenantScopedHeaders(tenantId),
    },
  );
  return billingSubscriptionSeatSchema.parse(data);
}

const adminSubscriptionRowSchema = z.object({
  subscription: subscriptionSchema,
  account: billingAccountSchema,
  organization: z
    .object({
      id: z.string(),
      slug: z.string(),
      name: z.string(),
      plan: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  seats: z
    .array(
      billingSubscriptionSeatSchema.extend({
        user: z
          .object({
            id: z.string(),
            name: z.string(),
            email: z.string(),
            firstName: z.string().nullable().optional(),
            lastName: z.string().nullable().optional(),
          })
          .optional(),
      }),
    )
    .default([]),
});

export type AdminBillingSubscriptionRow = z.infer<
  typeof adminSubscriptionRowSchema
>;

// ── SUPER_ADMIN Billing Admin Endpoints ──────────────────────────────

export interface TenantBillingSummary {
  tenantId: string;
  tenantName: string;
  accountCount: number;
  subscriptionCount: number;
  activeSubscriptions: number;
  totalMrr: number;
  totalOutstanding: number;
  invoiceCount: number;
  overdueInvoices: number;
  currency: string;
}

export interface NetworkBillingOverviewRow {
  id: string; // The ID of the organization or the billing account
  tenantId: string;
  accountId: string | null;
  name: string;
  type: "tenant" | "fleet" | "merchant" | "customer" | "reseller" | "platform";
  activeSubscription: Subscription | null;
  totalOutstanding: number;
  currency: string;
}

export interface NetworkInvoiceRow extends Invoice {
  accountName?: string;
  tenantId?: string;
}

export async function fetchNetworkOverview(): Promise<NetworkBillingOverviewRow[]> {
  const data = await request<unknown[]>("/billing/network/overview");
  return data as NetworkBillingOverviewRow[];
}

export async function fetchNetworkSubscriptions(filters?: {
  tenantId?: string;
  planId?: string;
  status?: Subscription["status"];
  take?: number;
}): Promise<AdminBillingSubscriptionRow[]> {
  const params = new URLSearchParams();
  if (filters?.tenantId) params.set("tenantId", filters.tenantId);
  if (filters?.planId) params.set("planId", filters.planId);
  if (filters?.status) params.set("status", filters.status);
  if (typeof filters?.take === "number")
    params.set("take", String(filters.take));

  const query = params.toString();
  const data = await request<unknown[]>(
    `/billing/network/subscriptions${query ? `?${query}` : ""}`,
    { cache: "no-store" },
  );
  return data.map((item) => adminSubscriptionRowSchema.parse(item));
}

export async function fetchNetworkInvoices(tenantId?: string): Promise<NetworkInvoiceRow[]> {
  const query = tenantId ? `?tenantId=${tenantId}` : "";
  const data = await request<unknown[]>(`/billing/network/invoices${query}`, { cache: "no-store" });
  return data as NetworkInvoiceRow[];
}

export async function fetchAdminBillingAccounts(
  take?: number,
): Promise<BillingAccount[]> {
  const params = new URLSearchParams();
  if (typeof take === "number") params.set("take", String(take));
  const query = params.toString();
  const data = await request<unknown[]>(
    `/billing/admin/accounts${query ? `?${query}` : ""}`,
  );
  return data.map((item) => billingAccountSchema.parse(item));
}

export async function fetchAdminTenantSummaries(): Promise<
  TenantBillingSummary[]
> {
  return request<TenantBillingSummary[]>("/billing/admin/tenant-summaries");
}

export async function fetchAdminBillingSubscriptions(filters?: {
  tenantId?: string;
  planId?: string;
  status?: Subscription["status"];
  take?: number;
}): Promise<AdminBillingSubscriptionRow[]> {
  const params = new URLSearchParams();
  if (filters?.tenantId) params.set("tenantId", filters.tenantId);
  if (filters?.planId) params.set("planId", filters.planId);
  if (filters?.status) params.set("status", filters.status);
  if (typeof filters?.take === "number")
    params.set("take", String(filters.take));

  const query = params.toString();
  const data = await request<unknown[]>(
    `/billing/admin/subscriptions${query ? `?${query}` : ""}`,
    { cache: "no-store" },
  );
  return data.map((item) => adminSubscriptionRowSchema.parse(item));
}

export async function createBillingSubscription(
  accountId: string,
  payload: Omit<Subscription, "id" | "createdAt" | "updatedAt">,
  tenantId?: string,
): Promise<Subscription> {
  const body = subscriptionSchema
    .pick({
      accountId: true,
      planId: true,
      planName: true,
      tier: true,
      seatCount: true,
      vehicleCount: true,
      deviceCount: true,
      vehicleIds: true,
      deviceIds: true,
      featureFlags: true,
      startDate: true,
      endDate: true,
      status: true,
      recurring: true,
      autoTopUp: true,
      trialEndsAt: true,
      nextRenewalAt: true,
      setupFee: true,
      monthlyFee: true,
      perVehicleFee: true,
      perDeviceFee: true,
      currency: true,
      metadata: true,
    })
    .parse({ ...payload, accountId });
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/subscriptions`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(body),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function updateBillingSubscription(
  subscriptionId: string,
  payload: Partial<
    Omit<Subscription, "id" | "accountId" | "createdAt" | "updatedAt">
  >,
  tenantId?: string,
): Promise<Subscription> {
  const data = await request<unknown>(
    `/billing/subscriptions/${subscriptionId}`,
    {
      method: "PATCH",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function cancelBillingSubscription(
  subscriptionId: string,
  effectiveAt?: string,
  tenantId?: string,
): Promise<Subscription> {
  const data = await request<unknown>(
    `/billing/subscriptions/${subscriptionId}/cancel`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify({ endDate: effectiveAt }),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function resumeBillingSubscription(
  subscriptionId: string,
  tenantId?: string,
): Promise<Subscription> {
  const data = await request<unknown>(
    `/billing/subscriptions/${subscriptionId}/resume`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function fetchBillingInvoices(
  accountId: string,
  tenantId?: string,
): Promise<Invoice[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/invoices`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => invoiceSchema.parse(item));
}

export async function fetchBillingInvoice(
  invoiceId: string,
  tenantId?: string,
): Promise<Invoice> {
  const data = await request<unknown>(`/billing/invoices/${invoiceId}`, {
    headers: tenantScopedHeaders(tenantId),
  });
  return invoiceSchema.parse(data);
}

export async function fetchBillingPayments(
  accountId: string,
  tenantId?: string,
): Promise<Payment[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/payments`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => paymentSchema.parse(item));
}

export async function recordBillingPayment(
  accountId: string,
  payload: {
    invoiceId?: string;
    method: Payment["method"];
    amount: { amount: number; currency: string };
    status: Payment["status"];
    transactionRef?: string;
    processedAt?: string;
    metadata?: Record<string, unknown>;
  },
  tenantId?: string,
): Promise<Payment> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/payments`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return paymentSchema.parse(data);
}

export async function fetchBillingPurchaseOrders(
  accountId: string,
  tenantId?: string,
): Promise<PurchaseOrder[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/purchase-orders`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => purchaseOrderSchema.parse(item));
}

export async function fetchBillingExpenses(
  accountId: string,
  tenantId?: string,
): Promise<Expense[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/expenses`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => expenseSchema.parse(item));
}

export async function fetchBillingRevenueShare(
  accountId: string,
  tenantId?: string,
): Promise<RevenueShareRule[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/revenue-share`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => revenueShareRuleSchema.parse(item));
}

export async function fetchBillingSimUsage(
  accountId: string,
  tenantId?: string,
): Promise<SimUsageRecord[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/sim-usage`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => simUsageRecordSchema.parse(item));
}

// ── Expense CRUD ─────────────────────────────────────────────────────
export async function createBillingExpense(
  accountId: string,
  payload: Omit<Expense, "id" | "createdAt" | "updatedAt">,
  tenantId?: string,
): Promise<Expense> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/expenses`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return expenseSchema.parse(data);
}

export async function updateBillingExpense(
  expenseId: string,
  payload: Partial<
    Omit<Expense, "id" | "accountId" | "createdAt" | "updatedAt">
  >,
  tenantId?: string,
): Promise<Expense> {
  const data = await request<unknown>(`/billing/expenses/${expenseId}`, {
    method: "PATCH",
    headers: tenantScopedHeaders(tenantId),
    body: JSON.stringify(payload),
  });
  return expenseSchema.parse(data);
}

export async function deleteBillingExpense(
  expenseId: string,
  tenantId?: string,
): Promise<void> {
  await request(`/billing/expenses/${expenseId}`, {
    method: "DELETE",
    headers: tenantScopedHeaders(tenantId),
  });
}

// ── Revenue Share Rule CRUD ──────────────────────────────────────────
export async function createBillingRevenueShareRule(
  accountId: string,
  payload: Omit<RevenueShareRule, "id" | "createdAt" | "updatedAt">,
  tenantId?: string,
): Promise<RevenueShareRule> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/revenue-share`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return revenueShareRuleSchema.parse(data);
}

export async function updateBillingRevenueShareRule(
  ruleId: string,
  payload: Partial<
    Omit<RevenueShareRule, "id" | "accountId" | "createdAt" | "updatedAt">
  >,
  tenantId?: string,
): Promise<RevenueShareRule> {
  const data = await request<unknown>(`/billing/revenue-share/${ruleId}`, {
    method: "PATCH",
    headers: tenantScopedHeaders(tenantId),
    body: JSON.stringify(payload),
  });
  return revenueShareRuleSchema.parse(data);
}

export async function deleteBillingRevenueShareRule(
  ruleId: string,
  tenantId?: string,
): Promise<void> {
  await request(`/billing/revenue-share/${ruleId}`, {
    method: "DELETE",
    headers: tenantScopedHeaders(tenantId),
  });
}

// ── SIM Usage Record CRUD ────────────────────────────────────────────
export async function createBillingSimUsageRecord(
  accountId: string,
  payload: Omit<SimUsageRecord, "id" | "createdAt" | "updatedAt">,
  tenantId?: string,
): Promise<SimUsageRecord> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/sim-usage`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return simUsageRecordSchema.parse(data);
}

export async function updateBillingSimUsageRecord(
  recordId: string,
  payload: Partial<
    Omit<SimUsageRecord, "id" | "accountId" | "createdAt" | "updatedAt">
  >,
  tenantId?: string,
): Promise<SimUsageRecord> {
  const data = await request<unknown>(`/billing/sim-usage/${recordId}`, {
    method: "PATCH",
    headers: tenantScopedHeaders(tenantId),
    body: JSON.stringify(payload),
  });
  return simUsageRecordSchema.parse(data);
}

export async function deleteBillingSimUsageRecord(
  recordId: string,
  tenantId?: string,
): Promise<void> {
  await request(`/billing/sim-usage/${recordId}`, {
    method: "DELETE",
    headers: tenantScopedHeaders(tenantId),
  });
}

// ── Invoice & PO Delete ──────────────────────────────────────────────
export async function deleteBillingInvoice(
  invoiceId: string,
  tenantId?: string,
): Promise<void> {
  await request(`/billing/invoices/${invoiceId}`, {
    method: "DELETE",
    headers: tenantScopedHeaders(tenantId),
  });
}

export async function recordOfflinePaymentForInvoice(
  invoiceId: string,
  payload: {
    amount?: number;
    currency?: string;
    referenceNumber?: string;
    notes?: string;
    receiptUrl?: string;
    method?: Payment["method"];
    receivedAt?: string;
  },
  tenantId?: string,
): Promise<{ payment: Payment; invoice: Invoice }> {
  const data = await request<unknown>(
    `/billing/invoices/${invoiceId}/offline-payment`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  const parsed = data as { payment: unknown; invoice: unknown };
  return {
    payment: paymentSchema.parse(parsed.payment),
    invoice: invoiceSchema.parse(parsed.invoice),
  };
}

export async function deleteBillingPurchaseOrder(
  purchaseOrderId: string,
  tenantId?: string,
): Promise<void> {
  await request(`/billing/purchase-orders/${purchaseOrderId}`, {
    method: "DELETE",
    headers: tenantScopedHeaders(tenantId),
  });
}

// ── Billing Account Settings ─────────────────────────────────────────
export async function updateBillingAccountSettings(
  accountId: string,
  payload: {
    autoRenew?: boolean;
    defaultInvoiceDestination?: string | null;
    paymentMethodId?: string | null;
  },
  tenantId?: string,
): Promise<BillingAccount> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/settings`,
    {
      method: "PATCH",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return billingAccountSchema.parse(data);
}

export async function upsertBillingDashboardSummary(
  accountId: string,
  payload: BillingDashboardSummary,
  tenantId?: string,
) {
  const body = billingDashboardSummarySchema.parse(payload);
  return request(`/billing/accounts/${accountId}/dashboard-summary`, {
    method: "POST",
    headers: tenantScopedHeaders(tenantId),
    body: JSON.stringify(body),
  });
}

export async function fetchBillingInsights(
  accountId: string,
  tenantId?: string,
): Promise<BillingInsight[]> {
  const data = await request<unknown[]>(
    `/billing/accounts/${accountId}/insights`,
    {
      headers: tenantId ? { "x-organization-id": tenantId } : undefined,
    },
  );
  return data.map((item) => billingInsightSchema.parse(item));
}

export async function downloadInvoicePdf(
  invoiceId: string,
  tenantId?: string,
): Promise<Blob> {
  const response = await fetch(
    `${API_BASE}/billing/invoices/${invoiceId}/download`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        ...(tenantScopedHeaders(tenantId) ?? {}),
      },
    },
  );

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      detail?.trim() ||
        `Failed to download invoice ${invoiceId} (${response.status})`,
    );
  }

  return response.blob();
}

export async function sendDeviceCommand(
  deviceId: string,
  payload: {
    type: string;
    correlationId?: string;
    priority?: "LOW" | "NORMAL" | "HIGH";
    executeAt?: string;
    body?: Record<string, unknown>;
  },
): Promise<CommandEnvelope> {
  const data = await request<unknown>(`/commands/${deviceId}`, {
    method: "POST",
    body: JSON.stringify({
      type: payload.type,
      correlationId: payload.correlationId,
      priority: payload.priority,
      executeAt: payload.executeAt,
      payload: payload.body,
    }),
  });
  return commandEnvelopeSchema.parse(data);
}

export async function fetchCommand(
  commandId: string,
): Promise<CommandEnvelope> {
  const data = await request<unknown>(`/commands/${commandId}`);
  return commandEnvelopeSchema.parse(data);
}

const reportDescriptorSchema = z.object({
  id: z.string(),
  index: z.number(),
  name: z.string(),
  description: z.string(),
  categoryId: z.string(),
  categoryName: z.string(),
  dataSources: z.array(z.string()),
  visuals: z.array(z.enum(["table", "chart", "trend", "map"])),
  exports: z.array(z.enum(["pdf", "xlsx", "csv", "json"])),
  delivery: z.array(z.enum(["email", "webhook", "dashboard"])),
  access: z.array(z.string()),
});

const reportCategorySchema = z.object({
  id: z.string(),
  indexOffset: z.number(),
  name: z.string(),
  reports: z.array(reportDescriptorSchema),
});

const tripSummarySchema = z.object({
  date: z.string(),
  vehicleId: z.string(),
  totalTrips: z.number(),
  distanceKm: z.number(),
  avgDurationMin: z.number(),
  fuelLiters: z.number(),
  idleMinutes: z.number(),
  overspeedMinutes: z.number(),
  incidents: z.number(),
});

export type ReportDescriptor = z.infer<typeof reportDescriptorSchema>;
export type ReportCategory = z.infer<typeof reportCategorySchema>;
export type TripSummary = z.infer<typeof tripSummarySchema>;
export const reportAssignmentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  reportId: z.string(),
  planTier: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
  vehicleId: z.string().nullable().optional(),
  deviceId: z.string().nullable().optional(),
  fleetId: z.string().nullable().optional(),
  deliveryChannels: z.array(z.string()),
  scheduleCron: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  status: z.string().optional(),
  lastRunAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ReportAssignment = z.infer<typeof reportAssignmentSchema>;

export async function fetchReportCatalog(
  tenantId?: string,
): Promise<ReportCategory[]> {
  const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : "";
  try {
    const data = await request<unknown>(`/reports/catalog${query}`);
    const parsed = reportCategorySchema.array().safeParse(data);
    if (parsed.success) {
      return parsed.data;
    }
    log.api.warn(
      "Report catalog schema drift detected; returning empty catalog.",
      parsed.error,
    );
    return [];
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        "Report catalog endpoint unavailable; returning empty catalog.",
      );
      return [];
    }
    throw error;
  }
}

export async function fetchReportDescriptor(
  reportId: string,
): Promise<ReportDescriptor> {
  const data = await request<unknown>(`/reports/${reportId}/descriptor`);
  return reportDescriptorSchema.parse(data);
}

export async function fetchImplementedReportIds(
  tenantId?: string,
): Promise<string[]> {
  const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : "";
  const data = await request<unknown>(`/reports/implemented${query}`);
  return Array.isArray(data) ? data : [];
}

export async function fetchTripSummary(params: {
  date?: string;
  vehicleId?: string;
  tenantId?: string;
}): Promise<TripSummary> {
  const search = new URLSearchParams();
  if (params.date) search.set("date", params.date);
  if (params.vehicleId) search.set("vehicleId", params.vehicleId);
  if (params.tenantId) search.set("tenantId", params.tenantId);
  const query = search.toString();
  const data = await request<unknown>(
    `/reports/trips${query ? `?${query}` : ""}`,
  );
  return tripSummarySchema.parse(data);
}

export interface TripReportExportPayload {
  tripIds?: string[];
  startDate?: string;
  endDate?: string;
  includeTriggers?: boolean;
  includeAlerts?: boolean;
  includeMapSnapshots?: boolean;
  tenantId?: string;
  organizationId?: string;
}

export async function exportVehicleTripReport(
  vehicleId: string,
  payload: TripReportExportPayload,
): Promise<Blob> {
  const response = await requestBinary(
    `/vehicles/${vehicleId}/trips/export/pdf`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  return response.blob();
}

export interface TripSummaryResponse {
  id: string;
  vehicleId: string;
  startTime: string;
  endTime: string;
  durationSeconds: number;
  distanceKm: number;
  avgSpeedKph?: number | null;
  maxSpeedKph?: number | null;
  fuelUsedLiters?: number | null;
  triggerCount: number;
  alertCount: number;
}

export interface TripPathPointResponse {
  sequence: number;
  lat: number;
  lng: number;
  recordedAt: string;
  speedKph?: number | null;
  heading?: number | null;
  altitude?: number | null;
  eventType?: string | null;
  isStartFlag?: boolean;
  isStopFlag?: boolean;
  isEndFlag?: boolean;
  source?: string;
}

export interface TripEventResponse {
  id: string;
  type: string;
  severity: string;
  message: string;
  recordedAt: string;
  source?: string;
  lat?: number | null;
  lng?: number | null;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNotes?: string | null;
  ticketId?: string | null;
  clipUrl?: string | null;
  snapshotUrl?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface TripTimelineItemResponse {
  id: string;
  source:
  | "trip-point"
  | "trip-event"
  | "ticket"
  | "camera-event"
  | "geofence-event";
  type: string;
  severity?: string | null;
  message?: string | null;
  recordedAt: string;
  sequence?: number;
  lat?: number | null;
  lng?: number | null;
  isStartFlag?: boolean;
  isStopFlag?: boolean;
  isEndFlag?: boolean;
  status?: string | null;
  ticketId?: string | null;
  geofenceId?: string | null;
  geofenceName?: string | null;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNotes?: string | null;
  clipUrl?: string | null;
  snapshotUrl?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface TripTimelineStatsResponse {
  pointCount: number;
  tripEventCount: number;
  ticketCount: number;
  cameraEventCount: number;
  geofenceEventCount: number;
}

export interface TripDetailResponse extends TripSummaryResponse {
  path: TripPathPointResponse[];
  events: TripEventResponse[];
  timeline?: TripTimelineItemResponse[];
  stats?: TripTimelineStatsResponse;
}

export async function fetchVehicleTripSummaries(
  vehicleId: string,
  options?: {
    start?: string;
    end?: string;
    refresh?: boolean;
    tenantId?: string;
    organizationId?: string;
  },
): Promise<TripSummaryResponse[]> {
  const searchParams = new URLSearchParams();
  if (options?.start) searchParams.set("start", options.start);
  if (options?.end) searchParams.set("end", options.end);
  if (options?.tenantId) searchParams.set("tenantId", options.tenantId);
  if (options?.organizationId)
    searchParams.set("organizationId", options.organizationId);
  if (typeof options?.refresh === "boolean") {
    searchParams.set("refresh", String(options.refresh));
  }

  const query = searchParams.toString();
  return request<TripSummaryResponse[]>(
    `/vehicles/${vehicleId}/trips${query ? `?${query}` : ""}`,
  );
}

export async function fetchVehicleTripHistorySummaries(
  vehicleId: string,
  options?: {
    days?: number;
    refresh?: boolean;
    tenantId?: string;
    organizationId?: string;
  },
): Promise<TripSummaryResponse[]> {
  const searchParams = new URLSearchParams();
  if (typeof options?.days === "number")
    searchParams.set("days", String(options.days));
  if (typeof options?.refresh === "boolean")
    searchParams.set("refresh", String(options.refresh));
  if (options?.tenantId) searchParams.set("tenantId", options.tenantId);
  if (options?.organizationId)
    searchParams.set("organizationId", options.organizationId);
  const query = searchParams.toString();
  return request<TripSummaryResponse[]>(
    `/vehicles/${vehicleId}/trips/history${query ? `?${query}` : ""}`,
  );
}

export async function fetchVehicleTripDetail(
  vehicleId: string,
  tripId: string,
  options?: { tenantId?: string; organizationId?: string },
): Promise<TripDetailResponse | null> {
  const searchParams = new URLSearchParams();
  if (options?.tenantId) searchParams.set("tenantId", options.tenantId);
  if (options?.organizationId)
    searchParams.set("organizationId", options.organizationId);
  const query = searchParams.toString();
  return request<TripDetailResponse | null>(
    `/vehicles/${vehicleId}/trips/${tripId}${query ? `?${query}` : ""}`,
  );
}

export async function fetchVehicleTripTimeline(
  vehicleId: string,
  tripId: string,
  options?: { tenantId?: string; organizationId?: string },
): Promise<TripDetailResponse | null> {
  const searchParams = new URLSearchParams();
  if (options?.tenantId) searchParams.set("tenantId", options.tenantId);
  if (options?.organizationId)
    searchParams.set("organizationId", options.organizationId);
  const query = searchParams.toString();
  return request<TripDetailResponse | null>(
    `/vehicles/${vehicleId}/trips/${tripId}/timeline${query ? `?${query}` : ""}`,
  );
}

export async function fetchVehicleTripEvents(
  vehicleId: string,
  tripId: string,
  options?: { tenantId?: string; organizationId?: string },
) {
  const searchParams = new URLSearchParams();
  if (options?.tenantId) searchParams.set("tenantId", options.tenantId);
  if (options?.organizationId)
    searchParams.set("organizationId", options.organizationId);
  const query = searchParams.toString();
  return request<Array<TripDetailResponse["events"][number]>>(
    `/vehicles/${vehicleId}/trips/${tripId}/events${query ? `?${query}` : ""}`,
  );
}

export async function fetchVehicleTripPath(
  vehicleId: string,
  tripId: string,
  options?: { tenantId?: string; organizationId?: string },
) {
  const searchParams = new URLSearchParams();
  if (options?.tenantId) searchParams.set("tenantId", options.tenantId);
  if (options?.organizationId)
    searchParams.set("organizationId", options.organizationId);
  const query = searchParams.toString();
  return request<Array<TripDetailResponse["path"][number]>>(
    `/vehicles/${vehicleId}/trips/${tripId}/path${query ? `?${query}` : ""}`,
  );
}

export async function upsertReportAssignment(payload: {
  tenantId: string;
  reportId: string;
  userId?: string;
  vehicleId?: string;
  deviceId?: string;
  fleetId?: string;
  deliveryChannels: string[];
  scheduleCron?: string;
}) {
  return request(`/reports/assignments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchReportAssignments(
  tenantId: string,
): Promise<ReportAssignment[]> {
  const data = await request<unknown>(
    `/reports/assignments?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return reportAssignmentSchema.array().parse(data);
}

export async function fetchReportData(
  reportId: string,
  params: {
    tenantId?: string;
    vehicleId?: string;
    userId?: string;
    from?: string;
    to?: string;
  },
) {
  const search = new URLSearchParams();
  if (params.tenantId) search.set("tenantId", params.tenantId);
  if (params.vehicleId) search.set("vehicleId", params.vehicleId);
  if (params.userId) search.set("userId", params.userId);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  const query = search.toString();
  return request(`/reports/${reportId}/data${query ? `?${query}` : ""}`);
}

export async function computeAdvancedRoute(payload: {
  origin: { latLng: { latitude: number; longitude: number } };
  destination: { latLng: { latitude: number; longitude: number } };
  intermediates?: Array<{ latLng: { latitude: number; longitude: number } }>;
  travelMode?: "DRIVE" | "BICYCLE" | "WALK" | "TWO_WHEELER";
  routingPreference?:
  | "TRAFFIC_AWARE"
  | "TRAFFIC_AWARE_OPTIMAL"
  | "TRAFFIC_UNAWARE";
  tollPasses?: string[];
  avoidTolls?: boolean;
  avoidHighways?: boolean;
  avoidFerries?: boolean;
  fieldMask?: string;
}): Promise<Record<string, unknown>> {
  if (routesApiDisabled) {
    throw new Error("Routes API disabled due unauthorized key restrictions.");
  }
  try {
    return await request("/maps/routes/compute", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes("Request failed (401)") ||
      message.includes("Request failed (403)") ||
      message.includes("referer")
    ) {
      routesApiDisabled = true;
      warnMapApiOnce(
        "routes:unauthorized",
        "[RoutesAPI] Disabled: missing/unauthorized Maps API key or referrer restriction.",
      );
    }
    warnMapApiOnce(
      `routes:${message}`,
      "[RoutesAPI] Unable to compute advanced route.",
      message,
    );
    throw error;
  }
}

export async function fetchTimeZone(params: {
  location: string;
  timestamp: string;
  language?: string;
}): Promise<{
  timeZoneId: string;
  timeZoneName: string;
  dstOffset: number;
  rawOffset: number;
}> {
  const search = new URLSearchParams({
    location: params.location,
    timestamp: params.timestamp,
  });
  if (params.language) search.set("language", params.language);
  try {
    return await request(`/maps/timezone?${search.toString()}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes("Request failed (401)") ||
      message.includes("Request failed (403)") ||
      message.includes("Request failed (404)")
    ) {
      warnMapApiOnce(
        "timezone:unavailable",
        "[Timezone] Falling back to UTC because the timezone service is unavailable.",
      );
      return {
        timeZoneId: "UTC",
        timeZoneName: "UTC",
        dstOffset: 0,
        rawOffset: 0,
      };
    }
    throw error;
  }
}

export async function estimateGeolocation(payload: {
  considerIp?: boolean;
  homeMobileCountryCode?: number;
  homeMobileNetworkCode?: number;
  radioType?: string;
  carrier?: string;
  cellTowers?: Array<Record<string, unknown>>;
  wifiAccessPoints?: Array<Record<string, unknown>>;
}): Promise<{ location: { lat: number; lng: number }; accuracy: number }> {
  return request(`/maps/geolocation`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface CameraInventoryChannel {
  id: string;
  label: string;
}

export interface CameraInventoryDevice {
  deviceId: string;
  vehicleId: string;
  vehicleName: string;
  registration: string | null;
  protocol: string;
  recordingMode: string;
  online: boolean;
  status: "recording" | "online" | "offline";
  lastSeen: string | null;
  location: { lat: number; lng: number } | null;
  channels: CameraInventoryChannel[];
}

export async function fetchCameraInventory(): Promise<CameraInventoryDevice[]> {
  const data = await request<unknown>("/camera/inventory");
  return Array.isArray(data) ? (data as CameraInventoryDevice[]) : [];
}

export async function fetchCameraStream(
  deviceId: string,
  channel: string,
  variant?: string,
): Promise<{ streamUrl: string }> {
  const suffix = variant ? `/${variant}` : "";
  return request(`/camera/${deviceId}/channels/${channel}/stream${suffix}`);
}

export async function fetchCameraSnapshot(
  deviceId: string,
  channel: string,
): Promise<{ snapshotUrl: string }> {
  return request(`/camera/${deviceId}/channels/${channel}/snapshot`);
}

export async function fetchCameraEvents(
  deviceId: string,
  since?: string,
): Promise<CameraEvent[]> {
  const search = new URLSearchParams({ deviceId, types: "camera" });
  if (since) search.set("since", since);
  const data = await request<unknown[]>(`/events?${search.toString()}`);
  return data.map((item) => cameraEventSchema.parse(item));
}

export type CameraPlaybackIndexItem = {
  eventId?: string;
  [key: string]: unknown;
};

export async function fetchCameraPlaybackIndex(params: {
  deviceId?: string;
  vehicleId?: string;
  eventId?: string;
  from?: string;
  to?: string;
  limit?: number;
}): Promise<CameraPlaybackIndexItem[]> {
  const search = new URLSearchParams();
  if (params.deviceId) search.set("deviceId", params.deviceId);
  if (params.vehicleId) search.set("vehicleId", params.vehicleId);
  if (params.eventId) search.set("eventId", params.eventId);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (typeof params.limit === "number")
    search.set("limit", String(params.limit));
  return request<CameraPlaybackIndexItem[]>(
    `/camera/playback?${search.toString()}`,
  );
}

export async function fetchFleetTrips(params?: {
  status?: string;
  pageSize?: number;
}) {
  const search = new URLSearchParams();
  if (params?.status) search.set("status", params.status);
  if (params?.pageSize) search.set("pageSize", String(params.pageSize));
  return request(
    `/fleet-engine/trips${search.toString() ? `?${search.toString()}` : ""}`,
  );
}

export async function createFleetTrip(payload: {
  tripId: string;
  vehicleId?: string;
  pickup: { lat: number; lng: number };
  dropoff: { lat: number; lng: number };
  requestedPickupTime?: string;
  riderName?: string;
}) {
  return request("/fleet-engine/trips", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateFleetTrip(
  tripId: string,
  payload: Record<string, unknown>,
) {
  return request(`/fleet-engine/trips/${tripId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function cancelFleetTrip(tripId: string) {
  return request(`/fleet-engine/trips/${tripId}/cancel`, {
    method: "PATCH",
    body: JSON.stringify({}),
  });
}

export async function createJob(payload: {
  id: string;
  pickup: { lat: number; lng: number };
  dropoff: { lat: number; lng: number };
  vehicleId?: string;
  requestedPickupTime?: string;
  riderName?: string;
}) {
  return request(`/jobs`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateJob(
  jobId: string,
  payload: { status: string; vehicleId?: string },
) {
  return request(`/jobs/${jobId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function completeJob(jobId: string) {
  return request(`/jobs/${jobId}/complete`, {
    method: "PATCH",
    body: JSON.stringify({}),
  });
}

// Vehicle Management APIs
export async function createVehicle(payload: Partial<Vehicle>) {
  return request("/vehicles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateVehicle(
  vehicleId: string,
  updates: Partial<Vehicle>,
) {
  return request(`/vehicles/${encodeURIComponent(vehicleId)}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

export async function updateVehicleForOrganization(
  vehicleId: string,
  organizationId: string,
  updates: Partial<Vehicle>,
) {
  return request(
    `/organizations/${encodeURIComponent(organizationId)}/vehicles/${encodeURIComponent(vehicleId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
  );
}

export async function fetchVehicleTickets(vehicleId: string) {
  return request(`/tickets?vehicleId=${vehicleId}`);
}

export async function createVehicleTicket(payload: Record<string, unknown>) {
  return request("/tickets", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type VehicleCommandQueueItem = Record<string, unknown>;

export async function fetchVehicleCommands(
  deviceId: string,
  limit = 25,
): Promise<VehicleCommandQueueItem[]> {
  const params = new URLSearchParams({
    vehicleId: deviceId,
    limit: String(limit),
  });
  return request<VehicleCommandQueueItem[]>(
    `/commands/history?${params.toString()}`,
  );
}

export async function sendVehicleCommand(
  deviceId: string,
  command: { type: string; payload?: Record<string, unknown> },
): Promise<VehicleCommandQueueItem> {
  return request<VehicleCommandQueueItem>(`/commands/${deviceId}`, {
    method: "POST",
    body: JSON.stringify(command),
  });
}

// duplicate legacy camera helpers removed in favor of variant-capable versions above

export async function fetchVehicleUbiScores(
  driverId: string,
  vehicleId?: string,
  tenantId?: string,
) {
  const params = new URLSearchParams({ driverId });
  if (vehicleId) params.append("vehicleId", vehicleId);
  if (tenantId) {
    params.set("tenantId", tenantId);
  }
  return request(`/ubi/scores?${params.toString()}`);
}

export async function fetchVehicleEarnings(
  vehicleId: string,
): Promise<DriverEarning[]> {
  const params = new URLSearchParams({ vehicleId });
  const data = await request<unknown[]>(`/wallet/earnings?${params}`);
  return walletVehicleEarningSchema.parse(data);
}

export async function fetchVehicleExpenses(
  vehicleId: string,
): Promise<DriverExpense[]> {
  const params = new URLSearchParams({ vehicleId });
  const data = await request<unknown[]>(`/wallet/expenses?${params}`);
  return walletVehicleExpenseSchema.parse(data);
}

export async function fetchDeviceConfig(deviceId: string) {
  return request(`/vehicles/${deviceId}`);
}

export async function fetchVehicleConfig(vehicleId: string) {
  return request(`/vehicles/${vehicleId}/config`);
}

export async function updateDeviceConfig(
  deviceId: string,
  config: Record<string, unknown>,
) {
  return request(`/vehicles/${deviceId}/config`, {
    method: "PATCH",
    body: JSON.stringify({ patch: config }),
  });
}

export async function fetchVehicleEvents(deviceId: string, types?: string) {
  const params = types
    ? `?deviceId=${deviceId}&types=${types}`
    : `?deviceId=${deviceId}`;
  return request(`/events${params}`);
}

export async function fetchVehicleMaintenance(vehicleId: string) {
  return request(`/vehicles/${vehicleId}/maintenance`);
}

export async function upsertVehicleMaintenance(
  vehicleId: string,
  payload: {
    id?: string;
    type: string;
    status?: "SCHEDULED" | "COMPLETED" | "OVERDUE" | "CANCELLED";
    dueAtDate?: string;
    dueAtKm?: number;
    completedAt?: string;
    cost?: number;
    notes?: string;
    evidenceUrls?: string[];
  },
) {
  return request(`/vehicles/${vehicleId}/maintenance`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchMaintenanceDue(days = 14) {
  return request(`/vehicles/maintenance/due?days=${days}`);
}

export async function fetchVehicleCompliance(vehicleId: string) {
  return request(`/vehicles/${vehicleId}/compliance`);
}

export async function upsertVehicleCompliance(
  vehicleId: string,
  payload: {
    id?: string;
    type: string;
    status?: "VALID" | "EXPIRING" | "EXPIRED" | "REVOKED";
    expiresAt?: string;
    provider?: string;
    policyNumber?: string;
    referenceNumber?: string;
    url?: string;
    notes?: string;
  },
) {
  return request(`/vehicles/${vehicleId}/compliance`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createBillingInvoice(
  accountId: string,
  payload: Omit<
    Invoice,
    "id" | "accountId" | "createdAt" | "updatedAt" | "number" | "issuedAt"
  >,
  tenantId?: string,
): Promise<Invoice> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/invoices`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return invoiceSchema.parse(data);
}

export async function createBillingPurchaseOrder(
  accountId: string,
  payload: Omit<PurchaseOrder, "id" | "accountId" | "createdAt" | "updatedAt">,
  tenantId?: string,
): Promise<PurchaseOrder> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/purchase-orders`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return purchaseOrderSchema.parse(data);
}

export async function createBillingPayment(
  accountId: string,
  payload: Omit<Payment, "id" | "createdAt" | "updatedAt">,
  tenantId?: string,
): Promise<Payment> {
  const data = await request<unknown>(
    `/billing/accounts/${accountId}/payments`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return paymentSchema.parse(data);
}

export async function updateBillingInvoice(
  invoiceId: string,
  payload: Partial<
    Omit<Invoice, "id" | "accountId" | "createdAt" | "updatedAt">
  >,
  tenantId?: string,
): Promise<Invoice> {
  const data = await request<unknown>(`/billing/invoices/${invoiceId}`, {
    method: "PATCH",
    headers: tenantScopedHeaders(tenantId),
    body: JSON.stringify(payload),
  });
  return invoiceSchema.parse(data);
}

export async function updateBillingPurchaseOrder(
  purchaseOrderId: string,
  payload: Partial<
    Omit<PurchaseOrder, "id" | "accountId" | "createdAt" | "updatedAt">
  >,
  tenantId?: string,
): Promise<PurchaseOrder> {
  const data = await request<unknown>(
    `/billing/purchase-orders/${purchaseOrderId}`,
    {
      method: "PATCH",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload),
    },
  );
  return purchaseOrderSchema.parse(data);
}

export async function suspendBillingSubscription(
  subscriptionId: string,
  tenantId?: string,
): Promise<Subscription> {
  const data = await request<unknown>(
    `/billing/subscriptions/${subscriptionId}/suspend`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function enableBillingSubscription(
  subscriptionId: string,
  tenantId?: string,
): Promise<Subscription> {
  const data = await request<unknown>(
    `/billing/subscriptions/${subscriptionId}/enable`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function disableBillingSubscription(
  subscriptionId: string,
  tenantId?: string,
): Promise<Subscription> {
  const data = await request<unknown>(
    `/billing/subscriptions/${subscriptionId}/disable`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function processPaymentGateway(
  paymentMethod: Payment["method"],
  amount: { amount: number; currency: string },
  invoiceId?: string,
  tenantId?: string,
): Promise<{
  success: boolean;
  transactionRef?: string;
  redirectUrl?: string;
  formFields?: Record<string, string>;
  status?: string;
  error?: string;
}> {
  const data = await request<unknown>("/billing/payment-gateway/process", {
    method: "POST",
    headers: tenantScopedHeaders(tenantId),
    body: JSON.stringify({ paymentMethod, amount, invoiceId }),
  });
  const result = data as {
    success: boolean;
    transactionId?: string;
    transactionRef?: string;
    reference?: string;
    redirectUrl?: string;
    formFields?: Record<string, string>;
    status?: string;
    error?: string;
  };
  return {
    success: result.success,
    transactionRef:
      result.transactionRef ?? result.transactionId ?? result.reference,
    redirectUrl: result.redirectUrl,
    formFields: result.formFields,
    status: result.status,
    error: result.error,
  };
}

const invoicePaymentProofSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  filePath: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  notes: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  uploadedAt: z.string(),
  uploadedBy: z.string().nullable(),
  source: z.enum(["authenticated-upload", "public-upload"]),
});

const invoicePaymentLinkResultSchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.string(),
  accountId: z.string(),
  accountName: z.string(),
  gateway: z.string(),
  hostedUrl: z.string().url(),
  token: z.string(),
  amountDue: z.number(),
  amountPaid: z.number(),
  outstandingAmount: z.number(),
  currency: z.string(),
  dueDate: z.string(),
  expiresAt: z.string(),
  recipientEmail: z.string().nullable(),
  recipientPhone: z.string().nullable(),
  recipientName: z.string().nullable(),
  emailDeliveryStatus: z.enum(["not_requested", "sent", "skipped", "failed"]),
  emailSentAt: z.string().nullable(),
  emailError: z.string().nullable(),
  share: z.object({
    subject: z.string(),
    message: z.string(),
  }),
});

const publicInvoicePaymentLinkSchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.string(),
  accountId: z.string(),
  tenantId: z.string().optional(),
  accountName: z.string(),
  status: z.string(),
  amountDue: z.number(),
  amountPaid: z.number(),
  outstandingAmount: z.number(),
  currency: z.string(),
  dueDate: z.string(),
  gateway: z.string(),
  hostedUrl: z.string().url(),
  expiresAt: z.string(),
  recipientName: z.string().nullable(),
  canStartCheckout: z.boolean(),
  canUploadProof: z.boolean(),
  paymentProofs: z.array(invoicePaymentProofSchema),
});

const hostedCheckoutResultSchema = z.object({
  success: z.boolean(),
  transactionRef: z.string().optional(),
  redirectUrl: z.string().optional(),
  formFields: z.record(z.string(), z.string()).optional(),
  status: z.string().optional(),
  error: z.string().optional(),
  gateway: z.string(),
});

export type InvoicePaymentProof = z.infer<typeof invoicePaymentProofSchema>;
export type InvoicePaymentLinkResult = z.infer<
  typeof invoicePaymentLinkResultSchema
>;
export type PublicInvoicePaymentLink = z.infer<
  typeof publicInvoicePaymentLinkSchema
>;

export async function createInvoicePaymentLink(
  invoiceId: string,
  payload?: {
    gateway?: string;
    recipientEmail?: string;
    recipientPhone?: string;
    recipientName?: string;
    sendEmail?: boolean;
    expiresInDays?: number;
  },
  tenantId?: string,
): Promise<InvoicePaymentLinkResult> {
  const data = await request<unknown>(
    `/billing/invoices/${invoiceId}/payment-link`,
    {
      method: "POST",
      headers: tenantScopedHeaders(tenantId),
      body: JSON.stringify(payload ?? {}),
    },
  );
  return invoicePaymentLinkResultSchema.parse(data);
}

export async function uploadInvoicePaymentProof(
  invoiceId: string,
  payload: {
    file: File;
    notes?: string;
    referenceNumber?: string;
  },
  tenantId?: string,
): Promise<InvoicePaymentProof> {
  const form = new FormData();
  form.append("file", payload.file);
  if (payload.notes) {
    form.append("notes", payload.notes);
  }
  if (payload.referenceNumber) {
    form.append("referenceNumber", payload.referenceNumber);
  }
  const data = await requestFormData<unknown>(
    `/billing/invoices/${invoiceId}/proof-of-payment`,
    form,
    { headers: tenantScopedHeaders(tenantId) },
  );
  return invoicePaymentProofSchema.parse(data);
}

export async function fetchPublicInvoicePaymentLink(
  token: string,
): Promise<PublicInvoicePaymentLink> {
  const data = await request<unknown>(`/public/billing-links/${token}`);
  return publicInvoicePaymentLinkSchema.parse(data);
}

export async function startPublicInvoiceCheckout(
  token: string,
): Promise<z.infer<typeof hostedCheckoutResultSchema>> {
  const data = await request<unknown>(
    `/public/billing-links/${token}/checkout`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
  return hostedCheckoutResultSchema.parse(data);
}

export async function uploadPublicInvoicePaymentProof(
  token: string,
  payload: {
    file: File;
    notes?: string;
    referenceNumber?: string;
  },
): Promise<InvoicePaymentProof> {
  const form = new FormData();
  form.append("file", payload.file);
  if (payload.notes) {
    form.append("notes", payload.notes);
  }
  if (payload.referenceNumber) {
    form.append("referenceNumber", payload.referenceNumber);
  }
  const data = await requestFormData<unknown>(
    `/public/billing-links/${token}/proof-of-payment`,
    form,
    { includeAuthorization: false },
  );
  return invoicePaymentProofSchema.parse(data);
}

// Enhanced subscription creation with automatic invoice and payment generation
export async function createCompleteBillingSubscription(
  accountId: string,
  subscriptionPayload: Omit<Subscription, "id" | "createdAt" | "updatedAt">,
  invoicePayload?: Partial<
    Omit<
      Invoice,
      "id" | "accountId" | "createdAt" | "updatedAt" | "number" | "issuedAt"
    >
  >,
): Promise<{
  subscription: Subscription;
  invoice: Invoice;
  payment: Payment;
  purchaseOrder: PurchaseOrder;
}> {
  // Create subscription
  const subscription = await createBillingSubscription(
    accountId,
    subscriptionPayload,
  );

  // Calculate invoice amount based on subscription
  const invoiceAmount = calculateSubscriptionAmount(subscription);

  // Create invoice
  const invoice = await createBillingInvoice(accountId, {
    amountDue: invoiceAmount,
    amountPaid: { amount: 0, currency: invoiceAmount.currency },
    currency: invoiceAmount.currency,
    periodStart: subscription.startDate,
    periodEnd:
      subscription.endDate ||
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
    status: "draft",
    lineItems: [
      {
        id: `item-${Date.now()}`,
        description: `${subscription.planName} - ${subscription.tier} Tier`,
        quantity: subscription.seatCount,
        unitPrice: {
          amount: invoiceAmount.amount / subscription.seatCount,
          currency: invoiceAmount.currency,
        },
        subtotal: invoiceAmount,
        category: "service",
      },
    ],
    ...invoicePayload,
  });

  // Create payment entry
  const payment = await recordBillingPayment(accountId, {
    invoiceId: invoice.id,
    method: "stripe", // Default payment method
    amount: invoiceAmount,
    status: "pending",
  });

  // Create purchase order
  const purchaseOrder = await createBillingPurchaseOrder(accountId, {
    supplier: "GlonexTrack Services",
    expectedAt: subscription.startDate,
    status: "draft",
    totalCost: invoiceAmount,
    currency: invoiceAmount.currency,
    items: [
      {
        id: `po-item-${Date.now()}`,
        description: `${subscription.planName} Subscription`,
        quantity: subscription.seatCount,
        unitCost: {
          amount: invoiceAmount.amount / subscription.seatCount,
          currency: invoiceAmount.currency,
        },
        subtotal: invoiceAmount,
        category: "service",
      },
    ],
  });

  return { subscription, invoice, payment, purchaseOrder };
}

// ──────────────────────────────────────────────────────────
// End-User (Sub-User / Small Holder) APIs
// ──────────────────────────────────────────────────────────

export interface EndUserProfile {
  id: string;
  userId: string;
  organizationId: string | null;
  customerRef: string | null;
  preferences: Record<string, unknown> | null;
  address: Record<string, unknown> | null;
  status: string | null;
  billingAccountId: string | null;
  createdAt: string;
  updatedAt: string;
  User: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    phone: string | null;
    status: string;
    persona: string | null;
    lastSignInAt: string | null;
    createdAt: string;
  };
  BillingAccount?: {
    id: string;
    balanceAmount: number;
    balanceCurrency: string;
    status: string;
  } | null;
  currentSubscription?: {
    id: string;
    planId: string;
    planName: string;
    tier: string;
    status: string;
    recurring: boolean;
    billingCycle: string;
    currency: string;
    nextRenewalAt: string | null;
    autoInvoice?: boolean;
  } | null;
  assetCounts?: {
    vehicles: number;
    devices: number;
    drivers: number;
  };
}

export interface EndUserListResponse {
  data: EndUserProfile[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchEndUsers(params?: {
  organizationId?: string;
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<EndUserListResponse> {
  const sp = new URLSearchParams();
  if (params?.organizationId) sp.set("organizationId", params.organizationId);
  if (params?.search) sp.set("search", params.search);
  if (params?.status) sp.set("status", params.status);
  if (params?.limit) sp.set("limit", params.limit.toString());
  if (params?.offset) sp.set("offset", params.offset.toString());
  const query = sp.toString();
  return request<EndUserListResponse>(`/end-users${query ? `?${query}` : ""}`);
}

export async function fetchEndUser(
  id: string,
  organizationId?: string,
): Promise<EndUserProfile> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<EndUserProfile>(`/end-users/${id}${query ? `?${query}` : ""}`);
}

export async function fetchEndUserSubscriptions(
  id: string,
  organizationId?: string,
): Promise<Subscription[]> {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  const data = await request<unknown[]>(
    `/end-users/${id}/subscriptions${query ? `?${query}` : ""}`,
  );
  return data.map((item) => subscriptionSchema.parse(item));
}

export async function assignEndUserPlan(
  id: string,
  payload: {
    planId: string;
    seatCount?: number;
    vehicleCount?: number;
    deviceCount?: number;
    billingCycle?: "monthly" | "quarterly" | "biannual" | "annual";
    startDate?: string;
    autoInvoice?: boolean;
    organizationId?: string;
  },
): Promise<Subscription> {
  const { organizationId, ...body } = payload;
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  const data = await request<unknown>(
    `/end-users/${id}/subscriptions${query ? `?${query}` : ""}`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  return subscriptionSchema.parse(data);
}

export async function createEndUser(payload: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  customerRef?: string;
  address?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  organizationId?: string;
}): Promise<EndUserProfile> {
  const { organizationId, ...body } = payload;
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<EndUserProfile>(`/end-users${query ? `?${query}` : ""}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateEndUser(
  id: string,
  payload: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    customerRef?: string;
    address?: Record<string, unknown>;
    preferences?: Record<string, unknown>;
    status?: string;
    locale?: string;
    timeZone?: string;
    organizationId?: string;
  },
): Promise<EndUserProfile> {
  const { organizationId, ...body } = payload;
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<EndUserProfile>(
    `/end-users/${id}${query ? `?${query}` : ""}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export async function adminSetEndUserPassword(
  id: string,
  password: string,
  organizationId?: string,
) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request(`/end-users/${id}/password${query ? `?${query}` : ""}`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function adminSendEndUserPasswordReset(
  id: string,
  organizationId?: string,
) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request(`/end-users/${id}/password-reset${query ? `?${query}` : ""}`, {
    method: "POST",
  });
}

export async function deleteEndUser(id: string, organizationId?: string) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request(`/end-users/${id}${query ? `?${query}` : ""}`, {
    method: "DELETE",
  });
}

export async function suspendEndUser(id: string, organizationId?: string) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request(`/end-users/${id}/suspend${query ? `?${query}` : ""}`, {
    method: "POST",
  });
}

export async function activateEndUser(id: string, organizationId?: string) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request(`/end-users/${id}/activate${query ? `?${query}` : ""}`, {
    method: "POST",
  });
}

export async function fetchEndUserAssets(id: string, organizationId?: string) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<{
    vehicles: unknown[];
    devices: unknown[];
    drivers: unknown[];
    counts: { vehicles: number; devices: number; drivers: number };
  }>(`/end-users/${id}/assets${query ? `?${query}` : ""}`);
}

export async function fetchEndUserInvoices(
  id: string,
  organizationId?: string,
) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<{ data: Invoice[]; total: number }>(
    `/end-users/${id}/invoices${query ? `?${query}` : ""}`,
  );
}

export async function createEndUserInvoice(
  id: string,
  payload: {
    lineItems: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      category?: string;
    }>;
    dueDate: string;
    currency?: string;
    notes?: string;
    organizationId?: string;
  },
) {
  const { organizationId, ...body } = payload;
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<Invoice>(
    `/end-users/${id}/invoices${query ? `?${query}` : ""}`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

// ──────────────────────────────────────────────────────────
// Organization Enrichment APIs (billing summary, device summary, etc.)
// ──────────────────────────────────────────────────────────

export interface OrgBillingSummary {
  billingAccountId: string | null;
  balance: { amount: number; currency: string } | null;
  activeSubscriptions: Subscription[];
  invoices: {
    total: number;
    totalDue: number;
    totalPaid: number;
    outstanding: number;
  };
  payments: { total: number; totalAmount: number };
  subUserCount: number;
}

export interface OrgDeviceSummary {
  devices: { total: number; byStatus: Record<string, number> };
  vehicles: { total: number; byStatus: Record<string, number> };
}

export interface OrgUsageStats {
  members: number;
  endUsers: number;
  fleets: number;
  drivers: number;
  usageEvents: Array<{
    eventType: string;
    count: number;
    totalQuantity: number;
  }>;
  period: { from: string; to: string };
}

export interface OrgStatement {
  period: string;
  invoices: Invoice[];
  payments: Payment[];
  totalCharged: number;
  totalPaid: number;
}

export async function fetchOrgBillingSummary(
  organizationId: string,
): Promise<OrgBillingSummary> {
  try {
    return await request<OrgBillingSummary>(
      `/organizations/${organizationId}/billing-summary`,
    );
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        `Billing summary unavailable for organization ${organizationId}; returning defaults.`,
      );
      return {
        billingAccountId: null,
        balance: null,
        activeSubscriptions: [],
        invoices: { total: 0, totalDue: 0, totalPaid: 0, outstanding: 0 },
        payments: { total: 0, totalAmount: 0 },
        subUserCount: 0,
      };
    }
    throw error;
  }
}

export async function fetchOrgDeviceSummary(
  organizationId: string,
): Promise<OrgDeviceSummary> {
  return request<OrgDeviceSummary>(
    `/organizations/${organizationId}/device-summary`,
  );
}

export async function fetchOrgUsageStats(
  organizationId: string,
): Promise<OrgUsageStats> {
  return request<OrgUsageStats>(`/organizations/${organizationId}/usage-stats`);
}

export async function fetchOrgStatements(
  organizationId: string,
  params?: {
    from?: string;
    to?: string;
  },
): Promise<{
  statements: OrgStatement[];
  dateRange: { from: string; to: string };
}> {
  const sp = new URLSearchParams();
  if (params?.from) sp.set("from", params.from);
  if (params?.to) sp.set("to", params.to);
  const query = sp.toString();
  try {
    return await request(
      `/organizations/${organizationId}/statements${query ? `?${query}` : ""}`,
    );
  } catch (error) {
    if (isSoftMissingFeatureError(error)) {
      log.api.warn(
        `Statements endpoint unavailable for organization ${organizationId}; returning empty statements.`,
      );
      return {
        statements: [],
        dateRange: {
          from: params?.from ?? "",
          to: params?.to ?? "",
        },
      };
    }
    throw error;
  }
}

// ──────────────────────────────────────────────────────────
// Domain Management APIs
// ──────────────────────────────────────────────────────────

export interface TenantDomain {
  id: string;
  organizationId: string;
  hostname: string;
  domainType: string;
  isPrimary: boolean;
  status: string;
  verification: Record<string, unknown> | null;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchOrgDomains(
  organizationId: string,
): Promise<TenantDomain[]> {
  return request<TenantDomain[]>(`/organizations/${organizationId}/domains`);
}

export async function addOrgDomain(
  organizationId: string,
  hostname: string,
): Promise<{
  domain: TenantDomain;
  dnsInstructions: {
    method: string;
    hostname: string;
    cnameTarget: string;
    alternativeTxtRecord: { name: string; value: string };
    note: string;
  };
}> {
  return request(`/organizations/${organizationId}/domains`, {
    method: "POST",
    body: JSON.stringify({ hostname }),
  });
}

export async function removeOrgDomain(
  organizationId: string,
  domainId: string,
) {
  return request(`/organizations/${organizationId}/domains/${domainId}`, {
    method: "DELETE",
  });
}

export async function verifyOrgDomain(
  organizationId: string,
  domainId: string,
): Promise<{
  domain?: TenantDomain;
  verified: boolean;
  message: string;
}> {
  return request(
    `/organizations/${organizationId}/domains/${domainId}/verify`,
    {
      method: "POST",
    },
  );
}

export async function setOrgPrimaryDomain(
  organizationId: string,
  domainId: string,
): Promise<TenantDomain> {
  return request(
    `/organizations/${organizationId}/domains/${domainId}/set-primary`,
    {
      method: "PATCH",
    },
  );
}

// ──────────────────────────────────────────────────────────
// Payment Gateway Settings APIs
// ──────────────────────────────────────────────────────────

export async function fetchPaymentGatewaySettings(organizationId?: string) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<{
    paymentGateway: Record<string, unknown>;
    subUserBilling: Record<string, unknown>;
  }>(`/settings/payment-gateway${query ? `?${query}` : ""}`);
}

export async function updatePaymentGatewaySettings(payload: {
  provider?: string;
  apiKey?: string;
  secretKey?: string;
  webhookSecret?: string;
  merchantId?: string;
  environment?: string;
  organizationId?: string;
}) {
  const { organizationId, ...body } = payload;
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request(`/settings/payment-gateway${query ? `?${query}` : ""}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function updateSubUserBillingSettings(payload: {
  defaultCurrency?: string;
  autoInvoice?: boolean;
  invoiceDayOfMonth?: number;
  paymentTermDays?: number;
  organizationId?: string;
}) {
  const { organizationId, ...body } = payload;
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request(`/settings/sub-user-billing${query ? `?${query}` : ""}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function testPaymentGatewayConnection(organizationId?: string) {
  const sp = new URLSearchParams();
  if (organizationId) sp.set("organizationId", organizationId);
  const query = sp.toString();
  return request<{ success: boolean; provider?: string; message: string }>(
    `/settings/payment-gateway/test${query ? `?${query}` : ""}`,
    { method: "POST" },
  );
}

// Helper function to calculate subscription amount
function calculateSubscriptionAmount(subscription: Subscription): {
  amount: number;
  currency: string;
} {
  // Simple calculation based on tier and seat count
  const tierPrices = {
    basic: 29.99,
    ai: 49.99,
    safety: 79.99,
    enterprise: 149.99,
    custom: 199.99,
  };

  const basePrice = tierPrices[subscription.tier] || tierPrices.basic;
  const totalAmount = basePrice * subscription.seatCount;

  return { amount: totalAmount, currency: "USD" };
}

const actionableSeveritySchema = z.enum(["critical", "warning", "insight"]);
const actionableCategorySchema = z.enum([
  "geofence",
  "driver-behavior",
  "fuel-telemetry",
  "idling",
  "route",
  "operations",
  "ai-insight",
]);
const actionableInsightSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  organizationId: z.string().nullable().optional(),
  vehicleId: z.string().nullable().optional(),
  title: z.string(),
  description: z.string().nullable().optional(),
  severity: actionableSeveritySchema,
  category: actionableCategorySchema,
  triggerId: z.string().nullable().optional(),
  source: z.enum(["deterministic", "ai"]),
  createdAt: z.string(),
  updatedAt: z.string(),
  status: z.string(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  vehicleName: z.string().nullable().optional(),
  vehicleReg: z.string().nullable().optional(),
});
const actionableFeedResponseSchema = z.object({
  data: z.array(actionableInsightSchema),
  nextCursor: z.string().optional(),
});
const actionableSeriesPointSchema = z.object({
  ts: z.string(),
  value: z.number(),
});
const actionableMetricsSchema = z.object({
  generatedAt: z.string(),
  totalFuelBurnedToday: z.number(),
  fleetRiskScoreAverage: z.number(),
  activeIdlingCount: z.number(),
  fuelEfficiencySeries: z.array(actionableSeriesPointSchema),
  fleetRiskSeries: z.array(actionableSeriesPointSchema),
  idlingSeries: z.array(actionableSeriesPointSchema),
  latestAiSynthesis: z
    .object({
      id: z.string(),
      title: z.string(),
      message: z.string(),
      createdAt: z.string(),
    })
    .optional(),
});

export type ActionableInsight = z.infer<typeof actionableInsightSchema>;
export type ActionableFeedResponse = z.infer<
  typeof actionableFeedResponseSchema
>;
export type ActionableMetrics = z.infer<typeof actionableMetricsSchema>;

const normalizeActionableSeriesPoint = (
  point: unknown,
  fallbackIso: string,
): unknown => {
  if (!point || typeof point !== "object" || Array.isArray(point)) {
    return point;
  }
  const record = point as Record<string, unknown>;
  return {
    ...record,
    ts: resolveTelemetryTimestamp(record, fallbackIso),
  };
};

const normalizeActionableMetricsPayload = (payload: unknown): unknown => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }
  const record = payload as Record<string, unknown>;
  const generatedAt =
    toIsoTimestamp(record.generatedAt) ?? new Date().toISOString();
  const normalizeSeries = (series: unknown) =>
    Array.isArray(series)
      ? series.map((point) =>
        normalizeActionableSeriesPoint(point, generatedAt),
      )
      : series;
  return {
    ...record,
    generatedAt,
    fuelEfficiencySeries: normalizeSeries(record.fuelEfficiencySeries),
    fleetRiskSeries: normalizeSeries(record.fleetRiskSeries),
    idlingSeries: normalizeSeries(record.idlingSeries),
  };
};

export async function fetchActionableInsightsFeed(params?: {
  tenantId?: string;
  limit?: number;
  cursor?: string;
  criticalOnly?: boolean;
  geofenceEvents?: boolean;
  driverBehavior?: boolean;
  fuelTelemetry?: boolean;
  aiInsights?: boolean;
}): Promise<ActionableFeedResponse> {
  const query = new URLSearchParams();
  if (params?.tenantId) query.set("tenantId", params.tenantId);
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.cursor) query.set("cursor", params.cursor);
  if (params?.criticalOnly !== undefined)
    query.set("criticalOnly", params.criticalOnly ? "1" : "0");
  if (params?.geofenceEvents !== undefined)
    query.set("geofenceEvents", params.geofenceEvents ? "1" : "0");
  if (params?.driverBehavior !== undefined)
    query.set("driverBehavior", params.driverBehavior ? "1" : "0");
  if (params?.fuelTelemetry !== undefined)
    query.set("fuelTelemetry", params.fuelTelemetry ? "1" : "0");
  if (params?.aiInsights !== undefined)
    query.set("aiInsights", params.aiInsights ? "1" : "0");
  const path = `/intelligence/feed${query.toString() ? `?${query.toString()}` : ""}`;
  const payload = await request<unknown>(path);
  return actionableFeedResponseSchema.parse(payload);
}

export async function fetchActionableMetrics(
  tenantId?: string,
): Promise<ActionableMetrics> {
  const query = new URLSearchParams();
  if (tenantId) query.set("tenantId", tenantId);
  const path = `/intelligence/metrics${query.toString() ? `?${query.toString()}` : ""}`;
  const payload = await request<unknown>(path);
  return actionableMetricsSchema.parse(
    normalizeActionableMetricsPayload(payload),
  );
}

export async function triggerActionableSynthesis(
  tenantId?: string,
): Promise<{ ok: boolean; tenantId: string }> {
  const query = new URLSearchParams();
  if (tenantId) query.set("tenantId", tenantId);
  const path = `/intelligence/synthesis/run${query.toString() ? `?${query.toString()}` : ""}`;
  return request(path, {
    method: "POST",
  });
}

// ── Live Sessions & Simulation ────────────────────────────────────

export interface LiveSessionResponse {
  id: string;
  vehicleId: string;
  organizationId: string;
  tenantId: string;
  type: "gps" | "simulation";
  status: string;
  currentLat: number;
  currentLng: number;
  currentHeading: number | null;
  currentSpeedKph: number | null;
  routePoints: Array<{ lat: number; lng: number }> | null;
  routeTotalM: number | null;
  progressM: number | null;
  simSpeedKph: number | null;
  startedAt: string;
  lastTickAt: string;
  completedAt: string | null;
}

export async function fetchLiveSessions(params?: {
  organizationId?: string;
  tenantId?: string;
}): Promise<LiveSessionResponse[]> {
  const query = new URLSearchParams();
  if (params?.organizationId)
    query.set("organizationId", params.organizationId);
  if (params?.tenantId) query.set("tenantId", params.tenantId);
  const path = `/vehicles/live-sessions${query.toString() ? `?${query.toString()}` : ""}`;
  return request<LiveSessionResponse[]>(path);
}

export async function fetchLiveSession(
  sessionId: string,
): Promise<LiveSessionResponse> {
  return request<LiveSessionResponse>(`/vehicles/live-sessions/${sessionId}`);
}

export async function startSimulation(params: {
  vehicleId: string;
  routePoints: Array<{ lat: number; lng: number }>;
  speedKph: number;
  organizationId?: string;
  tenantId?: string;
}): Promise<LiveSessionResponse> {
  const query = new URLSearchParams();
  if (params.organizationId) query.set("organizationId", params.organizationId);
  if (params.tenantId) query.set("tenantId", params.tenantId);
  const path = `/vehicles/${params.vehicleId}/simulate${query.toString() ? `?${query.toString()}` : ""}`;
  return request<LiveSessionResponse>(path, {
    method: "POST",
    body: JSON.stringify({
      routePoints: params.routePoints,
      speedKph: params.speedKph,
    }),
  });
}

export async function stopSimulation(
  vehicleId: string,
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/vehicles/${vehicleId}/simulate`, {
    method: "DELETE",
  });
}
